import { useState, useEffect } from "react";

const API_URL = "https://venci-backend-production.up.railway.app";

// ─── CSS GLOBAL RESPONSIVE ────────────────────────────────────────────────────
// Se inyecta una vez al cargar la app. Maneja el responsive del panel completo
// sin tocar cada componente individualmente. No afecta nada en PC (>768px).
const estilosResponsive = `
  * { box-sizing: border-box; }
  html, body { overflow-x: hidden; max-width: 100%; }
  
  @media (max-width: 768px) {
    /* Contenedor principal */
    body { overflow-x: hidden; max-width: 100vw; }

    /* Grilla de clientes — de 3 columnas a 1 */
    .venci-grid-clientes { grid-template-columns: 1fr !important; }

    /* Stats del dashboard — de 4 a 2 columnas */
    .venci-stats { grid-template-columns: 1fr 1fr !important; }

    /* Tabs de cliente — scroll horizontal */
    .venci-tabs { overflow-x: auto; white-space: nowrap; -webkit-overflow-scrolling: touch; }
    .venci-tabs button { min-width: 120px; }

    /* Tabla de movimientos — texto más chico */
    .venci-mov-row { font-size: 11px !important; }

    /* Modal — pantalla completa en mobile */
    .venci-modal { 
      width: 100% !important; 
      max-width: 100% !important; 
      margin: 0 !important;
      border-radius: 0 !important;
      min-height: 100vh !important;
    }

    /* Planes en panel admin — de 4 a 2 columnas */
    .venci-admin-stats { grid-template-columns: 1fr 1fr !important; }

    /* Botones de acción — más grandes para touch */
    button { min-height: 36px; }
  }

  @media (max-width: 480px) {
    /* Stats — 1 columna en pantallas muy chicas */
    .venci-stats { grid-template-columns: 1fr !important; }
    .venci-admin-stats { grid-template-columns: 1fr !important; }
    
    /* Texto más chico en tablas */
    .venci-mov-row { font-size: 10px !important; padding: 8px 10px !important; }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = estilosResponsive;
  document.head.appendChild(style);
}

// Hook para detectar si estamos en mobile
const useMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// Helper que agrega el token JWT a cada request automáticamente.
// Si el backend responde 401 (token inválido/expirado), limpiamos la
// sesión y recargamos — eso muestra el Login en vez de un panel roto.
function authFetch(url, options = {}) {
  const token = localStorage.getItem("venci_token");
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(res => {
    if (res.status === 401) {
      localStorage.removeItem("venci_token");
      localStorage.removeItem("venci_contador");
      window.location.reload();
    }
    return res;
  });
}

// ─── PALETA OSCURA VENCI ──────────────────────────────────────────────────────
const C = {
  // Fondos
  bg:        "#0F1923",   // fondo principal — azul muy oscuro
  bgSide:    "#0A1219",   // sidebar — más oscuro
  bgCard:    "#162433",   // cards
  bgCardHov: "#1A2D3E",   // cards hover
  bgInput:   "#111E2A",   // inputs

  // Bordes
  border:    "#1E3347",
  borderMid: "#2A4A63",

  // Texto
  ink:       "#EDF2F7",   // texto principal
  inkMid:    "#94B4CC",   // texto secundario
  inkDim:    "#4A6E87",   // texto tenue

  // Acentos
  green:     "#3AA882",   // verde Venci
  greenGlow: "rgba(58,168,130,0.15)",
  greenDim:  "rgba(58,168,130,0.08)",
  navy:      "#2A6496",   // azul Venci
  navyGlow:  "rgba(42,100,150,0.15)",

  // Estados
  danger:    "#E05252",
  dangerBg:  "rgba(224,82,82,0.10)",
  warning:   "#E0A030",
  warningBg: "rgba(224,160,48,0.10)",
  success:   "#3AA882",
  successBg: "rgba(58,168,130,0.10)",
};

// ─── UTILIDADES ───────────────────────────────────────────────────────────────
// Validación de CUIT/CUIL — mismo algoritmo que usa AFIP/ARCA (módulo 11)
const validarCuitFrontend = (cuit) => {
  if(!cuit) return { valido:false, error:"El CUIT es obligatorio" };
  const limpio = cuit.toString().replace(/[^0-9]/g,"");
  if(limpio.length!==11) return { valido:false, error:"El CUIT debe tener 11 números" };
  const tiposValidos = ["20","23","24","27","30","33","34"];
  if(!tiposValidos.includes(limpio.substring(0,2))) return { valido:false, error:"Prefijo de CUIT no reconocido" };
  const mult = [5,4,3,2,7,6,5,4,3,2];
  const d = limpio.split("").map(Number);
  let suma = 0;
  for(let i=0;i<10;i++) suma += d[i]*mult[i];
  let resto = suma % 11;
  let calc = 11 - resto;
  if(calc===11) calc = 0;
  if(calc===10) calc = 9;
  if(calc!==d[10]) return { valido:false, error:"El CUIT no es válido (dígito verificador incorrecto)" };
  return { valido:true };
};

const fmt = n => new Intl.NumberFormat("es-AR",{style:"currency",currency:"ARS",maximumFractionDigits:0}).format(n);
const fmtF = d => {
  // new Date("YYYY-MM-DD") interpreta como medianoche UTC — en Argentina
  // (UTC-3) eso cae el día anterior, mostrando una fecha incorrecta.
  // Solución: parsear el string directamente sin conversión de zona.
  if (!d) return "—";
  const [anio, mes, dia] = String(d).split("T")[0].split("-");
  return `${dia}/${mes}/${String(anio).slice(2)}`;
};
const initiales = n => n.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

// Arma el link de WhatsApp a partir del celular como lo haya escrito la
// persona. WhatsApp necesita el número en formato internacional y sin signos:
// para un celular argentino es 54 + 9 + característica sin el 0 + número.
// Si no se puede armar algo con pinta de número válido devuelve null, y el
// panel muestra el número igual para copiarlo a mano.
const linkWhatsApp = (tel) => {
  let d = String(tel || "").replace(/\D/g, "");
  if (!d) return null;
  d = d.replace(/^00/, "");
  if (d.startsWith("54")) {
    const resto = d.slice(2);
    // Los móviles llevan un 9 después del 54; si no está, se agrega.
    d = "54" + (resto.startsWith("9") ? resto : "9" + resto);
  } else {
    // Número local: se le saca el 0 de la característica si lo trajo.
    d = "549" + d.replace(/^0/, "");
  }
  // 54 + 9 + 10 dígitos = 13. Si quedó muy corto o muy largo, no inventamos
  // un link que lleve a un número equivocado.
  return d.length >= 12 && d.length <= 15 ? `https://wa.me/${d}` : null;
};
const genCAE = () => Array.from({length:14},()=>Math.floor(Math.random()*10)).join("");
const diasHasta = f => Math.ceil((new Date(f)-new Date())/(864e5));

// ─── DATOS ────────────────────────────────────────────────────────────────────
// Categorías genéricas de productos/servicios — lista real provista por Dariela,
// usada para enriquecer la descripción de cada factura sin pedir un detalle
// ítem por ítem completo (cantidad/precio unitario), que sería mucho más
// trabajo manual para el contador.
const CATEGORIAS_PRODUCTO = [
  "Productos de almacén", "Servicios de hotelería", "Servicios veterinarios",
  "Honorarios médicos", "Honorarios contables", "Alquiler", "Prendas de vestir",
  "Zapatería", "Productos varios", "Servicios de enfermería",
  "Servicios gastronómicos", "Cortes de carne de ave varios", "Servicios varios",
  "Servicio de transporte de pasajeros", "Venta de baterías",
  "Venta de partes, piezas y accesorios del automotor", "Bidones de agua",
  "Alquiler de dispenser de agua", "Artículos de forrajería",
  "Servicios de mantenimiento y reparación del automotor",
  "Productos de panificación", "Venta de hielo", "Venta de descartables",
  "Servicios de encomiendas", "Corte de carne varios", "Impresiones",
  "Artículos de librería", "Talonario", "Anotador", "Hoja de sublimación",
  "Servicios de peluquería",
  "Venta al por menor de productos cosméticos, de tocador y de perfumería",
  "Suplementos deportivos",
];

// Topes de ingresos brutos anuales del monotributo.
// Vigentes desde el 01/08/2026 (ajuste semestral por IPC, +16,8%).
// ARCA los actualiza en FEBRERO y AGOSTO de cada año: cuando salga la
// próxima escala hay que reemplazar estos números.
// Fuente: https://arca.gob.ar/monotributo/categorias.asp
const LIMITES_MONO = {
  A: 12009410, B: 17595183, C: 24670494, D: 30628651,
  E: 36028231, F: 45151659, G: 53995799, H: 81924660,
  I: 91699762, J: 105012519, K: 126610839,
};
// ATENCIÓN: esta tabla quedó desactualizada (valores de febrero 2026) y está
// incompleta (faltan G a K, y no distingue servicios de venta de bienes, que
// pagan distinto desde la categoría C). Hoy no se usa en ninguna pantalla.
// Antes de usarla, pedir la tabla vigente a ARCA y completarla.
const CUOTAS_MONO = {
  A:{j:15951,o:18516}, B:{j:17546,o:18516},
  C:{j:19319,o:18516}, D:{i:18695,j:21252,o:18516},
  E:{i:35473,j:26515,o:23145}, F:{i:48385,j:31778,o:23145},
};

const CLIENTES = [
  {id:1,nombre:"Juan García",    cuit:"20-35482910-4",tipo:"Monotributista",cat:"D",tel:"+5493442001122",arcaEstado:"Activo",  facturacionAnual:7800000, proximoVencimiento:"2026-06-20",estadoPago:"al_dia",  plan:null},
  {id:2,nombre:"María López",    cuit:"27-29183746-1",tipo:"Monotributista",cat:"B",tel:"+5493442003344",arcaEstado:"Activo",  facturacionAnual:5900000, proximoVencimiento:"2026-06-20",estadoPago:"al_dia",  plan:null},
  {id:3,nombre:"Carlos Romero",  cuit:"20-18273645-7",tipo:"Resp. Inscripto",cat:"—",tel:"+5493442005566",arcaEstado:"Activo", facturacionMes:314500,    ivaVence:"2026-06-18",estadoPago:"al_dia",plan:{estado:"activo",nro:"PF-2025-00482",cuotasMonto:14100,cuotasPagadas:4,cuotasTotal:10,proxCuota:"2026-06-15"}},
  {id:4,nombre:"Ana Fernández",  cuit:"27-34567890-3",tipo:"Monotributista",cat:"C",tel:"+5493442007788",arcaEstado:"Activo",  facturacionAnual:2100000, proximoVencimiento:"2026-06-20",estadoPago:"al_dia",  plan:null},
  {id:5,nombre:"Luis Martínez",  cuit:"20-45678901-2",tipo:"Resp. Inscripto",cat:"—",tel:"+5493442009900",arcaEstado:"Activo", facturacionMes:245000,    ivaVence:"2026-06-18",estadoPago:"al_dia",plan:{estado:"en_riesgo",nro:"PF-2026-00091",cuotasMonto:9800,cuotasPagadas:2,cuotasTotal:10,proxCuota:"2026-06-10",alerta:"1 cuota impaga"}},
  {id:6,nombre:"Sofía Herrera",  cuit:"27-56789012-8",tipo:"Monotributista",cat:"A",tel:"+5493442001111",arcaEstado:"Activo",  facturacionAnual:3900000, proximoVencimiento:"2026-06-20",estadoPago:"vencido", plan:null},
  {id:7,nombre:"Roberto Díaz",   cuit:"20-28374650-9",tipo:"Monotributista",cat:"E",tel:"+5493442002233",arcaEstado:"Activo",  facturacionAnual:11200000,proximoVencimiento:"2026-06-20",estadoPago:"al_dia",  plan:null,alertaCategoria:true},
  {id:8,nombre:"Valeria Sosa",   cuit:"27-39182736-4",tipo:"Monotributista",cat:"F",tel:"+5493442004455",arcaEstado:"Suspendido",facturacionAnual:8200000,proximoVencimiento:"2026-06-20",estadoPago:"vencido",plan:null},
];

const MOVIMIENTOS_MP = [
  {id:"156537312975",fecha:"2026-05-01",descripcion:"Transferencia recibida Zaira Yanina Aranguiz",valor:15000,tipo:"entrada"},
  {id:"157892341021",fecha:"2026-05-03",descripcion:"Pago recibido - Servicio de asesoramiento",   valor:45000,tipo:"entrada"},
  {id:"158234109876",fecha:"2026-05-05",descripcion:"Transferencia recibida Roberto Carlos Perez",  valor:28500,tipo:"entrada"},
  {id:"158901234567",fecha:"2026-05-07",descripcion:"Pago recibido - Honorarios profesionales",     valor:62000,tipo:"entrada"},
  {id:"159234567890",fecha:"2026-05-10",descripcion:"Transferencia recibida Comercio El Roble SRL", valor:18750,tipo:"entrada"},
  {id:"159876543210",fecha:"2026-05-12",descripcion:"Pago recibido - Consultoría contable",         valor:35000,tipo:"entrada"},
  {id:"160123456789",fecha:"2026-05-15",descripcion:"Transferencia recibida Ana Maria Gonzalez",    valor:22000,tipo:"entrada"},
  {id:"157232398160",fecha:"2026-05-01",descripcion:"Transferencia enviada Maria Amanda Dupuy",     valor:-2800,tipo:"salida"},
  {id:"156480114423",fecha:"2026-05-01",descripcion:"Transferencia enviada Silvina Arellano",       valor:-1500,tipo:"salida"},
  {id:"157254120830",fecha:"2026-05-02",descripcion:"Transferencia enviada Cash Mer",               valor:-4000,tipo:"salida"},
  {id:"163456789012",fecha:"2026-05-15",descripcion:"Reversión de pago - ID 159876543210",          valor:5000, tipo:"revision"},
];

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo = ({size=36}) => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
    <img src="/VENCIApp.png" alt="Venci" style={{height:size,width:"auto",objectFit:"contain",maxWidth:"160px"}}
      onError={e=>{e.target.style.display="none";}}/>
  </div>
);

// ─── COMPONENTES BASE ─────────────────────────────────────────────────────────
const Btn = ({children,onClick,variant="primary",disabled,small,style:s={}}) => {
  const v = {
    primary: {background:C.navy,    color:C.ink,   border:`1px solid ${C.navy}`},
    green:   {background:C.green,   color:"#0A1219",border:`1px solid ${C.green}`},
    ghost:   {background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`},
    danger:  {background:C.dangerBg,color:C.danger, border:`1px solid ${C.danger}`},
    success: {background:C.successBg,color:C.success,border:`1px solid ${C.success}`},
    warning: {background:C.warningBg,color:C.warning,border:`1px solid ${C.warning}`},
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{...v[variant],borderRadius:6,padding:small?"5px 10px":"8px 16px",fontSize:small?11:12,fontWeight:600,cursor:disabled?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif",opacity:disabled?0.4:1,display:"flex",alignItems:"center",gap:6,transition:"all 0.15s",letterSpacing:"0.02em",...s}}>
      {children}
    </button>
  );
};

const Modal = ({children,onClose,maxW=500}) => {
  const esMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(5,10,15,0.85)",zIndex:200,display:"flex",alignItems:esMobile?"flex-end":"center",justifyContent:"center",padding:esMobile?0:20,backdropFilter:"blur(4px)"}} onClick={onClose}>
      <div className="venci-modal" style={{background:C.bgCard,borderRadius:esMobile?"16px 16px 0 0":"12px",padding:"24px",width:"100%",maxWidth:esMobile?"100%":maxW,maxHeight:esMobile?"95vh":"92vh",overflowY:"auto",border:`1px solid ${C.border}`,boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}} onClick={e=>e.stopPropagation()}>{children}</div>
    </div>
  );
};

const Badge = ({children,color,bg,border}) => (
  <span style={{background:bg,color,border:`1px solid ${border}`,fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:4,letterSpacing:"0.05em",textTransform:"uppercase"}}>{children}</span>
);

// ─── MODAL VEP ────────────────────────────────────────────────────────────────
const ModalVEP = ({cliente, obligaciones, onClose, onPagar}) => {
  const [modo, setModo]   = useState("elegir");
  const [sel, setSel]     = useState(new Set());
  const [vep, setVep]     = useState(null);
  const [pagando, setPag] = useState(false);
  const [pagado, setPagado] = useState(false);

  const pendientes = obligaciones.filter(o=>o.estado==="pendiente");
  const totalConj  = pendientes.reduce((s,o)=>s+o.monto,0);
  const totalSel   = pendientes.filter(o=>sel.has(o.id)).reduce((s,o)=>s+o.monto,0);

  const genVEP = (obs) => {
    const num = `${Math.floor(Math.random()*900000+100000)}-${Math.floor(Math.random()*9000+1000)}`;
    setVep({num, total:obs.reduce((s,o)=>s+o.monto,0), obs, vence:obs[0]?.vence});
    setModo("generado");
  };

  const pagar = () => {
    setPag(true);
    setTimeout(()=>{ setPag(false); setPagado(true); onPagar(vep.obs.map(o=>o.id), vep.num); }, 2000);
  };

  const linkARCA = `https://payment.afip.gob.ar/ingresoVEP?cuit=${cliente.cuit.replace(/-/g,"")}`;

  return (
    <Modal onClose={modo==="elegir"?onClose:undefined} maxW={480}>
      {/* ELEGIR MODO */}
      {modo==="elegir"&&(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
            <div>
              <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Generar VEP</div>
              <div style={{fontSize:17,fontWeight:700,color:C.ink}}>{cliente.nombre}</div>
              <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>{pendientes.length} obligaciones pendientes</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim,lineHeight:1}}>×</button>
          </div>

          <div style={{display:"flex",gap:10,marginBottom:16}}>
            {[
              {label:"VEP Conjunto",desc:"Todo en un pago",monto:totalConj,count:pendientes.length,action:()=>genVEP(pendientes),color:C.green},
              {label:"VEP Individual",desc:"Elegís cuáles",monto:null,count:null,action:()=>setModo("individual"),color:C.navy},
            ].map((opt,i)=>(
              <div key={i} onClick={opt.action}
                style={{flex:1,background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"16px",cursor:"pointer",transition:"all 0.15s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=opt.color;e.currentTarget.style.background=C.bgCardHov;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.bgCard;}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4}}>{opt.label}</div>
                <div style={{fontSize:11,color:C.inkDim,marginBottom:10}}>{opt.desc}</div>
                {opt.monto&&<div style={{fontSize:16,fontWeight:700,color:opt.color}}>{fmt(opt.monto)}</div>}
                {opt.count&&<div style={{fontSize:10,color:C.inkDim,marginTop:2}}>{opt.count} obligaciones</div>}
                {!opt.monto&&<div style={{fontSize:12,color:opt.color}}>Selección →</div>}
              </div>
            ))}
          </div>

          <div style={{background:C.greenDim,border:`1px solid ${C.green}22`,borderRadius:6,padding:"10px 12px",fontSize:11,color:C.green,lineHeight:1.6}}>
            El cliente recibe el link de pago directo en ARCA. Sin pasar por ninguna cuenta intermediaria.
          </div>
        </>
      )}

      {/* INDIVIDUAL */}
      {modo==="individual"&&(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:C.ink}}>Seleccioná las obligaciones</div>
            <button onClick={()=>setModo("elegir")} style={{background:"none",border:"none",fontSize:12,cursor:"pointer",color:C.inkMid}}>← Volver</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
            {pendientes.map(ob=>{
              const isSel=sel.has(ob.id);
              return (
                <div key={ob.id} onClick={()=>{const n=new Set(sel);isSel?n.delete(ob.id):n.add(ob.id);setSel(n);}}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",border:`1px solid ${isSel?C.green:C.border}`,borderRadius:8,cursor:"pointer",background:isSel?C.greenDim:C.bgCard,transition:"all 0.15s"}}>
                  <div style={{width:18,height:18,borderRadius:3,border:`2px solid ${isSel?C.green:C.borderMid}`,background:isSel?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {isSel&&<span style={{color:"#0A1219",fontSize:11,lineHeight:1}}>✓</span>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{ob.concepto}</div>
                    <div style={{fontSize:10,color:C.inkDim}}>{ob.detalle}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:isSel?C.green:C.ink}}>{fmt(ob.monto)}</div>
                    <div style={{fontSize:10,color:C.inkDim}}>vence {fmtF(ob.vence)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.bg,borderRadius:6,marginBottom:14,border:`1px solid ${C.border}`}}>
            <span style={{fontSize:12,color:C.inkMid}}>{sel.size} seleccionadas</span>
            <span style={{fontSize:15,fontWeight:700,color:C.green}}>{fmt(totalSel)}</span>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={()=>setModo("elegir")} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cancelar</Btn>
            <Btn onClick={()=>genVEP(pendientes.filter(o=>sel.has(o.id)))} disabled={sel.size===0} variant="green" style={{flex:2,justifyContent:"center"}}>Generar VEP · {fmt(totalSel)}</Btn>
          </div>
        </>
      )}

      {/* VEP GENERADO */}
      {modo==="generado"&&!pagado&&(
        <>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>VEP Generado</div>
            <div style={{fontSize:17,fontWeight:700,color:C.ink}}>{cliente.nombre}</div>
          </div>
          <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:14}}>
            <div style={{background:`linear-gradient(90deg,${C.navy}33,${C.green}22)`,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:"0.06em"}}>VOLANTE ELECTRÓNICO DE PAGO</span>
              <span style={{fontSize:18,fontWeight:700,color:C.green}}>{fmt(vep.total)}</span>
            </div>
            {[["N° VEP",vep.num],["Titular",cliente.nombre],["CUIT",cliente.cuit],["Vencimiento",fmtF(vep.vence)]].map(([l,v],i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 16px",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <span style={{color:C.inkDim}}>{l}</span>
                <span style={{fontWeight:600,color:C.ink,fontFamily:l==="N° VEP"?"monospace":"inherit"}}>{v}</span>
              </div>
            ))}
            <div style={{padding:"10px 16px"}}>
              {vep.obs.map((o,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"3px 0",color:C.inkMid}}>
                  <span>{o.concepto}</span><span style={{fontWeight:600,color:C.ink}}>{fmt(o.monto)}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"9px 12px",marginBottom:14,fontSize:11,color:C.green,lineHeight:1.6}}>
            ✓ Notificación enviada a {cliente.nombre.split(" ")[0]}. El cliente paga directo en ARCA con su clave fiscal.
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={onClose} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cerrar</Btn>
            <a href={linkARCA} target="_blank" rel="noreferrer" style={{flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:C.green,color:"#0A1219",border:"none",borderRadius:6,padding:"8px 16px",fontSize:12,fontWeight:700,textDecoration:"none",letterSpacing:"0.02em"}}>
              🔗 Ir a pagar en ARCA
            </a>
          </div>
        </>
      )}

      {pagado&&(
        <div style={{textAlign:"center",padding:"20px 0"}}>
          <div style={{fontSize:44,marginBottom:12}}>✅</div>
          <div style={{fontSize:18,fontWeight:700,color:C.green,marginBottom:6}}>Pago confirmado</div>
          <div style={{fontSize:12,color:C.inkMid,marginBottom:16}}>VEP {vep.num} · {fmt(vep.total)}</div>
          <Btn onClick={onClose} variant="green" style={{margin:"0 auto",justifyContent:"center"}}>Cerrar</Btn>
        </div>
      )}
    </Modal>
  );
};

// ─── MODAL DETALLE CLIENTE CON FACTURACIÓN MP ────────────────────────────────
const ModalDetalleCliente = ({cliente, obligaciones, onClose, onVEP, showToast}) => {
  const [tab, setTab]           = useState("obligaciones"); // obligaciones | facturacion
  const [paso, setPaso]         = useState("inicio");
  const [progreso, setProgreso] = useState(0);
  const [pasoTxt, setPasoTxt]   = useState("");
  const [movimientos, setMovs]  = useState([]);
  const [sel, setSel]           = useState(new Set());
  const [filtro, setFiltro]     = useState("entradas");
  const [modalConf, setModalConf] = useState(false);
  const [facturasOk, setFacturasOk] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [categoriaPorMov, setCategoriaPorMov] = useState({}); // {movId: "Prendas de vestir"}
  const [movAbierto, setMovAbierto] = useState(null); // id del movimiento con el desplegable abierto

  const PASOS_IA = ["Leyendo el PDF...","Extrayendo encabezado...","Identificando período...","Procesando movimientos...","Clasificando entradas y salidas...","Detectando transferencias recibidas...","Verificando montos...","Clasificación completada"];

  const MOVIMIENTOS_DEMO = [
    {id:"156537312975",fecha:"2026-05-01",descripcion:"Transferencia recibida Zaira Yanina Aranguiz",valor:15000,tipo:"entrada"},
    {id:"157892341021",fecha:"2026-05-03",descripcion:"Pago recibido - Servicio de asesoramiento",   valor:45000,tipo:"entrada"},
    {id:"158234109876",fecha:"2026-05-05",descripcion:"Transferencia recibida Roberto Carlos Perez",  valor:28500,tipo:"entrada"},
    {id:"158901234567",fecha:"2026-05-07",descripcion:"Pago recibido - Honorarios profesionales",     valor:62000,tipo:"entrada"},
    {id:"157232398160",fecha:"2026-05-01",descripcion:"Transferencia enviada Maria Amanda Dupuy",     valor:-2800,tipo:"salida"},
    {id:"156480114423",fecha:"2026-05-01",descripcion:"Transferencia enviada Silvina Arellano",       valor:-1500,tipo:"salida"},
  ];

  const procesarPDFReal = async (archivo) => {
    setPaso("procesando"); setProgreso(0);
    let i=0;
    const iv=setInterval(()=>{ if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;} },400);
    try {
      const base64 = await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(archivo);});
      const resp = await authFetch(`${API_URL}/pdf/procesar`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pdf:base64,clienteId:cliente.id})});
      clearInterval(iv); setProgreso(100);
      const data = await resp.json();
      setTimeout(()=>{
        const movs = data.ok?data.data.movimientos:MOVIMIENTOS_DEMO;
        setMovs(movs); setSel(new Set(movs.filter(m=>m.tipo==="entrada").map(m=>m.id)));
        setPaso("revision");
      },400);
    } catch {
      clearInterval(iv);
      setTimeout(()=>{ setMovs(MOVIMIENTOS_DEMO); setSel(new Set(MOVIMIENTOS_DEMO.filter(m=>m.tipo==="entrada").map(m=>m.id))); setPaso("revision"); },400);
    }
  };

  const procesarDemo = () => {
    setPaso("procesando"); setProgreso(0); let i=0;
    const iv=setInterval(()=>{
      if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;}
      else{clearInterval(iv);setTimeout(()=>{setMovs(MOVIMIENTOS_DEMO);setSel(new Set(MOVIMIENTOS_DEMO.filter(m=>m.tipo==="entrada").map(m=>m.id)));setPaso("revision");},400);}
    },320);
  };

  const entradasCnt = movimientos.filter(m=>m.tipo==="entrada").length;
  const salidasCnt  = movimientos.filter(m=>m.tipo==="salida").length;
  const selCnt      = sel.size;
  const totalSel    = movimientos.filter(m=>sel.has(m.id)).reduce((s,m)=>s+m.valor,0);
  const movsParaFact= movimientos.filter(m=>sel.has(m.id));

  const onEmitir = (aprobadas) => { setFacturasOk(aprobadas); setModalConf(false); setPaso("emitidas"); showToast(`${aprobadas.length} facturas emitidas para ${cliente.nombre.split(" ")[0]}`); };
  const reiniciarFact = () => { setPaso("inicio"); setMovs([]); setSel(new Set()); setFiltro("entradas"); setFacturasOk([]); };

  const obsCliente = obligaciones.filter(o=>o.clienteId===cliente.id);
  const pendientes = obsCliente.filter(o=>o.estado==="pendiente");
  const tipoFact   = cliente.tipo==="Monotributista"?"C":"B";

  return (
    <Modal onClose={onClose} maxW={620}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:42,height:42,borderRadius:10,background:`linear-gradient(135deg,${C.navy}44,${C.green}44)`,border:`1px solid ${C.borderMid}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.green}}>
            {initiales(cliente.nombre)}
          </div>
          <div>
            <div style={{fontSize:17,fontWeight:700,color:C.ink}}>{cliente.nombre}</div>
            <div style={{fontSize:11,color:C.inkDim,fontFamily:"monospace"}}>{cliente.cuit} · {cliente.tipo==="Monotributista"?`Mono Cat. ${cliente.cat}`:"Resp. Inscripto"}</div>
          </div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim}}>×</button>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:18,borderBottom:`1px solid ${C.border}`,paddingBottom:12}}>
        {[["obligaciones","📋 Obligaciones"],["facturacion","📄 Facturación MP"]].map(([k,l])=>(
          <button key={k} onClick={()=>{setTab(k);if(k==="facturacion")reiniciarFact();}}
            style={{padding:"6px 14px",borderRadius:6,border:`1px solid ${tab===k?C.green+"44":C.border}`,background:tab===k?C.greenDim:"transparent",color:tab===k?C.green:C.inkMid,fontSize:12,fontWeight:tab===k?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>
            {l}
          </button>
        ))}
      </div>

      {/* TAB OBLIGACIONES */}
      {tab==="obligaciones"&&(
        <>
          <div style={{fontSize:10,fontWeight:700,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:8}}>Obligaciones del mes</div>
          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:16}}>
            {obsCliente.map((ob,i,arr)=>(
              <div key={ob.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:i<arr.length-1?`1px solid ${C.border}`:"none",background:i%2===0?C.bgCard:C.bg}}>
                <div>
                  <div style={{fontSize:12,fontWeight:500,color:C.ink}}>{ob.concepto}</div>
                  <div style={{fontSize:10,color:C.inkDim}}>Vence {fmtF(ob.vence)}</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:13,fontWeight:700,color:C.ink}}>{fmt(ob.monto)}</span>
                  {ob.estado==="pagado"
                    ?<Badge color={C.success} bg={C.successBg} border={C.success+"44"}>Pagado</Badge>
                    :<Badge color={C.warning} bg={C.warningBg} border={C.warning+"44"}>Pendiente</Badge>
                  }
                </div>
              </div>
            ))}
          </div>
          {pendientes.length>0&&(
            <Btn onClick={onVEP} variant="green" style={{width:"100%",justifyContent:"center"}}>
              📄 Generar VEP · {fmt(pendientes.reduce((s,o)=>s+o.monto,0))} →
            </Btn>
          )}
        </>
      )}

      {/* TAB FACTURACIÓN MP */}
      {tab==="facturacion"&&(
        <>
          {/* INICIO */}
          {paso==="inicio"&&(
            <div>
              <div style={{fontSize:11,color:C.inkDim,marginBottom:14,lineHeight:1.7}}>
                Subí el resumen PDF de Mercado Pago de <strong style={{color:C.ink}}>{cliente.nombre.split(" ")[0]}</strong>. La IA clasifica automáticamente los cobros y los prepara para facturar a ARCA.
              </div>
              <label style={{display:"block",border:`2px dashed ${C.green+"44"}`,borderRadius:8,padding:"28px 20px",textAlign:"center",cursor:"pointer",background:C.bg,transition:"all 0.2s",marginBottom:12}}
                onMouseEnter={e=>e.currentTarget.style.background=C.greenDim}
                onMouseLeave={e=>e.currentTarget.style.background=C.bg}>
                <input type="file" accept=".pdf" onChange={e=>{const f=e.target.files?.[0];if(f)procesarPDFReal(f);}} style={{display:"none"}}/>
                <div style={{fontSize:28,marginBottom:8}}>📄</div>
                <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>Seleccioná el PDF de MP</div>
                <div style={{fontSize:11,color:C.inkDim,marginBottom:12}}>Resumen de cuenta en pesos · {cliente.nombre.split(" ")[0]}</div>
                <span style={{background:C.green,color:"#0A1219",padding:"6px 14px",borderRadius:5,fontSize:11,fontWeight:700}}>📤 Seleccionar PDF</span>
              </label>
              <div style={{textAlign:"center"}}>
                <button onClick={procesarDemo} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 12px",fontSize:11,color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Usar datos de demo
                </button>
              </div>
            </div>
          )}

          {/* PROCESANDO */}
          {paso==="procesando"&&(
            <div style={{textAlign:"center",padding:"32px 0"}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
                <div style={{width:40,height:40,border:`2px solid ${C.border}`,borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              </div>
              <div style={{fontSize:14,fontWeight:600,color:C.ink,marginBottom:6}}>Procesando PDF</div>
              <div style={{fontSize:11,color:C.green,marginBottom:16,minHeight:16}}>{pasoTxt}</div>
              <div style={{background:C.bg,borderRadius:3,height:3,maxWidth:300,margin:"0 auto 6px",overflow:"hidden"}}>
                <div style={{height:"100%",background:C.green,borderRadius:3,width:`${progreso}%`,transition:"width 0.35s ease"}}/>
              </div>
              <div style={{fontSize:10,color:C.inkDim}}>{progreso}%</div>
            </div>
          )}

          {/* REVISIÓN */}
          {paso==="revision"&&(
            <>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",gap:6}}>
                  {[["entradas",`Cobros (${entradasCnt})`,C.success],["salidas",`Salidas (${salidasCnt})`,C.danger],["todos","Todos",C.inkMid]].map(([k,l,c])=>(
                    <button key={k} onClick={()=>setFiltro(k)} style={{padding:"4px 9px",borderRadius:4,border:`1px solid ${filtro===k?c:C.border}`,background:filtro===k?`${c}18`:"transparent",color:filtro===k?c:C.inkMid,fontSize:10,fontWeight:filtro===k?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{l}</button>
                  ))}
                </div>
                <div style={{display:"flex",gap:4}}>
                  <button onClick={()=>setSel(new Set(movimientos.filter(m=>m.tipo==="entrada").map(m=>m.id)))} style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.green,fontFamily:"'Inter',sans-serif"}}>☑ Todos</button>
                  <button onClick={()=>setSel(new Set())} style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>☐ Ninguno</button>
                </div>
              </div>

              <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:12,maxHeight:240,overflowY:"auto"}}>
                {movimientos.filter(m=>filtro==="todos"||m.tipo===filtro.replace("cobros","entrada")).map((m,i)=>{
                  const isSel=sel.has(m.id);
                  const isSelectable=m.tipo==="entrada";
                  return (
                    <div key={m.id} onClick={()=>isSelectable&&setSel(prev=>{const n=new Set(prev);isSel?n.delete(m.id):n.add(m.id);return n;})}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:isSel?C.greenDim:i%2===0?C.bgCard:C.bg,cursor:isSelectable?"pointer":"default",transition:"background 0.1s"}}>
                      <div style={{width:15,height:15,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {isSelectable&&<div style={{width:14,height:14,borderRadius:3,border:`2px solid ${isSel?C.green:C.borderMid}`,background:isSel?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>{isSel&&<span style={{color:"#0A1219",fontSize:9}}>✓</span>}</div>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.ink}}>{m.descripcion}</div>
                        <div style={{fontSize:9,color:C.inkDim,marginTop:1}}>{fmtF(m.fecha)}</div>
                      </div>
                      <span style={{fontWeight:700,flexShrink:0,color:m.valor>=0?C.success:C.danger}}>{m.valor>=0?"+":""}{fmt(m.valor)}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.bg,borderRadius:7,marginBottom:12,border:`1px solid ${selCnt>0?C.green+"44":C.border}`}}>
                <span style={{fontSize:11,color:C.inkMid}}>{selCnt>0?`${selCnt} cobro${selCnt>1?"s":""} seleccionado${selCnt>1?"s":""}`:""}</span>
                {selCnt>0&&<span style={{fontSize:14,fontWeight:700,color:C.green}}>{fmt(totalSel)}</span>}
              </div>

              <div style={{display:"flex",gap:8}}>
                <Btn onClick={reiniciarFact} variant="ghost" style={{flex:1,justifyContent:"center"}}>← Volver</Btn>
                <Btn onClick={()=>selCnt>0&&setModalConf(true)} disabled={selCnt===0} variant="green" style={{flex:2,justifyContent:"center"}}>
                  ⚡ Emitir {selCnt||""} factura{selCnt!==1?"s":""} a ARCA
                </Btn>
              </div>
            </>
          )}

          {/* EMITIDAS */}
          {paso==="emitidas"&&(
            <div>
              <div style={{textAlign:"center",padding:"20px 0 16px"}}>
                <div style={{fontSize:36,marginBottom:8}}>✅</div>
                <div style={{fontSize:16,fontWeight:700,color:C.success,marginBottom:4}}>{facturasOk.length} facturas emitidas</div>
                <div style={{fontSize:11,color:C.inkDim}}>{fmt(facturasOk.reduce((s,f)=>s+f.monto,0))} facturado para {cliente.nombre.split(" ")[0]}</div>
              </div>
              <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:14,maxHeight:180,overflowY:"auto"}}>
                {facturasOk.map((f,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 12px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:i%2===0?C.bgCard:C.bg}}>
                    <div>
                      <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:260,color:C.ink}}>{f.descripcion}</div>
                      <div style={{fontSize:9,fontFamily:"monospace",color:C.success}}>CAE {f.cae?.slice(0,10)}...</div>
                    </div>
                    <span style={{fontWeight:700,color:C.success}}>{fmt(f.monto)}</span>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn onClick={reiniciarFact} variant="ghost" style={{flex:1,justifyContent:"center"}}>+ Nuevo PDF</Btn>
                <Btn onClick={onClose} variant="success" style={{flex:1,justifyContent:"center"}}>✓ Cerrar</Btn>
              </div>
            </div>
          )}

          {modalConf&&(
            <ModalEnvioMasivo
              movimientos={movsParaFact}
              cliente={cliente}
              onClose={()=>setModalConf(false)}
              onConfirmar={onEmitir}
            />
          )}
        </>
      )}
    </Modal>
  );
};

// ─── SECCIÓN CLIENTES ─────────────────────────────────────────────────────────
// ─── MODAL NUEVO CLIENTE ─────────────────────────────────────────────────────
const ModalNuevoCliente = ({onClose, onGuardado}) => {
  const [form, setForm] = useState({
    nombre: "", cuit: "", tipo: "Monotributista", categoria: "A",
    telefono: "", email: ""
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError]         = useState(null);

  const set = (k,v) => setForm(prev=>({...prev,[k]:v}));

  const guardar = async () => {
    if(!form.nombre.trim()) return setError("El nombre es obligatorio");
    if(!form.cuit.trim())   return setError("El CUIT es obligatorio");

    const validacion = validarCuitFrontend(form.cuit);
    if(!validacion.valido) return setError(validacion.error);

    setGuardando(true); setError(null);
    try {
      const resp = await authFetch(`${API_URL}/clientes`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify(form)
      });
      const data = await resp.json();
      if(data.ok) {
        onGuardado(data.data);
        onClose();
      } else {
        setError(data.error || "Error al guardar");
      }
    } catch {
      setError("Error de conexión con el servidor");
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = {
    width:"100%", padding:"9px 12px", background:C.bgInput,
    border:`1px solid ${C.border}`, borderRadius:6, fontSize:13,
    color:C.ink, outline:"none", fontFamily:"'Inter',sans-serif",
    transition:"border 0.15s"
  };

  const labelStyle = {
    fontSize:10, fontWeight:700, color:C.inkDim,
    letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:5, display:"block"
  };

  return (
    <Modal onClose={onClose} maxW={480}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Nuevo cliente</div>
          <div style={{fontSize:17,fontWeight:700,color:C.ink}}>Agregar cliente al estudio</div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim}}>×</button>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {/* Nombre */}
        <div>
          <label style={labelStyle}>Nombre completo *</label>
          <input value={form.nombre} onChange={e=>set("nombre",e.target.value)}
            placeholder="Ej: Juan García"
            style={inputStyle}
            onFocus={e=>e.target.style.borderColor=C.green}
            onBlur={e=>e.target.style.borderColor=C.border}/>
        </div>

        {/* CUIT */}
        <div>
          <label style={labelStyle}>CUIT *</label>
          <input value={form.cuit} onChange={e=>set("cuit",e.target.value)}
            placeholder="Ej: 20-35482910-4"
            style={{...inputStyle, borderColor: form.cuit && !validarCuitFrontend(form.cuit).valido ? C.danger : inputStyle.border}}
            onFocus={e=>e.target.style.borderColor=C.green}
            onBlur={e=>{
              const v = validarCuitFrontend(form.cuit);
              e.target.style.borderColor = form.cuit && !v.valido ? C.danger : C.border;
            }}/>
          {form.cuit && !validarCuitFrontend(form.cuit).valido && (
            <div style={{fontSize:10,color:C.danger,marginTop:4}}>⚠ {validarCuitFrontend(form.cuit).error}</div>
          )}
        </div>

        {/* Tipo + Categoría */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <label style={labelStyle}>Tipo *</label>
            <select value={form.tipo} onChange={e=>set("tipo",e.target.value)}
              style={{...inputStyle,cursor:"pointer"}}>
              <option value="Monotributista">Monotributista</option>
              <option value="Resp. Inscripto">Resp. Inscripto</option>
            </select>
          </div>
          <div style={{opacity:form.tipo==="Monotributista"?1:0.3}}>
            <label style={labelStyle}>Categoría</label>
            <select value={form.categoria} onChange={e=>set("categoria",e.target.value)}
              disabled={form.tipo!=="Monotributista"}
              style={{...inputStyle,cursor:form.tipo==="Monotributista"?"pointer":"not-allowed"}}>
              {["A","B","C","D","E","F","G","H","I","J","K"].map(c=>(
                <option key={c} value={c}>Categoría {c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Teléfono */}
        <div>
          <label style={labelStyle}>Teléfono</label>
          <input value={form.telefono} onChange={e=>set("telefono",e.target.value)}
            placeholder="Ej: +5493442001122"
            style={inputStyle}
            onFocus={e=>e.target.style.borderColor=C.green}
            onBlur={e=>e.target.style.borderColor=C.border}/>
        </div>

        {/* Email */}
        <div>
          <label style={labelStyle}>Email</label>
          <input value={form.email} onChange={e=>set("email",e.target.value)}
            placeholder="Ej: juan@ejemplo.com"
            type="email"
            style={inputStyle}
            onFocus={e=>e.target.style.borderColor=C.green}
            onBlur={e=>e.target.style.borderColor=C.border}/>
        </div>
      </div>

      {error&&(
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"8px 12px",marginTop:14,fontSize:11,color:C.danger}}>
          ⚠ {error}
        </div>
      )}

      <div style={{display:"flex",gap:8,marginTop:20}}>
        <Btn onClick={onClose} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cancelar</Btn>
        <Btn onClick={guardar} disabled={guardando} variant="green" style={{flex:2,justifyContent:"center"}}>
          {guardando?(
            <><div style={{width:12,height:12,border:`2px solid rgba(0,0,0,0.3)`,borderTopColor:"#0A1219",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/> Guardando...</>
          ):"+ Agregar cliente"}
        </Btn>
      </div>
    </Modal>
  );
};

const SeccionClientes = ({clientes, setClientes, obligaciones, setObligaciones, showToast, onVerCliente}) => {
  // Compara el nombre escrito con el del cliente sin exigir que coincidan las
  // mayúsculas, las tildes ni los espacios de más: sigue siendo una
  // confirmación real, pero deja de fallar por detalles que no se ven.
  const mismoNombre = (a, b) => {
    const norm = s => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"")
                              .replace(/\s+/g," ").trim().toLowerCase();
    return norm(a) === norm(b);
  };

  const [aEliminar, setAEliminar] = useState(null);   // cliente elegido para borrar
  const [datosBorrar, setDatosBorrar] = useState(null); // qué se pierde al borrarlo
  const [textoConf, setTextoConf] = useState("");     // el contador escribe el nombre
  const [borrando, setBorrando] = useState(false);

  // Al abrir el cartel se consulta qué tiene cargado el cliente, así el
  // contador ve exactamente qué se va a borrar antes de confirmar.
  const pedirEliminar = async (cli) => {
    setAEliminar(cli); setTextoConf(""); setDatosBorrar(null);
    try {
      const resp = await authFetch(`${API_URL}/clientes/${cli.id}/datos-asociados`);
      const d = await resp.json();
      if (d.ok) setDatosBorrar(d.data);
    } catch(e) { /* si falla, el cartel igual permite borrar */ }
  };

  const confirmarEliminar = async () => {
    if (!aEliminar) return;
    setBorrando(true);
    try {
      const resp = await authFetch(`${API_URL}/clientes/${aEliminar.id}`, { method:"DELETE" });
      const d = await resp.json();
      if (!d.ok) { showToast("❌ " + (d.error||"No se pudo eliminar")); setBorrando(false); return; }
      setClientes(prev => prev.filter(x => x.id !== aEliminar.id));
      showToast(`Cliente ${aEliminar.nombre} eliminado`);
      setAEliminar(null); setDatosBorrar(null); setTextoConf("");
    } catch(e) { showToast("❌ Error: " + e.message); }
    setBorrando(false);
  };

  const isMobile = useMobile();
  const [busqueda, setBusqueda]       = useState("");
  const [filtro, setFiltro]           = useState("todos");
  const [modalVEP, setModalVEP]       = useState(null);
  const [modalDet, setModalDet]       = useState(null);
  const [modalNuevo, setModalNuevo]   = useState(false);

  const genObs = (c) => {
    if(c.tipo==="Monotributista"){
      const cat=CUOTAS_MONO[c.cat]; if(!cat) return [];
      return Object.entries(cat).map(([k,monto])=>({
        id:`${c.id}-${k}`, clienteId:c.id, nombre:c.nombre,
        concepto: k==="i"?"Impuesto integrado":k==="j"?"Aporte jubilatorio":"Obra social",
        monto, vence:"2026-06-20", estado:"pendiente",
      }));
    }
    return [
      {id:`${c.id}-iva`, clienteId:c.id,nombre:c.nombre,concepto:"IVA Mensual",          monto:112000,vence:"2026-06-18",estado:"pendiente"},
      {id:`${c.id}-gan`, clienteId:c.id,nombre:c.nombre,concepto:"Ganancias (Anticipo)", monto:48000, vence:"2026-06-20",estado:"pendiente"},
      {id:`${c.id}-iibb`,clienteId:c.id,nombre:c.nombre,concepto:"Ingresos Brutos",      monto:18500, vence:"2026-06-22",estado:"pendiente"},
    ];
  };

  const pct = (c) => {
    if(c.tipo!=="Monotributista") return null;
    const lim=LIMITES_MONO[c.cat]; if(!lim) return null;
    return Math.min(100,Math.round((c.facturacionAnual/lim)*100));
  };

  const urgente = (c) => {
    if(c.arcaEstado==="Suspendido") return true;
    if(c.estadoPago==="vencido") return true;
    if(c.plan?.estado==="en_riesgo") return true;
    const p=pct(c); if(p&&p>=90) return true;
    return false;
  };

  const filtrados = clientes.filter(c=>{
    const mb=c.nombre.toLowerCase().includes(busqueda.toLowerCase())||c.cuit.includes(busqueda);
    if(!mb) return false;
    if(filtro==="urgentes") return urgente(c);
    if(filtro==="mono")     return c.tipo==="Monotributista";
    if(filtro==="ri")       return c.tipo==="Resp. Inscripto";
    return true;
  });

  const onPagarVEP = (ids, vepNum) => {
    setObligaciones(prev=>prev.map(o=>ids.includes(o.id)?{...o,estado:"pagado"}:o));
    setModalVEP(null);
    showToast(`VEP ${vepNum} pagado correctamente`);
  };

  const urgentes    = clientes.filter(urgente).length;
  const suspendidos = clientes.filter(c=>c.arcaEstado==="Suspendido").length;

  return (
    <div>
      {/* Búsqueda hero — centro de la pantalla */}
      <div style={{textAlign:"center",marginBottom:28,paddingTop:8}}>
        <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12}}>Clientes del estudio · Junio 2026</div>
        <div style={{position:"relative",display:"inline-block",width:"100%",maxWidth:480}}>
          <span style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",fontSize:16,color:C.inkDim,pointerEvents:"none"}}>⌕</span>
          <input value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Buscar por nombre o CUIT..."
            style={{width:"100%",padding:"13px 16px 13px 44px",background:C.bgCard,border:`1px solid ${busqueda?C.green+"66":C.borderMid}`,borderRadius:10,fontSize:14,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif",transition:"border 0.2s",boxShadow:`0 4px 20px rgba(0,0,0,0.3)`}}/>
        </div>
        {busqueda&&<div style={{marginTop:8,fontSize:11,color:C.inkDim}}>{filtrados.length} resultado{filtrados.length!==1?"s":""}</div>}
        <div style={{marginTop:14}}>
          <Btn onClick={()=>setModalNuevo(true)} variant="green" style={{margin:"0 auto"}}>
            + Agregar cliente
          </Btn>
        </div>
      </div>

      {/* Banner urgentes */}
      {urgentes>0&&(
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:8,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:12,fontWeight:600,color:C.danger}}>⚠ {urgentes} cliente{urgentes>1?"s requieren":" requiere"} atención urgente</span>
          <Btn onClick={()=>setFiltro("urgentes")} variant="danger" small>Ver →</Btn>
        </div>
      )}

      {/* Stats */}
      <div className="venci-stats" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,1fr)",gap:10,marginBottom:20}}>
        {[
          {label:"Clientes activos",  value:clientes.length,       color:C.ink},
          {label:"Requieren atención",value:urgentes,              color:urgentes>0?C.danger:C.success},
          {label:"Suspendidos ARCA",  value:suspendidos,           color:suspendidos>0?C.danger:C.success},
          {label:"Con plan de pago",  value:clientes.filter(c=>c.plan).length, color:C.warning},
        ].map((s,i)=>(
          <div key={i} style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px"}}>
            <div style={{fontSize:10,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:24,fontWeight:700,color:s.color,fontFamily:"'Inter',sans-serif"}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros — scroll horizontal en mobile */}
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",overflowX:isMobile?"auto":"visible",WebkitOverflowScrolling:"touch"}}>

        <div style={{display:"flex",gap:4,flexShrink:0}}>
          {[["todos","Todos"],["urgentes","Urgentes"],["mono","Monotributistas"],["ri","Resp. Inscripto"]].map(([k,l])=>(
            <button key={k} onClick={()=>setFiltro(k)}
              style={{padding:"6px 12px",borderRadius:5,border:`1px solid ${filtro===k?C.green:C.border}`,background:filtro===k?C.greenDim:"transparent",color:filtro===k?C.green:C.inkMid,fontSize:11,fontWeight:filtro===k?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>
              {l}
            </button>
          ))}
        </div>
        <span style={{fontSize:11,color:C.inkDim,marginLeft:"auto"}}>{filtrados.length} de {clientes.length}</span>
      </div>

      {/* Grilla */}
      <div className="venci-grid-clientes" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
        {filtrados.map(c=>{
          const p         = pct(c);
          const esMono    = c.tipo==="Monotributista";
          const susp      = c.arcaEstado==="Suspendido";
          const urg       = urgente(c);
          const diasV     = diasHasta(c.proximoVencimiento||c.ivaVence||"2026-06-30");
          const colorPct  = p>=90?C.danger:p>=75?C.warning:C.green;
          const obsCliente= obligaciones.filter(o=>o.clienteId===c.id);
          const pendientes= obsCliente.filter(o=>o.estado==="pendiente");
          const totalPend = pendientes.reduce((s,o)=>s+o.monto,0);

          return (
            <div key={c.id}
              style={{background:C.bgCard,border:`1px solid ${urg?C.danger+"44":C.border}`,borderRadius:10,padding:"16px",position:"relative",overflow:"hidden",transition:"all 0.15s",cursor:"pointer"}}
              onClick={()=>onVerCliente&&onVerCliente(c)}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=urg?C.danger+"88":C.green+"66";e.currentTarget.style.background=C.bgCardHov;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=urg?C.danger+"44":C.border;e.currentTarget.style.background=C.bgCard;}}>

              {urg&&<div style={{position:"absolute",top:0,left:0,right:0,height:2,background:C.danger}}/>}

              {/* Header */}
              <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:12}}>
                <div style={{width:36,height:36,borderRadius:8,background:susp?C.dangerBg:`linear-gradient(135deg,${C.navy}44,${C.green}44)`,border:`1px solid ${susp?C.danger+"44":C.borderMid}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:susp?C.danger:C.green,flexShrink:0,fontFamily:"'Inter',sans-serif"}}>
                  {initiales(c.nombre)}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nombre}</div>
                  <div style={{fontSize:10,color:C.inkDim,fontFamily:"monospace",marginTop:1}}>{c.cuit}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"flex-end"}}>
                  {susp
                    ?<Badge color={C.danger} bg={C.dangerBg} border={C.danger+"44"}>Suspendido</Badge>
                    :<Badge color={C.success} bg={C.successBg} border={C.success+"44"}>Activo</Badge>
                  }
                  {c.plan?.estado==="en_riesgo"&&<Badge color={C.warning} bg={C.warningBg} border={C.warning+"44"}>Plan en riesgo</Badge>}
                </div>
              </div>

              {/* Tipo */}
              <div style={{fontSize:11,color:C.inkDim,marginBottom:10}}>
                {esMono?`Monotributista${c.cat||c.categoria?` · Categoría ${c.cat||c.categoria}`:""}`:"Responsable Inscripto"}
              </div>

              {/* Barra facturación */}
              {esMono&&p!==null&&(
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:4}}>
                    <span style={{color:C.inkDim}}>Facturación anual</span>
                    <span style={{fontWeight:700,color:colorPct}}>{p}%</span>
                  </div>
                  <div style={{height:4,background:C.bg,borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${p}%`,background:colorPct,borderRadius:2,transition:"width 0.6s"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:3,fontSize:10,color:C.inkDim}}>
                    <span>{fmt(c.facturacionAnual)}</span>
                    <span>límite {fmt(LIMITES_MONO[c.cat])}</span>
                  </div>
                  {p>=75&&<div style={{marginTop:4,fontSize:10,color:colorPct,fontWeight:600}}>{p>=90?"⚠ Cerca del límite de categoría":"↑ Acercándose al límite"}</div>}
                </div>
              )}

              {/* RI facturación */}
              {!esMono&&(
                <div style={{display:"flex",gap:6,marginBottom:10}}>
                  <div style={{flex:1,background:C.bg,borderRadius:5,padding:"6px 8px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:9,color:C.inkDim}}>Fact. del mes</div>
                    <div style={{fontSize:12,fontWeight:700,color:C.ink}}>{fmt(c.facturacionMes)}</div>
                  </div>
                  <div style={{flex:1,background:C.bg,borderRadius:5,padding:"6px 8px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:9,color:C.inkDim}}>IVA vence</div>
                    <div style={{fontSize:12,fontWeight:700,color:diasHasta(c.ivaVence)<=3?C.danger:C.ink}}>{fmtF(c.ivaVence)}</div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                <button onClick={(e)=>{ e.stopPropagation(); e.preventDefault(); pedirEliminar(c); }}
                  title="Eliminar cliente"
                  style={{background:"transparent",border:"none",cursor:"pointer",color:C.inkDim,fontSize:11,padding:"4px 6px",fontFamily:"'Inter',sans-serif",borderRadius:4,transition:"color 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.color=C.danger}
                  onMouseLeave={e=>e.currentTarget.style.color=C.inkDim}>
                  🗑 Eliminar
                </button>
                <Btn onClick={()=>onVerCliente&&onVerCliente(c)} variant="ghost" small>Ver cliente →</Btn>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal eliminar cliente — pide escribir el nombre para confirmar,
          porque el borrado es definitivo y los nombres de la lista se parecen */}
      {aEliminar&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
          <div style={{background:C.bgCard,borderRadius:14,width:"100%",maxWidth:460,border:`1px solid ${C.border}`,boxShadow:"0 32px 80px rgba(0,0,0,0.5)",overflow:"hidden"}}>
            <div style={{background:C.dangerBg,borderBottom:`1px solid ${C.danger}44`,padding:"14px 18px"}}>
              <div style={{fontSize:14,fontWeight:800,color:C.danger}}>Eliminar cliente</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Esta acción no se puede deshacer.</div>
            </div>
            <div style={{padding:"18px"}}>
              <div style={{fontSize:13,color:C.ink,marginBottom:4,fontWeight:700}}>{aEliminar.nombre}</div>
              <div style={{fontSize:11,color:C.inkDim,fontFamily:"monospace",marginBottom:14}}>{aEliminar.cuit}</div>

              {datosBorrar
                ? (datosBorrar.facturas>0||datosBorrar.obligaciones>0)
                  ? (
                    <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:8,padding:"11px 13px",marginBottom:14}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.danger,marginBottom:5}}>Se va a borrar también:</div>
                      {datosBorrar.facturas>0&&(
                        <div style={{fontSize:11,color:C.inkMid}}>· {datosBorrar.facturas} factura{datosBorrar.facturas!==1?"s":""} emitida{datosBorrar.facturas!==1?"s":""}</div>
                      )}
                      {datosBorrar.obligaciones>0&&(
                        <div style={{fontSize:11,color:C.inkMid}}>· {datosBorrar.obligaciones} obligación{datosBorrar.obligaciones!==1?"es":""}</div>
                      )}
                      {datosBorrar.facturas>0&&(
                        <div style={{fontSize:10,color:C.inkDim,marginTop:6,lineHeight:1.5}}>
                          Las facturas ya emitidas siguen existiendo en ARCA. Lo que se pierde es el registro en Venci.
                        </div>
                      )}
                    </div>
                  )
                  : (
                    <div style={{fontSize:11,color:C.inkDim,marginBottom:14}}>
                      Este cliente no tiene facturas ni obligaciones cargadas.
                    </div>
                  )
                : <div style={{fontSize:11,color:C.inkDim,marginBottom:14}}>Consultando datos del cliente...</div>
              }

              <label style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                Escribí el nombre del cliente para confirmar
              </label>
              <input value={textoConf} onChange={e=>setTextoConf(e.target.value)}
                placeholder={aEliminar.nombre}
                style={{width:"100%",marginTop:5,background:C.bgInput,border:`1px solid ${mismoNombre(textoConf, aEliminar.nombre)?C.danger:C.border}`,borderRadius:7,padding:"9px 11px",fontSize:12,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>

              {textoConf.trim() && !mismoNombre(textoConf, aEliminar.nombre) && (
                <div style={{fontSize:10.5,color:C.danger,marginTop:5}}>
                  El nombre no coincide con el del cliente.
                </div>
              )}

              <div style={{display:"flex",gap:8,marginTop:16}}>
                <Btn onClick={()=>{setAEliminar(null);setDatosBorrar(null);setTextoConf("");}} variant="ghost" style={{flex:1,justifyContent:"center"}}>
                  Cancelar
                </Btn>
                <Btn onClick={confirmarEliminar}
                  disabled={borrando||!mismoNombre(textoConf, aEliminar.nombre)}
                  variant="danger" style={{flex:1,justifyContent:"center"}}>
                  {borrando?"Eliminando...":"Eliminar definitivamente"}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuevo cliente */}
      {modalNuevo&&(
        <ModalNuevoCliente
          onClose={()=>setModalNuevo(false)}
          onGuardado={(nuevoCliente)=>{
            setClientes(prev=>[...prev, nuevoCliente]);
            showToast(`Cliente ${nuevoCliente.nombre} agregado correctamente`);
          }}
        />
      )}

      {/* Modal VEP */}
      {modalVEP&&<ModalVEP cliente={modalVEP.cliente} obligaciones={modalVEP.obs} onClose={()=>setModalVEP(null)} onPagar={onPagarVEP}/>}

      {/* Modal detalle */}
      {modalDet&&(
        <ModalDetalleCliente
          cliente={modalDet}
          obligaciones={obligaciones}
          onClose={()=>setModalDet(null)}
          onVEP={()=>{setModalDet(null);setModalVEP({cliente:modalDet,obs:obligaciones.filter(o=>o.clienteId===modalDet.id&&o.estado==="pendiente")});}}
          showToast={showToast}
        />
      )}
    </div>
  );
};

// ─── SECCIÓN FACTURACIÓN ──────────────────────────────────────────────────────
const SeccionFacturacion = ({clientes, showToast}) => {
  const [clienteSel, setClienteSel] = useState(null);
  const [paso, setPaso]             = useState("inicio");
  const [progreso, setProgreso]     = useState(0);
  const [pasoTxt, setPasoTxt]       = useState("");
  const [movimientos, setMovs]      = useState([]);
  const [sel, setSel]               = useState(new Set());
  const [filtro, setFiltro]         = useState("entradas");
  const [busqueda, setBusqueda]     = useState("");
  const [modalConf, setModalConf]   = useState(false);
  const [facturasOk, setFacturasOk] = useState([]);

  const PASOS_IA = ["Leyendo el PDF...","Extrayendo encabezado...","Identificando período...","Procesando movimientos...","Clasificando entradas y salidas...","Detectando transferencias recibidas...","Verificando montos...","Clasificación completada"];

  const procesarPDFReal = async (archivo) => {
    setPaso("procesando"); setProgreso(0);
    let i=0;
    const iv=setInterval(()=>{ if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;} },400);
    try {
      const base64 = await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(archivo);});
      const resp = await authFetch(`${API_URL}/pdf/procesar`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pdf:base64,clienteId:clienteSel?.id})});
      clearInterval(iv); setProgreso(100);
      const data = await resp.json();
      setTimeout(()=>{
        const movs = data.ok?data.data.movimientos:MOVIMIENTOS_MP;
        setMovs(movs); setSel(new Set(movs.filter(m=>m.tipo==="entrada").map(m=>m.id)));
        setPaso("revision");
      },400);
    } catch {
      clearInterval(iv);
      setTimeout(()=>{ setMovs(MOVIMIENTOS_MP); setSel(new Set(MOVIMIENTOS_MP.filter(m=>m.tipo==="entrada").map(m=>m.id))); setPaso("revision"); },400);
    }
  };

  const procesarDemo = () => {
    setPaso("procesando"); setProgreso(0); let i=0;
    const iv=setInterval(()=>{
      if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;}
      else{clearInterval(iv);setTimeout(()=>{setMovs(MOVIMIENTOS_MP);setSel(new Set(MOVIMIENTOS_MP.filter(m=>m.tipo==="entrada").map(m=>m.id)));setPaso("revision");},400);}
    },320);
  };

  const movFilt = movimientos.filter(m=>{
    const mb=m.descripcion.toLowerCase().includes(busqueda.toLowerCase());
    if(!mb) return false;
    if(filtro==="entradas") return m.tipo==="entrada";
    if(filtro==="salidas")  return m.tipo==="salida";
    if(filtro==="revision") return m.tipo==="revision";
    return true;
  });

  const entradasCnt = movimientos.filter(m=>m.tipo==="entrada").length;
  const salidasCnt  = movimientos.filter(m=>m.tipo==="salida").length;
  const revCnt      = movimientos.filter(m=>m.tipo==="revision").length;
  const selCnt      = sel.size;
  const totalSel    = movimientos.filter(m=>sel.has(m.id)).reduce((s,m)=>s+m.valor,0);
  const movsParaFact= movimientos.filter(m=>sel.has(m.id));

  const onEmitir = (aprobadas) => { setFacturasOk(aprobadas); setModalConf(false); setPaso("emitidas"); showToast(`${aprobadas.length} facturas emitidas en ARCA`); };
  const reiniciar = () => { setPaso("inicio");setClienteSel(null);setMovs([]);setSel(new Set());setFiltro("entradas");setFacturasOk([]);setBusqueda(""); };

  return (
    <div>
      {paso!=="inicio"&&<div style={{marginBottom:16}}><Btn onClick={reiniciar} variant="ghost" small>← Nuevo PDF</Btn></div>}

      {/* INICIO */}
      {paso==="inicio"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {/* Seleccionar cliente */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px"}}>
            <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:12}}>01 · Seleccioná el cliente</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {clientes.filter(c=>c.tipo==="Monotributista").map(c=>(
                <div key={c.id} onClick={()=>setClienteSel(c)}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",border:`1px solid ${clienteSel?.id===c.id?C.green:C.border}`,borderRadius:7,cursor:"pointer",background:clienteSel?.id===c.id?C.greenDim:"transparent",transition:"all 0.15s"}}
                  onMouseEnter={e=>{if(clienteSel?.id!==c.id){e.currentTarget.style.borderColor=C.borderMid;}}}
                  onMouseLeave={e=>{if(clienteSel?.id!==c.id){e.currentTarget.style.borderColor=C.border;}}}>
                  <div style={{width:30,height:30,borderRadius:6,background:`linear-gradient(135deg,${C.navy}44,${C.green}44)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:C.green,flexShrink:0}}>{initiales(c.nombre)}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:C.ink}}>{c.nombre}</div>
                    <div style={{fontSize:10,color:C.inkDim}}>Cat. {c.cat}</div>
                  </div>
                  {clienteSel?.id===c.id&&<span style={{color:C.green,fontSize:14}}>✓</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Subir PDF */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px",opacity:clienteSel?1:0.4,transition:"opacity 0.2s"}}>
            <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:12}}>02 · Subir PDF de Mercado Pago</div>
            <label style={{display:"block",border:`2px dashed ${clienteSel?C.green+"44":C.border}`,borderRadius:8,padding:"32px 20px",textAlign:"center",cursor:clienteSel?"pointer":"not-allowed",background:C.bg,transition:"all 0.2s"}}
              onMouseEnter={e=>clienteSel&&(e.currentTarget.style.background=C.greenDim)}
              onMouseLeave={e=>clienteSel&&(e.currentTarget.style.background=C.bg)}>
              <input type="file" accept=".pdf" disabled={!clienteSel} onChange={e=>{const f=e.target.files?.[0];if(f)procesarPDFReal(f);}} style={{display:"none"}}/>
              <div style={{fontSize:28,marginBottom:10}}>📄</div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>{clienteSel?"Seleccioná el PDF":"Primero elegí el cliente"}</div>
              {clienteSel&&<div style={{fontSize:11,color:C.inkDim,marginBottom:12}}>{clienteSel.nombre}</div>}
              {clienteSel&&<span style={{background:C.green,color:"#0A1219",padding:"6px 14px",borderRadius:5,fontSize:11,fontWeight:700}}>📤 Seleccionar archivo</span>}
            </label>
            {clienteSel&&(
              <div style={{marginTop:12,textAlign:"center"}}>
                <button onClick={procesarDemo} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 12px",fontSize:11,color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Usar datos de demo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PROCESANDO */}
      {paso==="procesando"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"48px",textAlign:"center"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
            <div style={{width:48,height:48,border:`2px solid ${C.border}`,borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          </div>
          <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:6}}>Procesando PDF</div>
          <div style={{fontSize:12,color:C.green,marginBottom:20,minHeight:18}}>{pasoTxt}</div>
          <div style={{background:C.bg,borderRadius:3,height:3,maxWidth:360,margin:"0 auto 8px",overflow:"hidden"}}>
            <div style={{height:"100%",background:C.green,borderRadius:3,width:`${progreso}%`,transition:"width 0.35s ease"}}/>
          </div>
          <div style={{fontSize:11,color:C.inkDim}}>{progreso}%</div>
        </div>
      )}

      {/* REVISIÓN */}
      {paso==="revision"&&(
        <>
          {/* Resumen PDF */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 18px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                <span style={{fontSize:13,fontWeight:600,color:C.ink}}>PDF procesado correctamente</span>
                <Badge color={C.success} bg={C.successBg} border={C.success+"44"}>IA ✓</Badge>
              </div>
              <div style={{fontSize:11,color:C.inkDim}}>{clienteSel?.nombre} · Mayo 2026</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              {[["Entradas",entradasCnt,C.success],["Salidas",salidasCnt,C.danger],["Revisar",revCnt,C.warning]].map(([l,v,c])=>(
                <div key={l} style={{background:C.bg,borderRadius:5,padding:"6px 10px",border:`1px solid ${C.border}`,textAlign:"center",minWidth:60}}>
                  <div style={{fontSize:9,color:C.inkDim,marginBottom:2}}>{l}</div>
                  <div style={{fontSize:14,fontWeight:700,color:c}}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Filtros tabla */}
          <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
            {[["entradas",`Entradas (${entradasCnt})`,C.success],["salidas",`Salidas (${salidasCnt})`,C.danger],["revision",`Revisar (${revCnt})`,C.warning],["todos","Todos",C.inkMid]].map(([k,l,c])=>(
              <button key={k} onClick={()=>setFiltro(k)} style={{padding:"5px 10px",borderRadius:4,border:`1px solid ${filtro===k?c:C.border}`,background:filtro===k?`${c}18`:"transparent",color:filtro===k?c:C.inkMid,fontSize:11,fontWeight:filtro===k?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>{l}</button>
            ))}
            <input value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Buscar..." style={{marginLeft:"auto",padding:"5px 10px",border:`1px solid ${C.border}`,borderRadius:5,fontSize:11,background:C.bgInput,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif",minWidth:160}}/>
            <button onClick={()=>setSel(new Set(movimientos.filter(m=>m.tipo==="entrada").map(m=>m.id)))} style={{padding:"5px 9px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,fontWeight:600,cursor:"pointer",color:C.green,fontFamily:"'Inter',sans-serif"}}>☑ Todas</button>
            <button onClick={()=>setSel(new Set())} style={{padding:"5px 9px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>☐ Ninguna</button>
          </div>

          {/* Tabla */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden",marginBottom:14}}>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:C.bg}}>
                  <th style={{padding:"8px 12px",width:36,borderBottom:`1px solid ${C.border}`}}/>
                  {["Fecha","Descripción","ID operación","Valor","Tipo"].map(h=>(
                    <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:9,fontWeight:700,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {movFilt.length===0&&<tr><td colSpan={6} style={{padding:24,textAlign:"center",color:C.inkDim,fontSize:12}}>Sin movimientos</td></tr>}
                  {movFilt.map((m,i)=>{
                    const isSel=sel.has(m.id);
                    const isSelectable=m.tipo==="entrada"||m.tipo==="revision";
                    return (
                      <tr key={m.id} onClick={()=>isSelectable&&setSel(prev=>{const n=new Set(prev);isSel?n.delete(m.id):n.add(m.id);return n;})}
                        style={{cursor:isSelectable?"pointer":"default",background:isSel?C.greenDim:i%2===0?C.bgCard:C.bg,transition:"background 0.1s"}}
                        onMouseEnter={e=>{if(!isSel&&isSelectable)e.currentTarget.style.background=C.bgCardHov;}}
                        onMouseLeave={e=>{if(!isSel)e.currentTarget.style.background=i%2===0?C.bgCard:C.bg;}}>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`}}>
                          {isSelectable&&<div style={{width:16,height:16,borderRadius:3,border:`2px solid ${isSel?C.green:C.borderMid}`,background:isSel?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>{isSel&&<span style={{color:"#0A1219",fontSize:10}}>✓</span>}</div>}
                        </td>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`,color:C.inkMid,whiteSpace:"nowrap"}}>{fmtF(m.fecha)}</td>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.ink}}>{m.descripcion}</td>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`,fontFamily:"monospace",fontSize:10,color:C.inkDim}}>{m.id}</td>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`,fontWeight:700,color:m.valor>=0?C.success:C.danger,textAlign:"right",whiteSpace:"nowrap"}}>{m.valor>=0?"+":""}{fmt(m.valor)}</td>
                        <td style={{padding:"9px 12px",borderBottom:`1px solid ${C.border}`}}>
                          {m.tipo==="entrada"&&<Badge color={C.success} bg={C.successBg} border={C.success+"33"}>Entrada</Badge>}
                          {m.tipo==="salida" &&<Badge color={C.danger}  bg={C.dangerBg}  border={C.danger+"33"}>Salida</Badge>}
                          {m.tipo==="revision"&&<Badge color={C.warning} bg={C.warningBg} border={C.warning+"33"}>Revisar</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Barra acción */}
          <div style={{position:"sticky",bottom:12,background:C.bgCard,border:`1px solid ${selCnt>0?C.green+"44":C.border}`,borderRadius:10,padding:"12px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 8px 32px rgba(0,0,0,0.4)",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{selCnt>0?`${selCnt} movimiento${selCnt>1?"s":""} seleccionado${selCnt>1?"s":""}`:"Sin selección"}</div>
              {selCnt>0&&<div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Total: <strong style={{color:C.green}}>{fmt(totalSel)}</strong> · Factura {clienteSel?.tipo==="Monotributista"?"C":"B"} · Consumidor Final · Backend REAL 🔥</div>}
            </div>
            <Btn onClick={()=>selCnt>0&&setModalConf(true)} disabled={selCnt===0} variant="green" style={{fontSize:13,padding:"9px 20px"}}>
              ⚡ Emitir {selCnt||""} factura{selCnt!==1?"s":""} a ARCA
            </Btn>
          </div>
        </>
      )}

      {/* EMITIDAS */}
      {paso==="emitidas"&&(
        <div>
          <div style={{background:C.bgCard,border:`1px solid ${C.success+"44"}`,borderRadius:10,padding:"28px",textAlign:"center",marginBottom:16}}>
            <div style={{fontSize:44,marginBottom:12}}>✅</div>
            <div style={{fontSize:20,fontWeight:700,color:C.success,marginBottom:4}}>{facturasOk.length} facturas emitidas</div>
            <div style={{fontSize:12,color:C.inkDim}}>{clienteSel?.nombre} · {fmt(facturasOk.reduce((s,f)=>s+f.monto,0))} facturado</div>
          </div>
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden",marginBottom:14}}>
            <div style={{background:C.bg,padding:"10px 16px",display:"flex",justifyContent:"space-between",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:"0.07em",textTransform:"uppercase"}}>Facturas con CAE</span>
              <span style={{fontSize:11,color:C.green}}>{facturasOk.length} aprobadas</span>
            </div>
            {facturasOk.map((f,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:i%2===0?C.bgCard:C.bg}}>
                <div>
                  <div style={{fontWeight:500,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:220}}>{f.descripcion}</div>
                  <div style={{fontSize:9,fontFamily:"monospace",color:C.success,marginTop:2}}>CAE {f.cae?.slice(0,10)}...</div>
                </div>
                <span style={{fontWeight:700,color:C.success}}>{fmt(f.monto)}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="success" style={{flex:1,justifyContent:"center"}}>📥 Descargar</Btn>
            <Btn variant="ghost" style={{flex:1,justifyContent:"center"}}>📲 Enviar al cliente</Btn>
            <Btn onClick={reiniciar} variant="ghost" style={{flex:1,justifyContent:"center"}}>+ Nuevo PDF</Btn>
          </div>
        </div>
      )}

      {modalConf&&<ModalEnvioMasivo movimientos={movsParaFact} cliente={clienteSel} onClose={()=>setModalConf(false)} onConfirmar={onEmitir}/>}
    </div>
  );
};

// ─── MODAL ENVÍO MASIVO ───────────────────────────────────────────────────────
const ModalEnvioMasivo = ({movimientos, cliente, onClose, onConfirmar}) => {
  const total    = movimientos.reduce((s,m)=>s+m.valor,0);
  const tipoFact = cliente?.tipo==="Monotributista"?"C":"B";
  const [paso, setPaso]         = useState("config");
  const [concepto, setConcepto] = useState("2");
  const [estadoF, setEstadoF]   = useState([]);
  const [factActual, setFactActual] = useState(0);

  // Detecta qué movimientos van a salir con la fecha AJUSTADA (más viejos
  // que el límite que permite ARCA para el concepto elegido), para
  // avisarle al contador ANTES de emitir, en vez de que se entere después.
  const limiteDias = parseInt(concepto) === 1 ? 5 : 10;
  const hoyParaCalculo = new Date();
  const fechaLimiteCalculo = new Date(hoyParaCalculo);
  fechaLimiteCalculo.setDate(fechaLimiteCalculo.getDate() - limiteDias);
  const fechaLimiteStr = fechaLimiteCalculo.toISOString().split("T")[0];

  const movsFueraDeRango = movimientos.filter(m => m.fecha < fechaLimiteStr);

  // ── CASO ESPECIAL: fechas anteriores al último comprobante emitido ────
  // ARCA exige orden cronológico dentro del punto de venta. Si ya se emitió
  // algo con fecha posterior, esas facturas se rechazan. Se le avisa al
  // contador ANTES y él decide emitirlas con fecha de hoy.
  const [ultimaEmitida, setUltimaEmitida] = useState(null);
  const [usarFechaHoy, setUsarFechaHoy]   = useState(false);

  useEffect(()=>{
    if (!cliente?.id) return;
    authFetch(`${API_URL}/clientes/${cliente.id}/ultima-fecha`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setUltimaEmitida(d.data?.ultima || null); })
      .catch(()=>{});
  },[cliente?.id]);

  const ultimaStr = ultimaEmitida ? String(ultimaEmitida).slice(0,10) : null;
  const movsAnteriores = ultimaStr ? movimientos.filter(m => m.fecha < ultimaStr) : [];
  const hayConflictoFecha = movsAnteriores.length > 0;

  // Cuando se detecta el conflicto, la opción viene marcada: es la única
  // forma de que ARCA acepte el lote.
  useEffect(()=>{
    if (hayConflictoFecha) setUsarFechaHoy(true);
  },[hayConflictoFecha]);

  const hoyStrFact = (()=>{ const h=new Date();
    return `${h.getFullYear()}-${String(h.getMonth()+1).padStart(2,"0")}-${String(h.getDate()).padStart(2,"0")}`; })();

  const iniciar = async () => {
    const init = movimientos.map((m,i) => ({idx:i,id:`${tipoFact}-${String(1000+i+1).padStart(4,"0")}`,fecha:m.fecha,monto:m.valor,descripcion:m.descripcion,cae:null,estado:"pendiente",error:null}));
    setEstadoF(init); setPaso("enviando");
    for(let idx=0; idx<init.length; idx++){
      const mov=movimientos[idx];
      setFactActual(idx);
      setEstadoF(prev=>prev.map((f,i)=>i===idx?{...f,estado:"enviando"}:f));
      try {
        const resp = await authFetch(`${API_URL}/arca/factura`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tipoComprobante:tipoFact==="C"?11:6,concepto:parseInt(concepto),monto:mov.valor,
          // Sólo se reemplaza la fecha si el contador tildó la opción
          fechaMovimiento: (hayConflictoFecha && usarFechaHoy) ? hoyStrFact : mov.fecha,
          fechaMovimientoOriginal: (hayConflictoFecha && usarFechaHoy) ? mov.fecha : null,
          clienteId:cliente.id,descripcion:mov.descripcion,idMovimientoOrigen:mov.id})});
        const data = await resp.json();
        if(data.ok){setEstadoF(prev=>prev.map((f,i)=>i===idx?{...f,estado:"aprobada",cae:data.data.cae,error:null}:f));}
        else{setEstadoF(prev=>prev.map((f,i)=>i===idx?{...f,estado:"rechazada",cae:null,error:data.error}:f));}
      } catch {
        setEstadoF(prev=>prev.map((f,i)=>i===idx?{...f,estado:"rechazada",cae:null,error:"Error de conexión"}:f));
      }
      await new Promise(r=>setTimeout(r,300));
    }
    setTimeout(()=>setPaso("resultado"),400);
  };

  const aprobadas    = estadoF.filter(f=>f.estado==="aprobada").length;
  const rechazadas   = estadoF.filter(f=>f.estado==="rechazada").length;
  const reintentando = estadoF.filter(f=>f.estado==="reintentando").length;
  const pct          = estadoF.length>0?Math.round((estadoF.filter(f=>["aprobada","rechazada"].includes(f.estado)).length/estadoF.length)*100):0;

  return (
    <Modal onClose={paso==="config"?onClose:undefined} maxW={540}>
      {paso==="config"&&(
        <>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:18}}>
            <div>
              <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Configurar emisión</div>
              <div style={{fontSize:16,fontWeight:700,color:C.ink}}>{movimientos.length} facturas · Factura {tipoFact}</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{cliente?.nombre} · Consumidor Final · Sin CUIT requerido</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim}}>×</button>
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:8}}>Concepto</div>
            <div style={{display:"flex",gap:6}}>
              {[["1","Productos"],["2","Servicios"],["3","Ambos"]].map(([v,l])=>(
                <div key={v} onClick={()=>setConcepto(v)} style={{flex:1,padding:"9px",border:`1px solid ${concepto===v?C.green:C.border}`,borderRadius:6,cursor:"pointer",background:concepto===v?C.greenDim:"transparent",textAlign:"center",transition:"all 0.15s"}}>
                  <div style={{fontSize:12,fontWeight:concepto===v?700:400,color:concepto===v?C.green:C.ink}}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:14}}>
            <div style={{background:C.bg,padding:"9px 14px",display:"flex",justifyContent:"space-between",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:600,color:C.inkMid}}>{movimientos.length} facturas a emitir</span>
              <span style={{fontSize:15,fontWeight:700,color:C.green}}>{fmt(total)}</span>
            </div>
            <div style={{maxHeight:160,overflowY:"auto"}}>
              {movimientos.map((m,i)=>(
                <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 14px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:i%2===0?C.bgCard:C.bg}}>
                  <span style={{color:C.inkDim,flexShrink:0,fontSize:10}}>{fmtF(m.fecha)}</span>
                  <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,color:C.ink}}>{m.descripcion}</div>
                  <span style={{fontWeight:700,color:C.success,marginLeft:10,flexShrink:0}}>{fmt(m.valor)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Aviso de fecha ajustada — antes de emitir, no después */}
          {/* CASO ESPECIAL — hay facturas con fecha anterior a la última emitida.
              ARCA las rechaza por orden cronológico, así que se le explica el
              motivo y se le ofrece emitirlas con fecha de hoy. */}
          {hayConflictoFecha && (
            <div style={{background:"rgba(254,188,46,0.1)",border:`2px solid ${C.warning}`,borderRadius:9,padding:"14px 16px",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:800,color:C.warning,marginBottom:6}}>
                ⚠️ Caso especial — fechas anteriores a la última factura
              </div>
              <div style={{fontSize:11.5,color:C.inkMid,lineHeight:1.65,marginBottom:10}}>
                El último comprobante de {cliente?.nombre} se emitió el <strong style={{color:C.ink}}>{fmtF(ultimaStr)}</strong>, y
                {movsAnteriores.length===1 ? " hay 1 factura con fecha anterior" : ` hay ${movsAnteriores.length} facturas con fecha anterior`}.
                ARCA exige que los comprobantes se emitan en orden cronológico, así que <strong style={{color:C.ink}}>las va a rechazar</strong>.
              </div>

              <label style={{display:"flex",alignItems:"flex-start",gap:9,cursor:"pointer",background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:7,padding:"11px 13px"}}>
                <input type="checkbox" checked={usarFechaHoy}
                  onChange={e=>setUsarFechaHoy(e.target.checked)}
                  style={{marginTop:2,width:15,height:15,accentColor:C.green,cursor:"pointer"}}/>
                <div>
                  <div style={{fontSize:12.5,fontWeight:700,color:C.ink,marginBottom:2}}>
                    Emitir todas con fecha de hoy ({fmtF(hoyStrFact)})
                  </div>
                  <div style={{fontSize:10.5,color:C.inkDim,lineHeight:1.6}}>
                    Se guarda la fecha original de cada cobro para que puedas rastrearlas después.
                  </div>
                </div>
              </label>

              {!usarFechaHoy && (
                <div style={{fontSize:11,color:C.danger,marginTop:9,fontWeight:600}}>
                  Sin tildar esta opción, ARCA va a rechazar esas facturas.
                </div>
              )}
            </div>
          )}

          {movsFueraDeRango.length > 0 && (
            <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"10px 12px",marginBottom:10,fontSize:11,color:C.danger,lineHeight:1.6}}>
              ⚠ {movsFueraDeRango.length} {movsFueraDeRango.length===1?"factura tiene":"facturas tienen"} más de {limiteDias} días de atraso (límite de ARCA para {concepto==="1"?"Productos":"Servicios"}).
              Se van a emitir con fecha <strong>{fmtF(fechaLimiteStr)}</strong> (el límite más antiguo que ARCA permite), en vez de la fecha real del movimiento.
            </div>
          )}

          <div style={{background:C.warningBg,border:`1px solid ${C.warning+"44"}`,borderRadius:6,padding:"8px 12px",marginBottom:14,fontSize:11,color:C.warning}}>
            ⚠ Una vez enviadas a ARCA no se pueden modificar.
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={onClose} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cancelar</Btn>
            <Btn onClick={iniciar} variant="green" style={{flex:2,justifyContent:"center"}}>⚡ Emitir {movimientos.length} facturas a ARCA</Btn>
          </div>
        </>
      )}

      {(paso==="enviando"||paso==="resultado")&&(
        <>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>{paso==="enviando"?"Enviando a ARCA":"Resultado"}</div>
            <div style={{fontSize:16,fontWeight:700,color:C.ink}}>{cliente?.nombre} · Factura {tipoFact}</div>
          </div>

          {paso==="enviando"&&(
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.inkMid,marginBottom:4}}><span>Factura {Math.min(factActual+1,movimientos.length)} de {movimientos.length}</span><span style={{color:C.green}}>{pct}%</span></div>
              <div style={{height:3,background:C.bg,borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",background:C.green,borderRadius:2,width:`${pct}%`,transition:"width 0.4s"}}/></div>
            </div>
          )}

          {paso==="resultado"&&(
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[[`✅ ${aprobadas}`,"Aprobadas",C.success,C.successBg],[`❌ ${rechazadas}`,"Rechazadas",rechazadas>0?C.danger:C.success,rechazadas>0?C.dangerBg:C.successBg],[`⏳ ${reintentando}`,"Sin resp.",reintentando>0?C.warning:C.success,reintentando>0?C.warningBg:C.successBg]].map(([v,l,c,bg])=>(
                <div key={l} style={{flex:1,background:bg,border:`1px solid ${c}44`,borderRadius:8,padding:"10px",textAlign:"center"}}>
                  <div style={{fontSize:17,fontWeight:700,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:c,fontWeight:600,marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:12}}>
            <div style={{background:C.bg,padding:"7px 12px",display:"flex",justifyContent:"space-between",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:10,fontWeight:700,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase"}}>Estado por factura</span>
              <span style={{fontSize:10,color:C.inkDim}}>{aprobadas+rechazadas+reintentando}/{estadoF.length}</span>
            </div>
            <div style={{maxHeight:280,overflowY:"auto"}}>
              {estadoF.map((f,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:i%2===0?C.bgCard:C.bg}}>
                  <div style={{width:18,height:18,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {f.estado==="pendiente"   &&<div style={{width:6,height:6,borderRadius:"50%",background:C.borderMid}}/>}
                    {f.estado==="enviando"    &&<div style={{width:14,height:14,border:`2px solid ${C.green}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.6s linear infinite"}}/>}
                    {f.estado==="aprobada"    &&<span style={{fontSize:12}}>✅</span>}
                    {f.estado==="rechazada"   &&<span style={{fontSize:12}}>❌</span>}
                    {f.estado==="reintentando"&&<span style={{fontSize:12}}>⏳</span>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:500,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.descripcion}</div>
                    <div style={{fontSize:9,color:C.inkDim,marginTop:1}}>
                      {f.estado==="aprobada"&&f.cae&&<span style={{fontFamily:"monospace",color:C.success}}>CAE {f.cae.slice(0,10)}...</span>}
                      {f.estado==="rechazada"&&<span style={{color:C.danger}}>{f.error}</span>}
                      {f.estado==="reintentando"&&<span style={{color:C.warning}}>Reintentando...</span>}
                    </div>
                  </div>
                  <span style={{fontWeight:700,flexShrink:0,color:f.estado==="aprobada"?C.success:f.estado==="rechazada"?C.danger:C.ink}}>{fmt(f.monto)}</span>
                </div>
              ))}
            </div>
          </div>

          {paso==="resultado"&&(
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={()=>onConfirmar(estadoF.filter(f=>f.estado==="aprobada"))} variant="success" style={{flex:2,justifyContent:"center"}}>Ver {aprobadas} aprobadas →</Btn>
              <Btn onClick={onClose} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cerrar</Btn>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

// ─── APP PRINCIPAL ─────────────────────────────────────────────────────────────
// ─── CONFIGURACIÓN ARCA — onboarding de certificado por cliente ─────────────
const ConfiguracionARCA = ({cliente, certEstado, cargandoCert, onActualizar, showToast}) => {
  const [csrTexto, setCsrTexto]   = useState(null);
  const [generando, setGenerando] = useState(false);
  const [certPem, setCertPem]     = useState("");
  const [subiendoCert, setSubiendoCert] = useState(false);
  const [puntoVentaInput, setPuntoVentaInput] = useState("");
  const [guardandoPV, setGuardandoPV] = useState(false);
  const [errorPaso, setErrorPaso] = useState(null);

  const estado = certEstado?.estado_certificado || "sin_iniciar";

  const PASOS = [
    {key:"sin_iniciar",        label:"Sin iniciar"},
    {key:"csr_generado",       label:"CSR generado"},
    {key:"certificado_subido", label:"Certificado subido"},
    {key:"listo",              label:"Listo para facturar"},
  ];
  const pasoActualIdx = PASOS.findIndex(p=>p.key===estado);

  const generarCSR = async () => {
    setGenerando(true); setErrorPaso(null);
    try {
      const resp = await authFetch(`${API_URL}/clientes/${cliente.id}/generar-csr`, { method:"POST" });
      const data = await resp.json();
      if(data.ok){
        setCsrTexto(data.data.csr);
        onActualizar();
        showToast("Certificado y clave generados correctamente");
      } else {
        setErrorPaso(data.error || "No se pudo generar el certificado");
      }
    } catch {
      setErrorPaso("Error de conexión con el servidor");
    } finally {
      setGenerando(false);
    }
  };

  const descargarCSR = () => {
    if(!csrTexto) return;
    const blob = new Blob([csrTexto], {type:"text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cliente.cuit.replace(/[^0-9]/g,"")}.csr`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const subirCertificado = async () => {
    if(!certPem.trim()) return setErrorPaso("Pegá el contenido del certificado (.crt) antes de continuar");
    setSubiendoCert(true); setErrorPaso(null);
    try {
      const resp = await authFetch(`${API_URL}/clientes/${cliente.id}/subir-certificado`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ certificadoPem: certPem })
      });
      const data = await resp.json();
      if(data.ok){
        onActualizar();
        showToast("Certificado guardado correctamente");
        setCertPem("");
      } else {
        setErrorPaso(data.error || "No se pudo guardar el certificado");
      }
    } catch {
      setErrorPaso("Error de conexión con el servidor");
    } finally {
      setSubiendoCert(false);
    }
  };

  const guardarPuntoVenta = async () => {
    const num = parseInt(puntoVentaInput);
    if(!num || num<=0) return setErrorPaso("Ingresá un número de Punto de Venta válido");
    setGuardandoPV(true); setErrorPaso(null);
    try {
      const resp = await authFetch(`${API_URL}/clientes/${cliente.id}/punto-venta`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ puntoVenta: num })
      });
      const data = await resp.json();
      if(data.ok){
        onActualizar();
        showToast(`¡${cliente.nombre.split(" ")[0]} ya puede facturar!`);
      } else {
        setErrorPaso(data.error || "No se pudo guardar el punto de venta");
      }
    } catch {
      setErrorPaso("Error de conexión con el servidor");
    } finally {
      setGuardandoPV(false);
    }
  };

  if(cargandoCert){
    return (
      <div style={{textAlign:"center",padding:"60px",color:C.inkDim}}>
        <div style={{width:28,height:28,border:`2px solid ${C.border}`,borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
        Cargando estado...
      </div>
    );
  }

  return (
    <div>
      {/* Intro */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",marginBottom:18}}>
        <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>Habilitación para facturar</div>
        <div style={{fontSize:11,color:C.inkDim,lineHeight:1.7}}>
          ARCA exige que cada cliente tenga su propio certificado digital y Punto de Venta tipo Webservices antes de poder emitir facturas a través de Venci. Este proceso se hace una sola vez por cliente.
        </div>
      </div>

      {/* Stepper de progreso */}
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {PASOS.map((p,i)=>(
          <div key={p.key} style={{flex:1}}>
            <div style={{height:4,borderRadius:2,background:i<=pasoActualIdx?C.green:C.border,marginBottom:6,transition:"background 0.3s"}}/>
            <div style={{fontSize:9,color:i<=pasoActualIdx?C.green:C.inkDim,fontWeight:i===pasoActualIdx?700:400,textAlign:"center"}}>{p.label}</div>
          </div>
        ))}
      </div>

      {errorPaso&&(
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"10px 14px",marginBottom:16,fontSize:11,color:C.danger}}>
          ⚠ {errorPaso}
        </div>
      )}

      {/* PASO 1: Generar y descargar CSR */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <Badge color={estado!=="sin_iniciar"?C.success:C.navy} bg={estado!=="sin_iniciar"?C.successBg:C.greenDim} border={estado!=="sin_iniciar"?C.success+"44":C.navy+"44"}>Paso 1</Badge>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Generar certificado digital</span>
        </div>
        <div style={{fontSize:11,color:C.inkDim,lineHeight:1.7,marginBottom:10}}>
          Venci genera automáticamente un par único para {cliente.nombre.split(" ")[0]}: una <strong style={{color:C.ink}}>clave privada</strong> (queda guardada de forma segura en Venci, nunca se comparte) y un archivo <strong style={{color:C.ink}}>.csr</strong> (solicitud de certificado) que vas a descargar.
        </div>
        <div style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:10,color:C.green,lineHeight:1.7}}>
          💡 <strong>¿Por qué hace falta esto?</strong> ARCA exige que cada CUIT use su propio certificado digital para facturar — no se puede usar el certificado de Venci ni el de otro cliente. Es la firma digital única de {cliente.nombre.split(" ")[0]} ante ARCA.
        </div>
        {estado==="sin_iniciar"?(
          <Btn onClick={generarCSR} disabled={generando} variant="green">
            {generando?"Generando...":"🔑 Generar certificado"}
          </Btn>
        ):(
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Badge color={C.success} bg={C.successBg} border={C.success+"44"}>✓ Generado</Badge>
            {csrTexto&&<Btn onClick={descargarCSR} variant="ghost" small>📥 Descargar .csr de nuevo</Btn>}
          </div>
        )}
      </div>

      {/* PASO 2: Instrucciones manuales en ARCA */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",marginBottom:14,opacity:estado==="sin_iniciar"?0.4:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <Badge color={C.warning} bg={C.warningBg} border={C.warning+"44"}>Paso 2</Badge>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Subir el .csr a ARCA</span>
        </div>
        <div style={{fontSize:10,color:C.inkDim,marginBottom:10,fontStyle:"italic"}}>
          Este paso lo hace {cliente.nombre.split(" ")[0]} (o quien tenga su clave fiscal) — no se puede hacer desde Venci.
        </div>
        <ol style={{fontSize:11,color:C.inkMid,lineHeight:2.2,paddingLeft:18,margin:0,marginBottom:12}}>
          <li>Entrar a <strong style={{color:C.ink}}>arca.gob.ar</strong> con el CUIT y la clave fiscal de {cliente.nombre.split(" ")[0]}</li>
          <li>En el buscador de servicios, escribir: <strong style={{color:C.ink}}>"Administración de Certificados Digitales"</strong> y entrar a ese servicio</li>
          <li>Buscar el botón <strong style={{color:C.ink}}>"Agregar Alias"</strong> (o "Nuevo Certificado")</li>
          <li>Ponerle un <strong style={{color:C.ink}}>nombre</strong> al alias — por ejemplo "Venci{cliente.nombre.split(" ")[0]}"</li>
          <li>Subir el archivo <strong style={{color:C.ink}}>.csr</strong> descargado en el Paso 1 (está en la carpeta de Descargas)</li>
          <li>Confirmar — ARCA procesa y genera el certificado, casi al instante</li>
        </ol>
        <div style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"10px 12px",fontSize:10,color:C.green,lineHeight:1.7}}>
          💡 <strong>¿Qué es el "alias"?</strong> Es solo un NOMBRE para identificar el certificado dentro de la cuenta de ARCA — como ponerle nombre a una carpeta. No tiene ningún significado técnico especial, solo sirve para reconocerlo si después hay varios certificados guardados.
        </div>
      </div>

      {/* PASO 3: Subir certificado de vuelta */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",marginBottom:14,opacity:estado==="sin_iniciar"?0.4:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <Badge color={estado==="certificado_subido"||estado==="listo"?C.success:C.navy} bg={estado==="certificado_subido"||estado==="listo"?C.successBg:C.greenDim} border={estado==="certificado_subido"||estado==="listo"?C.success+"44":C.navy+"44"}>Paso 3</Badge>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Descargar el certificado y subirlo a Venci</span>
        </div>
        <ol style={{fontSize:11,color:C.inkMid,lineHeight:2.2,paddingLeft:18,margin:0,marginBottom:12}}>
          <li>En la misma pantalla de ARCA, una vez confirmado el alias, va a aparecer en la lista de certificados</li>
          <li>Al lado, tocar <strong style={{color:C.ink}}>"Ver"</strong> o <strong style={{color:C.ink}}>"Descargar"</strong> — se descarga un archivo <strong style={{color:C.ink}}>.crt</strong></li>
          <li>Abrir ese archivo con el Bloc de Notas (clic derecho → Abrir con → Bloc de notas)</li>
          <li>Copiar TODO el contenido — desde <strong style={{color:C.ink}}>-----BEGIN CERTIFICATE-----</strong> hasta <strong style={{color:C.ink}}>-----END CERTIFICATE-----</strong></li>
          <li>Pegarlo en el cuadro de texto de abajo y tocar "Subir certificado"</li>
        </ol>
        {estado==="certificado_subido"||estado==="listo"?(
          <Badge color={C.success} bg={C.successBg} border={C.success+"44"}>✓ Certificado guardado</Badge>
        ):(
          <>
            <textarea value={certPem} onChange={e=>setCertPem(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              disabled={estado==="sin_iniciar"}
              style={{width:"100%",minHeight:100,padding:"10px 12px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,fontSize:10,fontFamily:"monospace",color:C.ink,outline:"none",resize:"vertical",marginBottom:10}}/>
            <Btn onClick={subirCertificado} disabled={subiendoCert||estado==="sin_iniciar"} variant="green">
              {subiendoCert?"Guardando...":"📤 Subir certificado"}
            </Btn>
          </>
        )}
      </div>

      {/* PASO 4: Vincular el certificado al servicio de Facturación Electrónica */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",marginBottom:14,opacity:estado==="sin_iniciar"?0.4:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <Badge color={C.warning} bg={C.warningBg} border={C.warning+"44"}>Paso 4</Badge>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Vincular el certificado al servicio</span>
        </div>
        <div style={{fontSize:10,color:C.inkDim,marginBottom:10,fontStyle:"italic"}}>
          Este paso es el que más se suele olvidar — sin él, el certificado existe pero ARCA no lo deja usar para facturar. Lo hace {cliente.nombre.split(" ")[0]}, en su cuenta de ARCA.
        </div>
        <ol style={{fontSize:11,color:C.inkMid,lineHeight:2.2,paddingLeft:18,margin:0,marginBottom:12}}>
          <li>Buscar el servicio: <strong style={{color:C.ink}}>"Administrador de Relaciones de Clave Fiscal"</strong></li>
          <li>Tocar <strong style={{color:C.ink}}>"Nueva Relación"</strong></li>
          <li>Buscar: <strong style={{color:C.ink}}>"ARCA"</strong></li>
          <li>Dentro de ARCA, ir a <strong style={{color:C.ink}}>"Webservices"</strong></li>
          <li>Buscar el servicio: <strong style={{color:C.ink}}>"Facturación Electrónica"</strong></li>
          <li>En el campo <strong style={{color:C.ink}}>"Representante"</strong>, tocar <strong style={{color:C.ink}}>"Buscar"</strong></li>
          <li>Ahí recién aparece un desplegable — elegir el certificado/alias generado en el Paso 2 (el que se llama "Venci{cliente.nombre.split(" ")[0]}" o como lo hayan puesto)</li>
          <li>Confirmar</li>
        </ol>
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"10px 12px",fontSize:10,color:C.danger,lineHeight:1.7}}>
          ⚠ <strong>Sin este paso</strong>, al intentar facturar va a aparecer el error "Computador no autorizado a acceder al servicio" — aunque el certificado se haya generado bien.
        </div>
      </div>

      {/* PASO 4: Punto de Venta */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",marginBottom:14,opacity:estado==="sin_iniciar"||estado==="csr_generado"?0.4:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <Badge color={estado==="listo"?C.success:C.navy} bg={estado==="listo"?C.successBg:C.greenDim} border={estado==="listo"?C.success+"44":C.navy+"44"}>Paso 5</Badge>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Crear el Punto de Venta tipo Webservices</span>
        </div>
        <div style={{fontSize:10,color:C.inkDim,marginBottom:10,fontStyle:"italic"}}>
          Este paso también lo hace {cliente.nombre.split(" ")[0]}, en su cuenta de ARCA.
        </div>
        <ol style={{fontSize:11,color:C.inkMid,lineHeight:2.2,paddingLeft:18,margin:0,marginBottom:12}}>
          <li>En ARCA, buscar el servicio: <strong style={{color:C.ink}}>"Administración de puntos de venta y domicilios"</strong></li>
          <li>Tocar <strong style={{color:C.ink}}>"Agregar"</strong></li>
          <li>En "Nombre de Fantasía", poner lo que prefiera</li>
          <li>En <strong style={{color:C.ink}}>"Sistema"</strong>, elegir exactamente <strong style={{color:C.ink}}>"Webservices"</strong></li>
          <li>Elegir el domicilio (suele aparecer automáticamente)</li>
          <li>Confirmar — ARCA asigna un número, por ejemplo "Punto de Venta N°4"</li>
        </ol>
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"10px 12px",marginBottom:10,fontSize:10,color:C.danger,lineHeight:1.7}}>
          ⚠ <strong>Importante:</strong> en "Sistema" tiene que decir exactamente <strong>"Webservices"</strong> — NO "Factura en Línea - Monotributo". Si elige el sistema equivocado, las facturas van a ser rechazadas por ARCA.
        </div>
        <div style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:10,color:C.green,lineHeight:1.7}}>
          💡 <strong>¿Por qué tiene que ser "Webservices" y no "Factura en Línea"?</strong> "Factura en Línea" es el sistema para que el contribuyente facture A MANO desde la web de ARCA. "Webservices" es el sistema para que programas externos (como Venci) facturen automáticamente, por API. Son dos sistemas separados, con numeración separada — el cliente puede tener ambos al mismo tiempo sin que se mezclen.
        </div>
        <div style={{fontSize:11,color:C.inkDim,marginBottom:10}}>
          Una vez creado, escribí ese número acá:
        </div>
        {estado==="listo"?(
          <Badge color={C.success} bg={C.successBg} border={C.success+"44"}>✓ Punto de Venta N°{certEstado.punto_venta} — Listo para facturar</Badge>
        ):(
          <div style={{display:"flex",gap:8}}>
            <input type="number" value={puntoVentaInput} onChange={e=>setPuntoVentaInput(e.target.value)}
              placeholder="Ej: 4" disabled={estado!=="certificado_subido"}
              style={{width:100,padding:"8px 12px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif"}}/>
            <Btn onClick={guardarPuntoVenta} disabled={guardandoPV||estado!=="certificado_subido"} variant="green">
              {guardandoPV?"Guardando...":"Guardar →"}
            </Btn>
          </div>
        )}
      </div>

      {estado==="listo"&&(
        <div style={{background:C.successBg,border:`1px solid ${C.success}44`,borderRadius:10,padding:"16px 20px",textAlign:"center"}}>
          <div style={{fontSize:24,marginBottom:6}}>✅</div>
          <div style={{fontSize:13,fontWeight:700,color:C.success}}>{cliente.nombre.split(" ")[0]} ya puede facturar a través de Venci</div>
        </div>
      )}
    </div>
  );
};

// ─── PÁGINA DETALLE CLIENTE ──────────────────────────────────────────────────
// ─── FACTURADOR MANUAL — para cargar una factura puntual sin pasar por ARCA ──
const FacturadorManual = ({cliente, tipoFact, onVolver, onEmitida, showToast}) => {
  const hoy = new Date().toISOString().split("T")[0];
  const [monto, setMonto]             = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fecha, setFecha]             = useState(hoy);
  const [concepto, setConcepto]       = useState(2); // 1=Productos 2=Servicios 3=Ambos
  const [categoria, setCategoria]     = useState("");
  const [emitiendo, setEmitiendo]     = useState(false);
  const [error, setError]             = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  const validarYConfirmar = () => {
    const montoNum = parseFloat(monto.replace(",","."));
    if(!montoNum || montoNum<=0) return setError("Ingresá un monto válido");
    if(!descripcion.trim()) return setError("Ingresá una descripción para la factura");
    setError(null);
    setConfirmando(true);
  };

  const emitir = async () => {
    const montoNum = parseFloat(monto.replace(",","."));

    setEmitiendo(true); setError(null);
    try {
      const tipoComprobante = tipoFact==="C"?11:6;
      const resp = await authFetch(`${API_URL}/arca/factura`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          tipoComprobante, concepto,
          monto: montoNum,
          fechaMovimiento: fecha,
          clienteId: cliente.id,
          descripcion: categoria ? `${descripcion.trim()} — ${categoria}` : descripcion.trim(),
        })
      });
      const data = await resp.json();
      if(data.ok){
        onEmitida(data.data);
      } else {
        setError(data.mensaje || data.error || "ARCA rechazó la factura");
      }
    } catch {
      setError("Error de conexión con el servidor");
    } finally {
      setEmitiendo(false);
    }
  };

  return (
    <div>
      <button onClick={onVolver}
        style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",color:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:16,padding:0}}>
        ← Volver
      </button>

      <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>Facturar manualmente</div>
      <div style={{fontSize:11,color:C.inkDim,marginBottom:18,lineHeight:1.6}}>
        Usá esto para cargar un cobro puntual que no vino de un PDF de Mercado Pago — por ejemplo, un pago en efectivo o una transferencia bancaria directa.
      </div>

      {error&&(
        <div style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"10px 14px",marginBottom:14,fontSize:11,color:C.danger}}>
          ⚠ {error}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <label style={{fontSize:10,fontWeight:600,color:C.inkMid,marginBottom:5,display:"block"}}>Monto *</label>
          <input value={monto} onChange={e=>setMonto(e.target.value)} placeholder="Ej: 15000"
            style={{width:"100%",padding:"10px 12px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif"}}/>
        </div>
        <div>
          <label style={{fontSize:10,fontWeight:600,color:C.inkMid,marginBottom:5,display:"block"}}>Fecha *</label>
          <input type="date" value={fecha} onChange={e=>setFecha(e.target.value)}
            style={{width:"100%",padding:"10px 12px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif"}}/>
        </div>
      </div>

      <div style={{marginBottom:12}}>
        <label style={{fontSize:10,fontWeight:600,color:C.inkMid,marginBottom:5,display:"block"}}>Descripción *</label>
        <input value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Ej: Honorarios por servicio de consultoría"
          style={{width:"100%",padding:"10px 12px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.ink,outline:"none",fontFamily:"'Inter',sans-serif"}}/>
      </div>

      <div style={{marginBottom:18}}>
        <label style={{fontSize:10,fontWeight:600,color:C.inkMid,marginBottom:5,display:"block"}}>Concepto</label>
        <div style={{display:"flex",gap:6}}>
          {[[1,"Productos"],[2,"Servicios"],[3,"Ambos"]].map(([v,l])=>(
            <button key={v} onClick={()=>setConcepto(v)}
              style={{flex:1,padding:"8px",borderRadius:6,border:`1px solid ${concepto===v?C.green:C.border}`,background:concepto===v?C.greenDim:"transparent",color:concepto===v?C.green:C.inkMid,fontSize:11,fontWeight:concepto===v?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div style={{marginBottom:18}}>
        <label style={{fontSize:10,fontWeight:600,color:C.inkMid,marginBottom:5,display:"block"}}>¿Qué se vendió? (opcional)</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {CATEGORIAS_PRODUCTO.map(cat=>(
            <button key={cat} onClick={()=>{
                const nueva = categoria===cat ? "" : cat;
                setCategoria(nueva);
                // Al elegir una categoría, se completa la descripción con ese
                // texto, igual que en el flujo de PDF. Queda editable por si
                // hay que agregarle algo. Al destildar, se limpia sólo si la
                // descripción era exactamente la categoría anterior — así no
                // se pisa nada que el usuario haya escrito a mano.
                if (nueva) setDescripcion(nueva);
                else if (descripcion === cat) setDescripcion("");
              }}
              style={{padding:"4px 9px",borderRadius:4,border:`1px solid ${categoria===cat?C.green:C.border}`,background:categoria===cat?C.greenDim:"transparent",color:categoria===cat?C.green:C.inkMid,fontSize:10,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      <Btn onClick={validarYConfirmar} disabled={emitiendo} variant="green">
        {emitiendo?"Emitiendo a ARCA...":`⚡ Emitir Factura ${tipoFact} a ARCA`}
      </Btn>

      {/* Vista previa — confirmación antes de emitir, con lo más importante:
          monto, fecha real y qué se vendió */}
      {confirmando&&(
        <Modal onClose={()=>setConfirmando(false)} maxW={400}>
          <div style={{textAlign:"center",marginBottom:4}}>
            <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Confirmar emisión</div>
            <div style={{fontSize:15,fontWeight:700,color:C.ink}}>Factura {tipoFact} · {cliente.nombre.split(" ")[0]}</div>
          </div>

          <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 20px",margin:"18px 0"}}>
            <div style={{fontSize:28,fontWeight:800,color:C.green,textAlign:"center",marginBottom:14}}>
              {fmt(parseFloat(monto.replace(",",".")))}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:`1px solid ${C.border}`,fontSize:12}}>
              <span style={{color:C.inkDim}}>Fecha</span>
              <span style={{color:C.ink,fontWeight:600}}>{fmtF(fecha)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:`1px solid ${C.border}`,fontSize:12}}>
              <span style={{color:C.inkDim}}>Qué se vendió</span>
              <span style={{color:C.ink,fontWeight:600,textAlign:"right",maxWidth:200}}>{categoria || descripcion}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:`1px solid ${C.border}`,fontSize:12}}>
              <span style={{color:C.inkDim}}>Concepto</span>
              <span style={{color:C.ink,fontWeight:600}}>{concepto===1?"Productos":concepto===2?"Servicios":"Ambos"}</span>
            </div>
          </div>

          <div style={{display:"flex",gap:8}}>
            <Btn onClick={()=>setConfirmando(false)} variant="ghost" style={{flex:1,justifyContent:"center"}}>Volver</Btn>
            <Btn onClick={()=>{setConfirmando(false);emitir();}} disabled={emitiendo} variant="green" style={{flex:1,justifyContent:"center"}}>
              {emitiendo?"Emitiendo...":"Confirmar y emitir"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── GUÍA DE AYUDA — cómo emitir una factura correctamente ──────────────────
const PASOS_GUIA = [
  {
    n: 1, titulo: "Subí el PDF de Mercado Pago",
    desc: "En la tab \"Facturación MP\", tocá \"Seleccionar PDF\" y elegí el resumen de cuenta descargado de Mercado Pago.",
  },
  {
    n: 2, titulo: "Esperá a que se clasifique",
    desc: "El sistema separa automáticamente los cobros (entradas) de los pagos (salidas) — solo los cobros se pueden facturar.",
  },
  {
    n: 3, titulo: "Tildá los movimientos a facturar",
    desc: "Marcá el checkbox de cada cobro que querés incluir, o usá \"☑ Todos los cobros\" para seleccionarlos todos de una vez.",
  },
  {
    n: 4, titulo: "Elegí qué se vendió",
    desc: "Tocá la flechita \"▼\" al final de cada fila y elegí la categoría correspondiente (Prendas de vestir, Servicios gastronómicos, etc.) — así la factura queda bien descripta.",
  },
  {
    n: 5, titulo: "Emití a ARCA",
    desc: "Tocá \"⚡ Emitir facturas a ARCA\", confirmá el resumen, y listo — la factura queda aprobada con su CAE real en segundos.",
  },
];

const ModalAyudaEmitir = ({onClose}) => {
  const [pasoIdx, setPasoIdx] = useState(0);
  const paso = PASOS_GUIA[pasoIdx];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(5,10,15,0.85)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}} onClick={onClose}>
      <div style={{background:C.bgCard,borderRadius:16,padding:0,width:"100%",maxWidth:480,overflow:"hidden",border:`1px solid ${C.border}`,boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}} onClick={e=>e.stopPropagation()}>

        {/* Header con la diagonal de marca */}
        <div style={{background:"#0F2A4D",position:"relative",padding:"28px 28px 24px",overflow:"hidden"}}>
          <svg style={{position:"absolute",top:0,right:-2,height:"100%",width:60}} viewBox="0 0 60 200" preserveAspectRatio="none">
            <polygon points="26,0 60,0 60,200 0,200" fill="#3DAA8A"/>
            <polygon points="13,0 40,0 40,200 0,200" fill="#0F2A4D"/>
          </svg>
          <button onClick={onClose} style={{position:"absolute",top:18,right:18,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:6,width:28,height:28,color:"#fff",cursor:"pointer",fontSize:16,zIndex:2}}>×</button>
          <div style={{position:"relative",zIndex:1}}>
            <div style={{fontSize:11,fontWeight:700,color:"#3DAA8A",letterSpacing:"0.1em",marginBottom:6}}>CÓMO EMITIR · {paso.n} DE {PASOS_GUIA.length}</div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Poppins',sans-serif"}}>{paso.titulo}</div>
          </div>
        </div>

        {/* Cuerpo */}
        <div style={{padding:"24px 28px"}}>
          <p style={{fontSize:13.5,color:C.inkMid,lineHeight:1.7,marginBottom:24}}>{paso.desc}</p>

          {/* Indicador de pasos */}
          <div style={{display:"flex",gap:6,marginBottom:20}}>
            {PASOS_GUIA.map((_,i)=>(
              <div key={i} onClick={()=>setPasoIdx(i)} style={{height:4,flex:1,borderRadius:2,background:i<=pasoIdx?C.green:C.border,cursor:"pointer",transition:"background 0.2s"}}/>
            ))}
          </div>

          <div style={{display:"flex",gap:8,justifyContent:"space-between"}}>
            <Btn onClick={()=>setPasoIdx(p=>Math.max(0,p-1))} disabled={pasoIdx===0} variant="ghost" small>← Anterior</Btn>
            {pasoIdx < PASOS_GUIA.length-1 ? (
              <Btn onClick={()=>setPasoIdx(p=>p+1)} variant="green" small>Siguiente →</Btn>
            ) : (
              <Btn onClick={onClose} variant="green" small>Entendido ✓</Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PaginaCliente = ({cliente, obligaciones, setObligaciones, onVolver, showToast}) => {
  const [tab, setTab]           = useState("facturacion");
  const [suscripcion, setSuscripcion] = useState(null); // ciclo de ESTE cliente
  const [pagandoSus, setPagandoSus]   = useState(false);

  // ── FACTURACIÓN NOMINADA DESDE EXCEL ──────────────────────────────────
  // Módulo aparte: factura a nombre del paciente con su DNI. No comparte
  // nada con el flujo de los resúmenes en PDF.
  const MOTIVOS_NOMINADA = ["Sesiones de psicología"];

  // ── Ficha del cliente: datos de contacto y fiscales, editables en el lugar ──
  // Guarda lo último que se grabó para no depender de recargar la página.
  // Mientras no se edite nada, vale lo que vino en el prop `cliente`.
  const [ficha, setFicha]             = useState(null);
  const [editandoFicha, setEditFicha] = useState(false);
  const [guardandoFicha, setGuardFicha] = useState(false);
  const [borradorFicha, setBorrador]  = useState({});
  const fichaActual = { ...(cliente || {}), ...(ficha || {}) };

  const abrirEdicionFicha = () => {
    setBorrador({
      email:             fichaActual.email || "",
      telefono:          fichaActual.telefono || "",
      domicilio:         fichaActual.domicilio || "",
      ingresosBrutos:    fichaActual.ingresos_brutos || "",
      inicioActividades: fichaActual.inicio_actividades
        ? String(fichaActual.inicio_actividades).slice(0,10) : "",
    });
    setEditFicha(true);
  };

  const guardarFicha = async () => {
    setGuardFicha(true);
    try {
      const r = await authFetch(`${API_URL}/clientes/${cliente.id}/ficha`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify(borradorFicha),
      });
      const d = await r.json();
      if (d.ok) {
        setFicha(d.data);
        setEditFicha(false);
        showToast("✓ Datos del cliente actualizados");
      } else {
        showToast("❌ " + (d.error || "No se pudo guardar"));
      }
    } catch(e) {
      showToast("❌ Error de conexión: " + e.message);
    } finally {
      setGuardFicha(false);
    }
  };

  const [excelNom, setExcelNom]         = useState(null);
  const [leyendoNom, setLeyendoNom]     = useState(false);
  const [datosNom, setDatosNom]         = useState(null);   // respuesta del lector
  const [filasNom, setFilasNom]         = useState([]);     // filas editables
  const [selNom, setSelNom]             = useState(new Set());
  const [motivoNom, setMotivoNom]       = useState(MOTIVOS_NOMINADA[0]);
  const [loteDesde, setLoteDesde]       = useState("");
  const [loteHasta, setLoteHasta]       = useState("");
  const [emitiendoNom, setEmitiendoNom] = useState(false);
  const [progresoNom, setProgresoNom]   = useState(null);

  const hoyNom = (()=>{ const h=new Date();
    return `${h.getFullYear()}-${String(h.getMonth()+1).padStart(2,"0")}-${String(h.getDate()).padStart(2,"0")}`; })();

  // La descripción se arma sola con el motivo y el período, igual que en el
  // comprobante de ejemplo: "SESIONES DE PSICOLOGIA CORRESPONDIENTES A SEPTIEMBRE 2026."
  const descripcionNom = (motivo, etiqueta) =>
    `${(motivo||"").toUpperCase()} CORRESPONDIENTES A ${(etiqueta||"").toUpperCase()}.`;

  // ── Vista previa: cómo va a salir la factura ANTES de emitirla ─────────
  const [previaNom, setPreviaNom]   = useState(null);   // la fila que se mira
  const [confirmarNom, setConfirmarNom] = useState(false); // confirmación del lote
  const [emisorNom, setEmisorNom]   = useState(null);   // datos del emisor
  // Envío por mail al cliente: las facturas ya emitidas se le mandan a ÉL
  // con los PDF adjuntos, y él se las reenvía a cada paciente.
  const [enviandoMailNom, setEnviandoMailNom] = useState(false);
  // Las facturas del Historial tildadas para mandar por mail.
  const [selMail, setSelMail] = useState(new Set());

  useEffect(()=>{
    if (!cliente?.id) return;
    authFetch(`${API_URL}/clientes/${cliente.id}/datos-emisor`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setEmisorNom(d.data); })
      .catch(()=>{});
  },[cliente?.id]);

  // Datos del emisor que pide el comprobante y pueden estar sin cargar
  const faltanDatosEmisor = emisorNom
    ? [
        !emisorNom.domicilio          && "Domicilio comercial",
        !emisorNom.ingresos_brutos    && "Ingresos Brutos",
        !emisorNom.inicio_actividades && "Fecha de inicio de actividades",
      ].filter(Boolean)
    : [];

  const fmtFechaCorta = (iso) => {
    if (!iso) return "";
    const [a,m,d] = String(iso).slice(0,10).split("-");
    return `${d}/${m}/${a}`;
  };

  // ── Carga a mano: mismas facturas, tipeadas en vez de leídas del Excel ──
  const [modoNom, setModoNom]       = useState(null);   // null | "excel" | "manual"
  const [filasManual, setFilasManual] = useState([
    { id: 1, nombre: "", dni: "", sesiones: "1", unitario: "" },
  ]);

  const MESES_NOM = ["","ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO",
                     "AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"];

  // Etiqueta del período para la descripción, sin usar new Date(): el texto
  // se arma del propio string AAAA-MM-DD.
  const etiquetaDePeriodo = (desde, hasta) => {
    if (!desde || !hasta) return "";
    const [ad, md] = desde.split("-").map(Number);
    const [ah, mh] = hasta.split("-").map(Number);
    return (ad === ah && md === mh)
      ? `${MESES_NOM[md]} ${ad}`
      : `${MESES_NOM[md]} A ${MESES_NOM[mh]} ${ah}`;
  };

  // Mes en curso, para que la carga a mano arranque con un período puesto
  const mesEnCurso = (() => {
    const h = new Date();
    const a = h.getFullYear(), m = h.getMonth() + 1;
    const ultimo = new Date(a, m, 0).getDate();
    return {
      desde: `${a}-${String(m).padStart(2,"0")}-01`,
      hasta: `${a}-${String(m).padStart(2,"0")}-${ultimo}`,
    };
  })();

  const agregarFilaManual = () =>
    setFilasManual(prev => [...prev,
      { id: Math.max(0, ...prev.map(f=>f.id)) + 1, nombre:"", dni:"", sesiones:"1", unitario:"" }]);

  const borrarFilaManual = (id) =>
    setFilasManual(prev => prev.length > 1 ? prev.filter(f => f.id !== id) : prev);

  const editarManual = (id, campo, valor) =>
    setFilasManual(prev => prev.map(f => f.id === id ? { ...f, [campo]: valor } : f));

  const totalFilaManual = (f) =>
    (parseFloat(String(f.unitario).replace(",", ".")) || 0) * (parseInt(f.sesiones, 10) || 0);

  // Convierte lo tipeado al MISMO formato que devuelve el lector de Excel,
  // así de acá en adelante la pantalla de revisión y la emisión son idénticas.
  const confirmarCargaManual = () => {
    const validas = filasManual.filter(f =>
      f.nombre.trim() && String(f.dni).replace(/\D/g,"") && totalFilaManual(f) > 0
    );
    if (!validas.length) {
      showToast("⚠ Cargá al menos una fila con nombre, DNI e importe");
      return;
    }

    const dnis = validas.map(f => String(f.dni).replace(/\D/g,""));
    const repetidos = dnis.filter((d,i) => dnis.indexOf(d) !== i);
    if (repetidos.length) {
      showToast(`⚠ El DNI ${repetidos[0]} está cargado dos veces — juntalo en una sola fila`);
      return;
    }

    const facturas = validas.map(f => {
      const ses = parseInt(f.sesiones, 10) || 1;
      const uni = parseFloat(String(f.unitario).replace(",", ".")) || 0;
      return {
        dni: String(f.dni).replace(/\D/g,""),
        nombre: f.nombre.trim(),
        cantidad: ses,
        cantidad_factura: ses,
        unitario: uni,
        total: Math.round(uni * ses * 100) / 100,
        fechas: [],
        enviar: false,
        medio_pago: "",
        periodo_desde: mesEnCurso.desde,
        periodo_hasta: mesEnCurso.hasta,
        // Misma clave que usa el Excel: DNI + mes del período. Así tipear dos
        // veces lo mismo tampoco duplica.
        idOrigen: `EXCEL-${String(f.dni).replace(/\D/g,"")}-${mesEnCurso.desde.slice(0,7).replace("-","")}`,
        yaFacturada: false,
        diferencia: Math.round(uni * ses * 100) / 100,
      };
    });

    setDatosNom({
      etiqueta_periodo: etiquetaDePeriodo(mesEnCurso.desde, mesEnCurso.hasta),
      facturas,
      avisos: [],
      resumen: {
        filas: facturas.reduce((s,f)=>s+f.cantidad, 0),
        facturas: facturas.length,
        total: facturas.reduce((s,f)=>s+f.total, 0),
        a_enviar: 0,
      },
      manual: true,
    });
    setFilasNom(facturas.map(f => ({ ...f, estado:"pendiente", cae:null, error:null })));
    setSelNom(new Set(facturas.map(f => f.dni)));
    setLoteDesde(mesEnCurso.desde);
    setLoteHasta(mesEnCurso.hasta);
  };

  const subirExcelNom = async (archivo) => {
    if (!archivo) return;
    setLeyendoNom(true); setDatosNom(null); setFilasNom([]); setSelNom(new Set());
    try {
      const fd = new FormData();
      fd.append("excel", archivo);
      fd.append("clienteId", cliente.id);
      const r = await authFetch(`${API_URL}/nominada/leer-excel`, { method:"POST", body: fd });
      const d = await r.json();
      if (!d.ok) {
        showToast("❌ " + (d.error||"No se pudo leer la planilla"));
        // Si el lector explicó fila por fila qué pasó, se muestra en pantalla:
        // un toast que se va solo no alcanza para corregir una planilla.
        if (d.descartadas?.length)
          setDatosNom({ facturas:[], avisos:[], descartadas:d.descartadas,
                        errorLectura:d.error, resumen:{filas:0,facturas:0,total:0} });
        setLeyendoNom(false);
        return;
      }

      const data = d.data;
      setDatosNom(data);
      setFilasNom(data.facturas.map(f => ({ ...f, estado:"pendiente", cae:null, error:null })));
      // Vienen tildadas todas menos las que ya se facturaron antes
      setSelNom(new Set(data.facturas.filter(f=>!f.yaFacturada).map(f=>f.dni)));
      if (data.facturas.length) {
        setLoteDesde(data.facturas[0].periodo_desde);
        setLoteHasta(data.facturas[0].periodo_hasta);
      }
    } catch(e) { showToast("❌ Error: " + e.message); }
    setLeyendoNom(false);
  };

  // Aplica el período de arriba a las filas tildadas
  const aplicarPeriodoLote = () => {
    if (!loteDesde || !loteHasta) { showToast("⚠ Completá el período"); return; }
    setFilasNom(prev => prev.map(f =>
      selNom.has(f.dni) ? { ...f, periodo_desde: loteDesde, periodo_hasta: loteHasta } : f
    ));
    // La descripción lleva el período adentro, así que se recalcula la etiqueta
    setDatosNom(prev => prev
      ? { ...prev, etiqueta_periodo: etiquetaDePeriodo(loteDesde, loteHasta) }
      : prev);
    showToast(`✓ Período aplicado a ${selNom.size} factura${selNom.size===1?"":"s"}`);
  };

  const editarFilaNom = (dni, campo, valor) =>
    setFilasNom(prev => prev.map(f => f.dni===dni ? { ...f, [campo]: valor } : f));

  // Le manda al CLIENTE, por mail, las facturas que le pasemos, con los PDF
  // adjuntos. Venci nunca le escribe al paciente: se la reenvía el cliente.
  const enviarFacturasPorMailNom = async (ids, periodo) => {
    if (!ids.length) { showToast("⚠ No hay facturas emitidas para enviar"); return; }
    if (!fichaActual.email) {
      showToast(`⚠ ${fichaActual.nombre||"El cliente"} no tiene email cargado — editalo en Datos del cliente`);
      return;
    }
    setEnviandoMailNom(true);
    try {
      const r = await authFetch(`${API_URL}/nominada/enviar-email`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ clienteId: cliente.id, facturaIds: ids, periodo: periodo||"" }),
      });
      const d = await r.json();
      if (d.ok) {
        showToast(`✉️ ${d.data.enviadas} factura${d.data.enviadas===1?"":"s"} enviada${d.data.enviadas===1?"":"s"} a ${d.data.destino}`);
        if (d.data.noEnviadas?.length)
          showToast(`⚠ No se pudieron adjuntar: ${d.data.noEnviadas.join(", ")}`);
        setSelMail(new Set());
        // Se recarga el historial para que las enviadas queden marcadas
        // y el contador vea cuáles ya salieron.
        try {
          const rf = await authFetch(`${API_URL}/clientes/${cliente.id}/facturas`);
          const df = await rf.json();
          if (df.ok) setFacturas(df.data);
        } catch(e) { /* el mail ya salió: no poder refrescar no es un error */ }
      } else {
        showToast("❌ " + (d.error || "No se pudo enviar el mail"));
      }
    } catch(e) {
      showToast("❌ Error de conexión: " + e.message);
    } finally {
      setEnviandoMailNom(false);
    }
  };

  // Abre el PDF de una factura nominada (tres copias, con el DNI y el nombre
  // del paciente). El endpoint pide token, así que no sirve un <a href>:
  // se baja con fetch y se abre el archivo ya descargado.
  const abrirPDFNominada = async (facturaId) => {
    if (!facturaId) { showToast("⚠ Esa factura no tiene PDF disponible"); return; }
    try {
      const token = localStorage.getItem("venci_token");
      const r = await fetch(`${API_URL}/nominada/${facturaId}/pdf`,
                            { headers:{ Authorization:`Bearer ${token}` } });
      if (!r.ok) { showToast("❌ No se pudo abrir el PDF"); return; }
      window.open(URL.createObjectURL(await r.blob()), "_blank");
    } catch(e) { showToast("❌ Error: " + e.message); }
  };

  const toggleNom = (dni) => setSelNom(prev => {
    const s = new Set(prev);
    s.has(dni) ? s.delete(dni) : s.add(dni);
    return s;
  });

  const emitirNominadas = async () => {
    const aEmitir = filasNom.filter(f => selNom.has(f.dni) && f.estado!=="aprobada");
    if (!aEmitir.length) { showToast("⚠ No hay facturas tildadas"); return; }

    setConfirmarNom(false);
    setEmitiendoNom(true);
    setProgresoNom({ total: aEmitir.length, hechas: 0, ok: 0, error: 0 });

    for (let i = 0; i < aEmitir.length; i++) {
      const f = aEmitir[i];
      try {
        const r = await authFetch(`${API_URL}/nominada/emitir`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            clienteId: cliente.id,
            dni: f.dni,
            nombreReceptor: f.nombre,
            // Si el paciente ya tenía factura de este período, se emite SÓLO
            // lo que falta — no el total de la planilla, que duplicaría.
            monto: f.parcial ? f.diferencia : f.total,
            cantidad: f.parcial ? (f.sesionesNuevas || 1) : f.cantidad_factura,
            precioUnitario: f.unitario,
            periodoDesde: f.periodo_desde,
            periodoHasta: f.periodo_hasta,
            fechaEmision: hoyNom,
            // Cada factura lleva el mes de SU propio período: si una fila se
            // editó aparte, su descripción dice ese mes y no el del lote.
            descripcion: descripcionNom(motivoNom, etiquetaDePeriodo(f.periodo_desde, f.periodo_hasta)),
            condicionVenta: f.medio_pago || "Otra",
            idOrigen: f.idOrigen || null,
            diasVtoPago: 10,
          }),
        });
        const d = await r.json();
        if (d.ok) {
          setFilasNom(prev => prev.map(x => x.dni===f.dni
            ? { ...x, estado:"aprobada", cae:d.data.cae, idFactura:d.data.idFactura, error:null } : x));
          setProgresoNom(p => ({ ...p, hechas:p.hechas+1, ok:p.ok+1 }));
        } else {
          setFilasNom(prev => prev.map(x => x.dni===f.dni
            ? { ...x, estado:"rechazada", error:d.error } : x));
          setProgresoNom(p => ({ ...p, hechas:p.hechas+1, error:p.error+1 }));
        }
      } catch(e) {
        setFilasNom(prev => prev.map(x => x.dni===f.dni
          ? { ...x, estado:"rechazada", error:"Error de conexión" } : x));
        setProgresoNom(p => ({ ...p, hechas:p.hechas+1, error:p.error+1 }));
      }
      await new Promise(r => setTimeout(r, 250));
    }
    setEmitiendoNom(false);
  };

  // ── Movimientos guardados de este cliente ─────────────────────────────
  const hoyISO = new Date().toISOString().slice(0,10);
  const primerDiaMes = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}-01`;
  const [movDesde,  setMovDesde]  = useState(primerDiaMes);
  const [movHasta,  setMovHasta]  = useState(hoyISO);
  const [movEstado, setMovEstado] = useState("todos");
  const [movFuente, setMovFuente] = useState("todas");
  const [movTipo,   setMovTipo]   = useState("todos");
  const [movLista,  setMovLista]  = useState([]);
  const [movResumen,setMovResumen]= useState(null);
  const [movCargando,setMovCargando] = useState(false);

  const cargarMovimientos = async () => {
    setMovCargando(true);
    try {
      const q = new URLSearchParams({
        desde: movDesde, hasta: movHasta,
        estado: movEstado, fuente: movFuente, tipo: movTipo,
      });
      const r = await authFetch(`${API_URL}/clientes/${cliente.id}/movimientos?${q}`);
      const d = await r.json();
      if (d.ok) { setMovLista(d.data.movimientos||[]); setMovResumen(d.data.resumen||null); }
    } catch(e) { /* si falla, la lista queda vacía y no rompe nada */ }
    setMovCargando(false);
  };

  // Se cargan al entrar a la pestaña y cada vez que cambia un filtro
  useEffect(()=>{
    if (tab === "movimientos" && cliente?.id) cargarMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab, cliente?.id, movDesde, movHasta, movEstado, movFuente, movTipo]);

  // ── Medios de cobro que el sistema lee, y pedido de nuevos ────────────
  const [lectores, setLectores]                 = useState([]);
  const [medioPedido, setMedioPedido]           = useState("");
  const [comentarioPedido, setComentarioPedido] = useState("");
  const [archivoPedido, setArchivoPedido]       = useState(null);
  const [enviandoPedido, setEnviandoPedido]     = useState(false);
  const [pedidoEnviado, setPedidoEnviado]       = useState(false);

  useEffect(()=>{
    authFetch(`${API_URL}/lectores`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setLectores(d.data); })
      .catch(()=>{});
  },[]);

  // El resumen se manda por mail al equipo y NO se guarda en el sistema.
  const enviarPedidoLector = async () => {
    if (!medioPedido.trim()) { showToast("⚠ Escribí el nombre del medio de cobro"); return; }
    setEnviandoPedido(true);
    try {
      const fd = new FormData();
      fd.append("medio", medioPedido.trim());
      fd.append("clienteId", cliente.id);
      if (comentarioPedido) fd.append("comentario", comentarioPedido);
      if (archivoPedido)    fd.append("resumen", archivoPedido);

      const r = await authFetch(`${API_URL}/lectores/solicitar`, { method:"POST", body: fd });
      const d = await r.json();
      if (!d.ok) { showToast("❌ " + (d.error||"No se pudo enviar")); setEnviandoPedido(false); return; }

      setPedidoEnviado(true);
      setMedioPedido(""); setComentarioPedido(""); setArchivoPedido(null);
    } catch(e) { showToast("❌ Error: " + e.message); }
    setEnviandoPedido(false);
  };

  // Cada cliente tiene su propia prueba y su propio ciclo, según la fecha en
  // que el contador le dio el alta. Por eso se consulta por cliente.
  useEffect(()=>{
    if (!cliente?.id) return;
    authFetch(`${API_URL}/clientes/${cliente.id}/suscripcion`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setSuscripcion(d.data); })
      .catch(()=>{});
  },[cliente?.id]);

  // Abre el checkout de Mercado Pago para la suscripción de ESTE cliente
  const pagarSuscripcion = async () => {
    setPagandoSus(true);
    try {
      const r = await authFetch(`${API_URL}/clientes/${cliente.id}/suscripcion`, { method:"POST" });
      const d = await r.json();
      if (!d.ok || !d.data?.init_point) {
        showToast("❌ " + (d.error || "No se pudo generar el link de pago"));
        setPagandoSus(false);
        return;
      }
      window.open(d.data.init_point, "_blank");
    } catch(e) { showToast("❌ Error: " + e.message); }
    setPagandoSus(false);
  };

  // El cobro del ciclo del contador se sacó: ahora cada cliente tiene su
  // propia suscripción y se paga desde su ficha (ver pagarSuscripcion).
  const [facturas, setFacturas] = useState([]);
  const [certEstado, setCertEstado] = useState(null);
  const [cargandoCert, setCargandoCert] = useState(true);
  const [paso, setPaso]         = useState("inicio");
  const [progreso, setProgreso] = useState(0);
  const [pasoTxt, setPasoTxt]   = useState("");
  const [movimientos, setMovs]  = useState([]);
  const [sel, setSel]           = useState(new Set());
  const [filtro, setFiltro]     = useState("entradas");
  const [modalConf, setModalConf] = useState(false);
  const [facturasOk, setFacturasOk] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [categoriaPorMov, setCategoriaPorMov] = useState({}); // {movId: "Prendas de vestir"}
  const [movAbierto, setMovAbierto] = useState(null); // id del movimiento con el desplegable abierto
  const [fuenteDetectada, setFuenteDetectada] = useState(null);
  const [pidiendoFuente, setPidiendoFuente] = useState(false);
  const [archivoPendiente, setArchivoPendiente] = useState(null);
  const [verificacionCuit, setVerificacionCuit] = useState(null);
  const [idsYaFacturados, setIdsYaFacturados] = useState(new Set());
  const [categoriaGlobal, setCategoriaGlobal] = useState(""); // categoría global para aplicar a todos los tildados

  const PASOS_IA = ["Leyendo el PDF...","Extrayendo encabezado...","Identificando período...","Procesando movimientos...","Clasificando entradas y salidas...","Detectando transferencias...","Verificando montos...","Clasificación completada"];

  // Cargar facturas del cliente desde la DB
  useEffect(()=>{
    authFetch(`${API_URL}/clientes/${cliente.id}/facturas`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setFacturas(d.data); })
      .catch(()=>{});
  }, [cliente.id]);

  // Cargar estado del certificado ARCA del cliente
  const cargarEstadoCert = () => {
    setCargandoCert(true);
    authFetch(`${API_URL}/clientes/${cliente.id}/estado-certificado`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setCertEstado(d.data); setCargandoCert(false); })
      .catch(()=>setCargandoCert(false));
  };
  useEffect(()=>{ cargarEstadoCert(); }, [cliente.id]);

  const MOVIMIENTOS_DEMO = [
    {id:"156537312975",fecha:"2026-05-01",descripcion:"Transferencia recibida Zaira Yanina Aranguiz",valor:15000,tipo:"entrada"},
    {id:"157892341021",fecha:"2026-05-03",descripcion:"Pago recibido - Servicio de asesoramiento",   valor:45000,tipo:"entrada"},
    {id:"158234109876",fecha:"2026-05-05",descripcion:"Transferencia recibida Roberto Carlos Perez",  valor:28500,tipo:"entrada"},
    {id:"158901234567",fecha:"2026-05-07",descripcion:"Pago recibido - Honorarios profesionales",     valor:62000,tipo:"entrada"},
    {id:"157232398160",fecha:"2026-05-01",descripcion:"Transferencia enviada Maria Amanda Dupuy",     valor:-2800,tipo:"salida"},
    {id:"156480114423",fecha:"2026-05-01",descripcion:"Transferencia enviada Silvina Arellano",       valor:-1500,tipo:"salida"},
  ];

  const procesarPDFReal = async (archivo, fuenteManual=null) => {
    setPaso("procesando"); setProgreso(0);
    setArchivoPendiente(archivo);
    let i=0;
    const iv=setInterval(()=>{ if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;} },400);
    try {
      const base64 = await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(archivo);});
      const resp = await authFetch(`${API_URL}/pdf/procesar`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pdf:base64,clienteId:cliente.id,fuenteManual})});
      clearInterval(iv); setProgreso(100);
      const data = await resp.json();

      // Si no se reconoció el formato (y no era ya un intento manual),
      // pedimos al usuario que elija de qué billetera/banco es, en vez
      // de adivinar y arriesgar un resultado incorrecto.
      if (!data.ok && data.error === "FUENTE_DESCONOCIDA" && !fuenteManual) {
        clearInterval(iv);
        setPidiendoFuente(true);
        setPaso("inicio");
        return;
      }

      // Consultamos qué movimientos de este cliente ya fueron facturados
      // antes — así, si se vuelve a subir el mismo PDF, esas filas
      // aparecen marcadas y bloqueadas en vez de poder re-facturarse.
      const respIds = await authFetch(`${API_URL}/clientes/${cliente.id}/movimientos-facturados`);
      const dataIds = await respIds.json();
      const idsFacturados = dataIds.ok ? new Set(dataIds.data) : new Set();
      setIdsYaFacturados(idsFacturados);

      setTimeout(()=>{
        const movs = data.ok?data.data.movimientos:MOVIMIENTOS_DEMO;
        if (data.ok) {
          setFuenteDetectada(data.data.fuenteDetectada);
          setVerificacionCuit(data.data.verificacionCuit || null);
        }
        setMovs(movs);
        // Pre-tildamos solo las entradas que TODAVÍA no fueron facturadas
        setSel(new Set(movs.filter(m=>m.tipo==="entrada" && !idsFacturados.has(m.id)).map(m=>m.id)));
        setPaso("revision");
      },400);
    } catch {
      clearInterval(iv);
      setTimeout(()=>{ setMovs(MOVIMIENTOS_DEMO); setSel(new Set(MOVIMIENTOS_DEMO.filter(m=>m.tipo==="entrada").map(m=>m.id))); setPaso("revision"); },400);
    }
  };

  const procesarDemo = () => {
    setPaso("procesando"); setProgreso(0); let i=0;
    const iv=setInterval(()=>{
      if(i<PASOS_IA.length){setPasoTxt(PASOS_IA[i]);setProgreso(Math.round((i+1)/PASOS_IA.length*100));i++;}
      else{clearInterval(iv);setTimeout(()=>{setMovs(MOVIMIENTOS_DEMO);setSel(new Set(MOVIMIENTOS_DEMO.filter(m=>m.tipo==="entrada").map(m=>m.id)));setPaso("revision");},400);}
    },320);
  };

  const reiniciarFact = () => { setPaso("inicio"); setMovs([]); setSel(new Set()); setFiltro("entradas"); setFacturasOk([]); };
  const onEmitir = (aprobadas) => {
    setFacturasOk(aprobadas);
    setModalConf(false);
    setPaso("emitidas");
    // Marcamos de inmediato los movimientos recién facturados como
    // "ya facturados" — sin esperar a una nueva consulta al backend, así
    // si el contador vuelve a esta misma lista (sin re-subir el PDF),
    // ya las ve bloqueadas y no puede emitirlas de nuevo por accidente.
    setIdsYaFacturados(prev => {
      const nuevo = new Set(prev);
      aprobadas.forEach(f => { if (f.idMovimientoOrigen !== undefined) nuevo.add(f.idMovimientoOrigen); });
      return nuevo;
    });
    // Recargar facturas desde DB
    authFetch(`${API_URL}/clientes/${cliente.id}/facturas`)
      .then(r=>r.json()).then(d=>{ if(d.ok) setFacturas(d.data); });
    showToast(`${aprobadas.length} facturas emitidas para ${cliente.nombre.split(" ")[0]}`);
  };

  const esMono    = cliente.tipo==="Monotributista";
  // El total facturado se calcula a partir de las FACTURAS REALES emitidas
  // (las mismas que se ven en el Historial), no del campo "facturacion_anual"
  // del cliente — ese campo nunca se actualiza automáticamente al emitir,
  // y por eso siempre mostraba $0 aunque ya hubiera facturas reales.
  const anioActual = new Date().getFullYear();
  const facturacionAnualReal = facturas
    .filter(f => f.fecha_emision && new Date(f.fecha_emision).getFullYear() === anioActual)
    .reduce((s, f) => s + parseFloat(f.monto || 0), 0);
  const pct       = esMono && LIMITES_MONO[cliente.categoria] ? Math.min(100,Math.round((facturacionAnualReal/LIMITES_MONO[cliente.categoria])*100)) : null;
  const colorPct  = pct>=90?C.danger:pct>=75?C.warning:C.green;
  const obsCliente= obligaciones.filter(o=>o.clienteId===cliente.id);
  const pendientes= obsCliente.filter(o=>o.estado==="pendiente");
  const tipoFact  = esMono?"C":"B";
  const selCnt    = sel.size;
  const totalSel  = movimientos.filter(m=>sel.has(m.id)).reduce((s,m)=>s+m.valor,0);
  const movsParaFact = movimientos.filter(m=>sel.has(m.id)).map(m=>{
    const cat = categoriaPorMov[m.id];
    // La descripción de la factura NO debe mostrar el nombre real de quien
    // transfirió (eso es un dato del medio de pago, no de la venta en sí) —
    // según pidió Dariela, el cliente en la factura siempre es "Consumidor
    // Final", y la descripción debe reflejar SOLO qué se vendió (la
    // categoría elegida del desplegable), nunca el texto original del PDF
    // de Mercado Pago con el nombre de la persona.
    return { ...m, descripcion: cat || "Venta de mercadería" };
  });
  // Tildados a los que todavía no se les asignó categoría: si se emiten así,
  // la factura sale con la descripción genérica "Venta de mercadería".
  const selSinCategoria = movimientos.filter(m=>sel.has(m.id)&&!categoriaPorMov[m.id]).length;
  const entradasCnt = movimientos.filter(m=>m.tipo==="entrada").length;
  const salidasCnt  = movimientos.filter(m=>m.tipo==="salida").length;

  return (
    <div style={{animation:"fadeUp 0.3s ease"}}>
      {/* Botón volver — no se muestra para cuentas tipo "comercio",
          que no tienen una grilla de clientes a la que volver */}
      {onVolver&&(
        <button onClick={onVolver}
          style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",color:C.inkMid,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:24,padding:"4px 0"}}
          onMouseEnter={e=>e.currentTarget.style.color=C.ink}
          onMouseLeave={e=>e.currentTarget.style.color=C.inkMid}>
          ← Volver a clientes
        </button>
      )}

      {/* Suscripción de ESTE cliente — $15.000 por mes, la paga el contador
          desde acá. Cada cliente tiene su propia fecha según cuándo se dio
          el alta, por eso el cartel vive en la ficha y no en el panel. */}
      {suscripcion && !suscripcion.exento && (suscripcion.avisar || suscripcion.enGracia || !suscripcion.permitido) && (
        <div style={{
          background: suscripcion.permitido && !suscripcion.enGracia ? "rgba(254,188,46,0.1)" : C.dangerBg,
          border: `1px solid ${suscripcion.permitido && !suscripcion.enGracia ? C.warning : C.danger}66`,
          borderRadius:10, padding:"14px 18px", marginBottom:16,
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:14, flexWrap:"wrap"
        }}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:suscripcion.permitido&&!suscripcion.enGracia?C.warning:C.danger,marginBottom:3}}>
              {!suscripcion.permitido
                ? (suscripcion.enTrial ? "⛔ La prueba venció" : "⛔ La suscripción venció")
                : suscripcion.enGracia
                ? `⚠ Venció — te ${suscripcion.diasGraciaRestantes===1?"queda":"quedan"} ${suscripcion.diasGraciaRestantes} día${suscripcion.diasGraciaRestantes===1?"":"s"} para facturar`
                : (suscripcion.enTrial
                    ? `⏱ La prueba vence en ${suscripcion.diasRestantes} día${suscripcion.diasRestantes===1?"":"s"}`
                    : `📅 La suscripción vence en ${suscripcion.diasRestantes} día${suscripcion.diasRestantes===1?"":"s"}`)}
            </div>
            <div style={{fontSize:11,color:C.inkMid}}>
              {!suscripcion.permitido
                ? `No se pueden emitir facturas de ${cliente.nombre} hasta activar la suscripción.`
                : `${fmt(suscripcion.precioMensual)} por mes para seguir facturando a ${cliente.nombre}.`}
            </div>
          </div>
          <button onClick={pagarSuscripcion} disabled={pagandoSus}
            style={{background:C.green,border:"none",color:"#fff",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:pagandoSus?"wait":"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
            {pagandoSus ? "Abriendo..." : `Pagar ${fmt(suscripcion.precioMensual)}/mes`}
          </button>
        </div>
      )}

      {/* Header cliente */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:52,height:52,borderRadius:12,background:`linear-gradient(135deg,${C.navy}66,${C.green}66)`,border:`1px solid ${C.borderMid}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:C.green}}>
            {initiales(cliente.nombre)}
          </div>
          <div>
            <h1 style={{fontSize:22,fontWeight:700,color:C.ink,letterSpacing:"-0.02em",marginBottom:4}}>{cliente.nombre}</h1>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:C.inkDim,fontFamily:"monospace"}}>{cliente.cuit}</span>
              <span style={{color:C.borderMid}}>·</span>
              <span style={{fontSize:11,color:C.inkDim}}>{esMono?`Monotributista · Categoría ${cliente.categoria}`:"Responsable Inscripto"}</span>
              <Badge color={cliente.arca_estado==="Suspendido"?C.danger:C.success} bg={cliente.arca_estado==="Suspendido"?C.dangerBg:C.successBg} border={cliente.arca_estado==="Suspendido"?C.danger+"44":C.success+"44"}>
                {cliente.arca_estado==="Suspendido"?"⚠ Suspendido":"✓ ARCA activo"}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* ── FICHA DEL CLIENTE ──────────────────────────────────────────────
          Los datos de contacto y los fiscales que salen impresos en la
          factura, a la vista al abrir el cliente y editables con el lápiz.
          El mail es el que se usa para mandarle las facturas, así que si
          falta se avisa acá y no recién al momento de enviar. */}
      <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:editandoFicha?14:12}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:"0.07em",textTransform:"uppercase"}}>
            Datos del cliente
          </div>
          {!editandoFicha&&(
            <button onClick={abrirEdicionFicha} title="Editar los datos del cliente"
              style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 11px",fontSize:11,color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:5}}>
              ✏️ Editar
            </button>
          )}
        </div>

        {editandoFicha ? (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(215px,1fr))",gap:12,marginBottom:14}}>
              {[
                ["email",             "Email",                 "estudio@ejemplo.com",   "email"],
                ["telefono",          "Celular",               "343 745-6068",          "tel"],
                ["domicilio",         "Domicilio comercial",   "San Martín 123, Santa Elena", "text"],
                ["ingresosBrutos",    "Ingresos Brutos",       "27-32580108-0",         "text"],
                ["inicioActividades", "Inicio de actividades", "",                      "date"],
              ].map(([campo,etiqueta,ph,tipo])=>(
                <div key={campo}>
                  <label style={{display:"block",fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>
                    {etiqueta}
                  </label>
                  <input type={tipo} value={borradorFicha[campo]||""} placeholder={ph}
                    onChange={e=>setBorrador(p=>({...p,[campo]:e.target.value}))}
                    style={{width:"100%",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",fontSize:12,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
            </div>
            <div style={{fontSize:10,color:C.inkDim,marginBottom:12,lineHeight:1.6}}>
              El domicilio, Ingresos Brutos y el inicio de actividades salen impresos en la factura.
              Si dejás un campo vacío, se borra ese dato.
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={()=>setEditFicha(false)} variant="ghost">Cancelar</Btn>
              <button onClick={guardarFicha} disabled={guardandoFicha}
                style={{background:C.green,border:"none",color:"#0A1219",borderRadius:8,padding:"9px 20px",fontSize:12,fontWeight:700,cursor:guardandoFicha?"wait":"pointer",fontFamily:"'Inter',sans-serif"}}>
                {guardandoFicha ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:"13px 20px"}}>
            {[
              ["Email",                 fichaActual.email,    true],
              ["Celular",               fichaActual.telefono, false],
              ["CUIT",                  fichaActual.cuit,     false],
              ["Punto de venta",        fichaActual.punto_venta ? String(fichaActual.punto_venta).padStart(5,"0") : "", false],
              ["Domicilio comercial",   fichaActual.domicilio, false],
              ["Ingresos Brutos",       fichaActual.ingresos_brutos, false],
              ["Inicio de actividades", fichaActual.inicio_actividades ? fmtF(fichaActual.inicio_actividades) : "", false],
            ].map(([etiqueta,valor,avisarSiFalta])=>(
              <div key={etiqueta}>
                <div style={{fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>
                  {etiqueta}
                </div>
                <div style={{fontSize:12.5,color:valor?C.ink:(avisarSiFalta?C.warning:C.inkDim),fontWeight:valor?500:400,wordBreak:"break-word"}}>
                  {valor || (avisarSiFalta ? "Falta — sin esto no se le pueden enviar las facturas" : "—")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Barra facturación Mono */}
      {esMono && pct!==null && (
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:600,color:C.ink}}>Facturación anual vs límite de categoría</div>
            <div style={{fontSize:20,fontWeight:700,color:colorPct,fontFamily:"'Inter',sans-serif"}}>{pct}%</div>
          </div>
          <div style={{height:8,background:C.bg,borderRadius:4,overflow:"hidden",marginBottom:8}}>
            <div style={{height:"100%",width:`${pct}%`,background:colorPct,borderRadius:4,transition:"width 0.8s ease"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.inkDim}}>
            <span>Facturado: <strong style={{color:C.ink}}>{fmt(facturacionAnualReal)}</strong></span>
            <span>Límite Cat. {cliente.categoria}: <strong style={{color:C.ink}}>{fmt(LIMITES_MONO[cliente.categoria])}</strong></span>
          </div>
          {pct>=75&&(
            <div style={{marginTop:8,fontSize:11,color:colorPct,fontWeight:600}}>
              {pct>=90?"⚠ Muy cerca del límite — considera cambiar de categoría":"↑ Acercándose al límite de categoría"}
              {" · "}Faltan {fmt(LIMITES_MONO[cliente.categoria]-facturacionAnualReal)} para Cat. siguiente
            </div>
          )}
        </div>
      )}

      {/* Tabs — scroll horizontal en mobile */}
      <div className="venci-tabs" style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {[
          ["facturacion","📄 Facturación"],
          ["historial",  "🗂 Historial"],
          ["exportar",   "📥 Exportar"],
          ["arca",       "⚙️ Configuración ARCA"],
          ["movimientos","💵 Movimientos"],
          ["nominada",   "📋 Facturar por Excel"],
          ["lectores",   "🏦 Medios de cobro"],
          ["ayuda",      "❓ Cómo emitir"],
        ].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)}
            style={{padding:"8px 16px",borderRadius:"6px 6px 0 0",border:`1px solid ${tab===k?C.border:"transparent"}`,borderBottom:tab===k?`1px solid ${C.bgCard}`:`1px solid ${C.border}`,background:tab===k?C.bgCard:"transparent",color:tab===k?C.green:C.inkMid,fontSize:12,fontWeight:tab===k?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s",marginBottom:-1,display:"flex",alignItems:"center",gap:6}}>
            {l}
            {k==="arca"&&certEstado&&certEstado.estado_certificado!=="listo"&&(
              <span style={{width:6,height:6,borderRadius:"50%",background:C.warning,flexShrink:0}}/>
            )}
            {k==="arca"&&certEstado&&certEstado.estado_certificado==="listo"&&(
              <span style={{width:6,height:6,borderRadius:"50%",background:C.green,flexShrink:0}}/>
            )}
          </button>
        ))}
      </div>

      {/* CONFIRMACIÓN ANTES DE EMITIR — sale sola al tocar "Emitir", para que
          nadie mande un lote a ARCA sin ver antes qué se va a emitir. */}
      {confirmarNom&&(()=>{
        const aEmitir = filasNom.filter(f => selNom.has(f.dni) && f.estado!=="aprobada");
        const totalLote = aEmitir.reduce((s,f)=>s+(f.parcial ? f.diferencia : f.total), 0);
        const parciales = aEmitir.filter(f=>f.parcial);
        const ejemplo = aEmitir[0];
        const vto = (()=>{ const d=new Date(hoyNom+"T12:00:00Z"); d.setUTCDate(d.getUTCDate()+10);
                           return d.toISOString().slice(0,10); })();
        return (
          <Modal onClose={()=>setConfirmarNom(false)} maxW={560}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Revisá antes de emitir</div>
                <div style={{fontSize:17,fontWeight:700,color:C.ink}}>
                  {aEmitir.length} factura{aEmitir.length===1?"":"s"} · {fmt(totalLote)}
                </div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>
                  A nombre de cada paciente, con su DNI. Una vez emitidas no se pueden deshacer.
                </div>
              </div>
              <button onClick={()=>setConfirmarNom(false)}
                style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim}}>×</button>
            </div>

            {/* Datos del emisor incompletos */}
            {faltanDatosEmisor.length>0&&(
              <div style={{background:"rgba(254,188,46,0.1)",border:`1px solid ${C.warning}`,borderRadius:8,padding:"11px 13px",marginBottom:12}}>
                <div style={{fontSize:11.5,fontWeight:700,color:C.warning,marginBottom:4}}>⚠️ Faltan datos del emisor</div>
                <div style={{fontSize:11,color:C.inkMid,lineHeight:1.65}}>
                  El PDF sale sin <strong style={{color:C.ink}}>{faltanDatosEmisor.join(" · ")}</strong>.
                  Las facturas son válidas igual, pero el comprobante impreso queda incompleto.
                </div>
              </div>
            )}

            {/* Emisiones parciales */}
            {parciales.length>0&&(
              <div style={{background:"rgba(61,170,138,0.08)",border:`1px solid rgba(61,170,138,0.35)`,borderRadius:8,padding:"11px 13px",marginBottom:12}}>
                <div style={{fontSize:11.5,fontWeight:700,color:C.green,marginBottom:4}}>
                  {parciales.length} {parciales.length===1?"factura es":"facturas son"} por la diferencia
                </div>
                <div style={{fontSize:11,color:C.inkMid,lineHeight:1.65}}>
                  {parciales.length===1?"Ese paciente ya tenía":"Esos pacientes ya tenían"} factura de este período.
                  Se emite sólo lo que falta, no el total de la planilla.
                </div>
              </div>
            )}

            {/* Lo que va igual en todas */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"13px 15px",marginBottom:12}}>
              <div style={{fontSize:9.5,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:9}}>Igual en todas</div>
              {[
                ["Emisor", `${cliente?.nombre} · CUIT ${cliente?.cuit}`],
                ["Tipo", "Factura C · Consumidor Final"],
                ["Fecha de emisión", fmtFechaCorta(hoyNom)],
                ["Vto. para el pago", fmtFechaCorta(vto)],
              ].map(([k,v])=>(
                <div key={k} style={{display:"flex",gap:8,marginBottom:4,fontSize:11.5,lineHeight:1.6}}>
                  <span style={{color:C.inkDim,minWidth:118,flexShrink:0}}>{k}</span>
                  <span style={{color:C.ink,fontWeight:500}}>{v}</span>
                </div>
              ))}
              {ejemplo&&(
                <div style={{display:"flex",gap:8,marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`,fontSize:11.5,lineHeight:1.6}}>
                  <span style={{color:C.inkDim,minWidth:118,flexShrink:0}}>Descripción</span>
                  <span style={{color:C.green,fontWeight:600}}>
                    {descripcionNom(motivoNom, etiquetaDePeriodo(ejemplo.periodo_desde, ejemplo.periodo_hasta))}
                  </span>
                </div>
              )}
            </div>

            {/* Las facturas, una por una */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 0",marginBottom:12,maxHeight:190,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
                <tbody>
                  {aEmitir.map(f=>(
                    <tr key={f.dni} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:"7px 14px",color:C.ink,fontWeight:600}}>{f.nombre}</td>
                      <td style={{padding:"7px 6px",color:C.inkDim,fontFamily:"monospace",fontSize:10.5}}>{f.dni}</td>
                      <td style={{padding:"7px 6px",color:C.inkDim,fontSize:10.5,whiteSpace:"nowrap"}}>
                        {fmtFechaCorta(f.periodo_desde)} al {fmtFechaCorta(f.periodo_hasta)}
                      </td>
                      <td style={{padding:"7px 14px",textAlign:"right",fontWeight:700,color:f.parcial?C.warning:C.ink,whiteSpace:"nowrap"}}>
                        {fmt(f.parcial ? f.diferencia : f.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{fontSize:10,color:C.inkDim,marginBottom:14,lineHeight:1.6}}>
              Cada PDF sale en tres copias: original, duplicado y triplicado.
              Para ver una factura en detalle, cerrá acá y tocá el ojito 👁 de esa fila.
            </div>

            <div style={{display:"flex",gap:8}}>
              <Btn onClick={()=>setConfirmarNom(false)} variant="ghost" style={{flex:1,justifyContent:"center"}}>
                Cancelar
              </Btn>
              <button onClick={emitirNominadas}
                style={{flex:2,background:C.green,border:"none",color:"#0A1219",borderRadius:8,padding:"11px 20px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Confirmar y emitir a ARCA
              </button>
            </div>
          </Modal>
        );
      })()}
      {/* VISTA PREVIA — cómo va a salir la factura, antes de emitirla */}
      {previaNom&&(()=>{
        const f = previaNom;
        const monto = f.parcial ? f.diferencia : f.total;
        const cant  = f.parcial ? (f.sesionesNuevas || 1) : f.cantidad_factura;
        const desc  = descripcionNom(motivoNom, etiquetaDePeriodo(f.periodo_desde, f.periodo_hasta));
        const vto   = (()=>{ const d=new Date(hoyNom+"T12:00:00Z"); d.setUTCDate(d.getUTCDate()+10);
                             return d.toISOString().slice(0,10); })();
        const linea = (etiqueta, valor, resaltar) => (
          <div style={{display:"flex",gap:8,marginBottom:4,fontSize:11.5,lineHeight:1.6}}>
            <span style={{color:C.inkDim,minWidth:132,flexShrink:0}}>{etiqueta}</span>
            <span style={{color:resaltar?C.green:C.ink,fontWeight:resaltar?700:500}}>{valor || "—"}</span>
          </div>
        );
        return (
          <Modal onClose={()=>setPreviaNom(null)} maxW={580}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Vista previa</div>
                <div style={{fontSize:16,fontWeight:700,color:C.ink}}>Así va a salir la factura</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Todavía no se emitió nada.</div>
              </div>
              <button onClick={()=>setPreviaNom(null)}
                style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkDim}}>×</button>
            </div>

            {/* Si al emisor le faltan datos, se avisa ACÁ y no después de emitir */}
            {faltanDatosEmisor.length>0&&(
              <div style={{background:"rgba(254,188,46,0.1)",border:`1px solid ${C.warning}`,borderRadius:8,padding:"11px 13px",marginBottom:14}}>
                <div style={{fontSize:11.5,fontWeight:700,color:C.warning,marginBottom:4}}>
                  ⚠️ Faltan datos del emisor
                </div>
                <div style={{fontSize:11,color:C.inkMid,lineHeight:1.65}}>
                  El comprobante va a salir sin: <strong style={{color:C.ink}}>{faltanDatosEmisor.join(" · ")}</strong>.
                  La factura es válida igual, pero el PDF queda incompleto.
                </div>
              </div>
            )}

            {/* Emisor */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"13px 15px",marginBottom:10}}>
              <div style={{fontSize:9.5,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Emisor</div>
              {linea("Razón social:", cliente?.nombre)}
              {linea("CUIT:", cliente?.cuit)}
              {linea("Domicilio:", emisorNom?.domicilio)}
              {linea("Ingresos Brutos:", emisorNom?.ingresos_brutos)}
              {linea("Inicio actividades:", emisorNom?.inicio_actividades ? fmtFechaCorta(emisorNom.inicio_actividades) : "")}
              {linea("Punto de venta:", cliente?.punto_venta ? String(cliente.punto_venta).padStart(5,"0") : "—")}
            </div>

            {/* Receptor */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"13px 15px",marginBottom:10}}>
              <div style={{fontSize:9.5,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Receptor</div>
              {linea("DNI:", f.dni, true)}
              {linea("Apellido y nombre:", f.nombre, true)}
              {linea("Condición IVA:", "Consumidor Final")}
              {linea("Condición de venta:", f.medio_pago || "Otra")}
            </div>

            {/* Comprobante */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"13px 15px",marginBottom:10}}>
              <div style={{fontSize:9.5,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Comprobante</div>
              {linea("Tipo:", "Factura C")}
              {linea("Fecha de emisión:", fmtFechaCorta(hoyNom))}
              {linea("Período facturado:", `${fmtFechaCorta(f.periodo_desde)} al ${fmtFechaCorta(f.periodo_hasta)}`, true)}
              {linea("Vto. para el pago:", fmtFechaCorta(vto))}
            </div>

            {/* Detalle */}
            <div style={{background:C.bgCard,border:`1px solid ${C.green}44`,borderRadius:8,padding:"13px 15px"}}>
              <div style={{fontSize:9.5,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Detalle</div>
              <div style={{fontSize:11.5,color:C.ink,fontWeight:600,lineHeight:1.6,marginBottom:10}}>{desc}</div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11.5,color:C.inkMid,marginBottom:4}}>
                <span>{cant} {cant===1?"unidad":"unidades"} × {fmt(f.unitario)}</span>
                <span>{fmt(monto)}</span>
              </div>
              {f.parcial&&(
                <div style={{fontSize:10.5,color:C.warning,marginBottom:6,lineHeight:1.6}}>
                  Ya tiene {fmt(f.yaFacturadoTotal)} facturado de este período — se emite sólo la diferencia.
                </div>
              )}
              <div style={{borderTop:`1px solid ${C.border}`,marginTop:8,paddingTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12.5,fontWeight:700,color:C.ink}}>Importe Total</span>
                <span style={{fontSize:17,fontWeight:800,color:C.green}}>{fmt(monto)}</span>
              </div>
            </div>

            <div style={{fontSize:10,color:C.inkDim,marginTop:12,lineHeight:1.6}}>
              El PDF sale en tres copias: original, duplicado y triplicado.
            </div>

            <div style={{display:"flex",gap:8,marginTop:16}}>
              <Btn onClick={()=>setPreviaNom(null)} variant="ghost" style={{flex:1,justifyContent:"center"}}>Cerrar</Btn>
            </div>
          </Modal>
        );
      })()}
      {/* TAB FACTURAR POR EXCEL — facturas nominadas, a nombre del paciente.
          Módulo aparte: no comparte nada con la facturación por resúmenes. */}
      {tab==="nominada"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Paso 1 — subir la planilla */}
          {!datosNom&&(
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px"}}>
              <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4}}>Facturar a nombre del paciente</div>
              <div style={{fontSize:11,color:C.inkDim,marginBottom:16,lineHeight:1.7}}>
                Las facturas salen con el <strong style={{color:C.inkMid}}>DNI y el nombre</strong> de cada persona,
                agrupadas por paciente. Subí la planilla o cargá los datos a mano.
              </div>

              {/* Elegir cómo cargar */}
              {!modoNom&&(
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:12}}>
                  <label style={{display:"block",border:`2px dashed ${C.green+"44"}`,borderRadius:8,padding:"28px 20px",textAlign:"center",cursor:"pointer",background:C.bg}}>
                    <input type="file" accept=".xlsx,.xls" style={{display:"none"}}
                      onChange={e=>{const f=e.target.files?.[0]; if(f){setExcelNom(f); setModoNom("excel"); subirExcelNom(f);} }}/>
                    <div style={{fontSize:30,marginBottom:10}}>📋</div>
                    <div style={{fontSize:13.5,fontWeight:700,color:C.ink,marginBottom:4}}>Subir planilla</div>
                    <div style={{fontSize:10.5,color:C.inkDim,lineHeight:1.6}}>
                      Excel con Fecha · Paciente · DNI · Importe
                    </div>
                  </label>

                  <button onClick={()=>setModoNom("manual")}
                    style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"28px 20px",textAlign:"center",cursor:"pointer",background:C.bg,fontFamily:"'Inter',sans-serif"}}>
                    <div style={{fontSize:30,marginBottom:10}}>✍️</div>
                    <div style={{fontSize:13.5,fontWeight:700,color:C.ink,marginBottom:4}}>Cargar a mano</div>
                    <div style={{fontSize:10.5,color:C.inkDim,lineHeight:1.6}}>
                      Tipear los pacientes uno por uno
                    </div>
                  </button>
                </div>
              )}

              {/* Leyendo el Excel */}
              {modoNom==="excel"&&leyendoNom&&(
                <div style={{textAlign:"center",padding:"36px 20px"}}>
                  <div style={{fontSize:30,marginBottom:10}}>⏳</div>
                  <div style={{fontSize:13,color:C.inkMid}}>Leyendo la planilla...</div>
                </div>
              )}

              {/* Carga a mano */}
              {modoNom==="manual"&&(
                <div>
                  <div style={{overflowX:"auto",marginBottom:12}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{borderBottom:`1px solid ${C.border}`}}>
                          {[["Paciente","left"],["DNI","left"],["Sesiones","right"],
                            ["Importe por sesión","right"],["Total","right"],["",""]].map(([h,a],i)=>(
                            <th key={i} style={{textAlign:a||"left",padding:"8px 6px",fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filasManual.map(f=>(
                          <tr key={f.id} style={{borderBottom:`1px solid ${C.border}`}}>
                            <td style={{padding:"6px"}}>
                              <input value={f.nombre} onChange={e=>editarManual(f.id,"nombre",e.target.value)}
                                placeholder="Apellido y nombre"
                                style={{width:"100%",minWidth:150,background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 8px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                            </td>
                            <td style={{padding:"6px"}}>
                              <input value={f.dni} inputMode="numeric"
                                onChange={e=>editarManual(f.id,"dni",e.target.value.replace(/\D/g,"").slice(0,8))}
                                placeholder="22963864"
                                style={{width:100,background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 8px",fontSize:11.5,color:C.ink,fontFamily:"monospace",outline:"none"}}/>
                            </td>
                            <td style={{padding:"6px",textAlign:"right"}}>
                              <input value={f.sesiones} inputMode="numeric"
                                onChange={e=>editarManual(f.id,"sesiones",e.target.value.replace(/\D/g,"").slice(0,3))}
                                style={{width:54,background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 8px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",textAlign:"right"}}/>
                            </td>
                            <td style={{padding:"6px",textAlign:"right"}}>
                              <input value={f.unitario} inputMode="decimal"
                                onChange={e=>editarManual(f.id,"unitario",e.target.value.replace(/[^\d.,]/g,""))}
                                placeholder="45000"
                                style={{width:100,background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 8px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",textAlign:"right"}}/>
                            </td>
                            <td style={{padding:"6px",textAlign:"right",fontWeight:700,color:totalFilaManual(f)>0?C.ink:C.inkDim,whiteSpace:"nowrap"}}>
                              {totalFilaManual(f)>0 ? fmt(totalFilaManual(f)) : "—"}
                            </td>
                            <td style={{padding:"6px",textAlign:"right"}}>
                              <button onClick={()=>borrarFilaManual(f.id)}
                                disabled={filasManual.length===1}
                                title="Quitar fila"
                                style={{background:"transparent",border:"none",color:filasManual.length===1?C.border:C.inkDim,fontSize:14,cursor:filasManual.length===1?"not-allowed":"pointer"}}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <button onClick={agregarFilaManual}
                      style={{background:"transparent",border:`1px solid ${C.border}`,color:C.inkMid,borderRadius:6,padding:"7px 14px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      + Agregar paciente
                    </button>
                    <div style={{marginLeft:"auto",fontSize:11.5,color:C.inkMid}}>
                      Total: <strong style={{color:C.green,fontSize:13}}>
                        {fmt(filasManual.reduce((s,f)=>s+totalFilaManual(f),0))}
                      </strong>
                    </div>
                  </div>

                  <div style={{display:"flex",gap:8,marginTop:16}}>
                    <Btn onClick={()=>{setModoNom(null);}} variant="ghost">Volver</Btn>
                    <button onClick={confirmarCargaManual}
                      style={{background:C.green,border:"none",color:"#0A1219",borderRadius:8,padding:"10px 22px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Continuar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Paso 2 — revisar y emitir */}
            {/* FILAS QUE QUEDARON AFUERA — lo más importante de toda la
                pantalla. Cada una es una sesión que NO se va a facturar.
                Antes se salteaban sin avisar y la plata se perdía sin que
                nadie se enterara. */}
            {datosNom?.descartadas?.length>0&&(
              <div style={{background:"rgba(220,80,70,0.09)",border:`1px solid ${C.danger}`,borderRadius:9,padding:"13px 15px"}}>
                <div style={{fontSize:12.5,fontWeight:800,color:C.danger,marginBottom:4}}>
                  ⛔ {datosNom.descartadas.length} fila{datosNom.descartadas.length===1?"":"s"} de
                  la planilla no se {datosNom.descartadas.length===1?"va":"van"} a facturar
                </div>
                <div style={{fontSize:10.5,color:C.inkMid,lineHeight:1.65,marginBottom:9}}>
                  Corregilas en el Excel y volvé a subirlo. Si emitís así, esas sesiones quedan sin facturar.
                </div>
                <div style={{maxHeight:168,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:7,background:C.bg}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <tbody>
                      {datosNom.descartadas.map((d,i)=>(
                        <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                          <td style={{padding:"6px 10px",color:C.inkDim,whiteSpace:"nowrap",fontFamily:"monospace",fontSize:10}}>
                            Fila {d.linea}
                          </td>
                          <td style={{padding:"6px 8px",color:C.ink,fontWeight:600,maxWidth:170,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {d.nombre || "—"}
                          </td>
                          <td style={{padding:"6px 10px",color:C.danger,fontSize:10.5}}>
                            {d.motivo}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Si no se pudo leer NINGUNA fila, no tiene sentido mostrar
              el flujo de emisión vacío: solo el motivo y volver a empezar. */}
          {datosNom?.errorLectura&&(
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={()=>{setDatosNom(null);setFilasNom([]);setExcelNom(null);setModoNom(null);}}
                variant="ghost">Subir otra planilla</Btn>
            </div>
          )}

          {datosNom&&!datosNom.errorLectura&&(
            <>
              {/* Resumen */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10}}>
                {[
                  ["Filas leídas",  datosNom.resumen.filas,    C.inkMid],
                  ["Facturas",      datosNom.resumen.facturas, C.ink],
                  ["Total",         fmt(datosNom.resumen.total), C.green],
                  ["Período",       datosNom.etiqueta_periodo, C.ink],
                ].map(([k,v,col])=>(
                  <div key={k} style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px"}}>
                    <div style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5}}>{k}</div>
                    <div style={{fontSize:15,fontWeight:800,color:col}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Lo que ya se facturó de este período — el cartel que evita
                  duplicar y, sobre todo, que evita dejar sesiones sin facturar
                  cuando la planilla se manda dos veces en el mismo mes. */}
              {(()=>{
                const parciales = filasNom.filter(f=>f.parcial);
                const completas = filasNom.filter(f=>f.yaFacturada && !f.facturadoDeMas);
                const deMas     = filasNom.filter(f=>f.facturadoDeMas);
                if (!parciales.length && !completas.length && !deMas.length) return null;
                const faltante = parciales.reduce((s,f)=>s+(f.diferencia||0),0);
                return (
                  <div style={{background:"rgba(61,170,138,0.07)",border:`1px solid rgba(61,170,138,0.35)`,borderRadius:9,padding:"13px 15px"}}>
                    <div style={{fontSize:12,fontWeight:800,color:C.green,marginBottom:7}}>
                      🔍 Ya hay facturas emitidas de este período
                    </div>
                    {completas.length>0&&(
                      <div style={{fontSize:11,color:C.inkMid,lineHeight:1.7}}>
                        • <strong style={{color:C.ink}}>{completas.length}</strong> {completas.length===1?"paciente ya tiene":"pacientes ya tienen"} su
                        factura completa — {completas.length===1?"viene destildado":"vienen destildados"} para no duplicar.
                      </div>
                    )}
                    {parciales.length>0&&(
                      <div style={{fontSize:11,color:C.inkMid,lineHeight:1.7}}>
                        • <strong style={{color:C.warning}}>{parciales.length}</strong> {parciales.length===1?"paciente tiene":"pacientes tienen"} sesiones
                        nuevas sin facturar por <strong style={{color:C.warning}}>{fmt(faltante)}</strong>.
                        Se emite sólo esa diferencia, no el total de la planilla.
                      </div>
                    )}
                    {deMas.length>0&&(
                      <div style={{fontSize:11,color:C.danger,lineHeight:1.7,fontWeight:600}}>
                        • {deMas.length} {deMas.length===1?"paciente figura":"pacientes figuran"} con MÁS facturado de lo que dice la
                        planilla. Revisalo antes de seguir.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Datos del emisor sin cargar: el comprobante sale incompleto y
                  conviene saberlo ANTES de emitir el lote entero. */}
              {faltanDatosEmisor.length>0&&(
                <div style={{background:"rgba(254,188,46,0.1)",border:`1px solid ${C.warning}`,borderRadius:9,padding:"13px 15px"}}>
                  <div style={{fontSize:12,fontWeight:800,color:C.warning,marginBottom:6}}>
                    ⚠️ Faltan datos del emisor en el comprobante
                  </div>
                  <div style={{fontSize:11,color:C.inkMid,lineHeight:1.7}}>
                    El PDF va a salir sin <strong style={{color:C.ink}}>{faltanDatosEmisor.join(" · ")}</strong>.
                    Las facturas son válidas igual —el CAE no depende de esto— pero el comprobante
                    impreso queda incompleto. Tocá el ojito 👁 de cualquier fila para verlo.
                  </div>
                </div>
              )}

              {/* Avisos sobre los datos — nada se corrige solo */}
              {datosNom.avisos?.length>0&&(
                <div style={{background:"rgba(254,188,46,0.1)",border:`1px solid ${C.warning}`,borderRadius:9,padding:"13px 15px"}}>
                  <div style={{fontSize:12,fontWeight:800,color:C.warning,marginBottom:7}}>
                    ⚠️ Revisá estos datos antes de emitir
                  </div>
                  {datosNom.avisos.map((a,i)=>(
                    <div key={i} style={{fontSize:11,color:C.inkMid,lineHeight:1.7,marginBottom:3}}>• {a.mensaje}</div>
                  ))}
                </div>
              )}

              {/* Controles del lote */}
              <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                <div style={{display:"flex",flexDirection:"column",gap:3,minWidth:190}}>
                  <label style={{fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Motivo</label>
                  <select value={motivoNom} onChange={e=>setMotivoNom(e.target.value)}
                    style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 9px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",cursor:"pointer",outline:"none"}}>
                    {MOTIVOS_NOMINADA.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                {[["Desde",loteDesde,setLoteDesde],["Hasta",loteHasta,setLoteHasta]].map(([lab,val,set])=>(
                  <div key={lab} style={{display:"flex",flexDirection:"column",gap:3}}>
                    <label style={{fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Período {lab}</label>
                    <input type="date" value={val} onChange={e=>set(e.target.value)}
                      style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 9px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  </div>
                ))}
                <button onClick={aplicarPeriodoLote}
                  style={{background:"transparent",border:`1px solid ${C.green}`,color:C.green,borderRadius:6,padding:"7px 14px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Aplicar a las tildadas
                </button>
                <div style={{marginLeft:"auto",fontSize:11,color:C.inkDim}}>
                  {selNom.size} de {filasNom.length} tildadas
                </div>
              </div>

              {/* Así va a salir la descripción */}
              <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px"}}>
                <span style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Descripción de la factura: </span>
                <span style={{fontSize:11.5,color:C.ink,fontWeight:600}}>
                  {descripcionNom(motivoNom, datosNom.etiqueta_periodo)}
                </span>
                <div style={{fontSize:10,color:C.inkDim,marginTop:4,lineHeight:1.6}}>
                  El mes sale del período facturado. Si una fila tiene otro período, su factura lleva ese mes.
                </div>
              </div>

              {/* Lista */}
              <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{borderBottom:`1px solid ${C.border}`}}>
                      <th style={{padding:"10px 8px",width:34}}>
                        <input type="checkbox"
                          checked={selNom.size===filasNom.length&&filasNom.length>0}
                          onChange={e=>setSelNom(e.target.checked ? new Set(filasNom.map(f=>f.dni)) : new Set())}
                          style={{width:14,height:14,accentColor:C.green,cursor:"pointer"}}/>
                      </th>
                      {["Paciente","DNI","Ses.","Importe","Período facturado","Estado"].map((h,i)=>(
                        <th key={h} style={{textAlign:i>=2&&i<=3?"right":"left",padding:"10px 8px",fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* La fila ya facturada se atenúa para que se note que no se
                        va a emitir, pero no tanto como para que no se pueda leer
                        ni tocar el botón del PDF de su comprobante. */}
                    {filasNom.map(f=>(
                      <tr key={f.dni} style={{borderBottom:`1px solid ${C.border}`,opacity:f.yaFacturada?0.72:1}}>
                        <td style={{padding:"8px"}}>
                          <input type="checkbox" checked={selNom.has(f.dni)}
                            disabled={f.estado==="aprobada"}
                            onChange={()=>toggleNom(f.dni)}
                            style={{width:14,height:14,accentColor:C.green,cursor:"pointer"}}/>
                        </td>
                        <td style={{padding:"8px",color:C.ink,fontWeight:600}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <button onClick={()=>setPreviaNom(f)} title="Ver cómo va a salir la factura"
                              style={{background:"transparent",border:"none",cursor:"pointer",fontSize:13,padding:0,lineHeight:1,flexShrink:0}}>
                              👁
                            </button>
                            {f.nombre}
                          </div>
                        </td>
                        <td style={{padding:"8px",color:C.inkMid,fontFamily:"monospace",fontSize:11}}>{f.dni}</td>
                        <td style={{padding:"8px",textAlign:"right",color:C.inkMid}}>{f.cantidad}</td>
                        <td style={{padding:"8px",textAlign:"right",whiteSpace:"nowrap"}}>
                          <div style={{fontWeight:700,color:f.parcial?C.warning:C.ink}}>
                            {fmt(f.parcial ? f.diferencia : f.total)}
                          </div>
                          {f.parcial&&(
                            <div style={{fontSize:9.5,color:C.inkDim,fontWeight:400}}>
                              de {fmt(f.total)}
                            </div>
                          )}
                        </td>
                        <td style={{padding:"8px"}}>
                          <div style={{display:"flex",gap:4,alignItems:"center"}}>
                            <input type="date" value={f.periodo_desde}
                              onChange={e=>editarFilaNom(f.dni,"periodo_desde",e.target.value)}
                              style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 5px",fontSize:10.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                            <span style={{fontSize:10,color:C.inkDim}}>a</span>
                            <input type="date" value={f.periodo_hasta}
                              onChange={e=>editarFilaNom(f.dni,"periodo_hasta",e.target.value)}
                              style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 5px",fontSize:10.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                          </div>
                        </td>
                        <td style={{padding:"8px"}}>
                          <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start"}}>
                          {f.estado==="aprobada" ? (
                            <button onClick={()=>abrirPDFNominada(f.idFactura)}
                              title="Ver el PDF de la factura"
                              style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:"rgba(61,170,138,0.15)",color:C.green,border:"none",cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>
                              ✓ CAE {f.cae} · PDF
                            </button>
                          ) : f.estado==="rechazada" ? (
                            <span title={f.error} style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:"rgba(220,80,70,0.15)",color:C.danger,whiteSpace:"nowrap"}}>
                              ✕ {String(f.error||"").slice(0,40)}
                            </span>
                          ) : f.parcial ? (
                            <span title={`Ya tiene ${fmt(f.yaFacturadoTotal)} facturado de este período`}
                              style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:"rgba(254,188,46,0.18)",color:C.warning,whiteSpace:"nowrap"}}>
                              Falta facturar {fmt(f.diferencia)}
                            </span>
                          ) : f.facturadoDeMas ? (
                            <span title={`Facturado: ${fmt(f.yaFacturadoTotal)} · En la planilla: ${fmt(f.total)}`}
                              style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:"rgba(220,80,70,0.15)",color:C.danger,whiteSpace:"nowrap"}}>
                              Facturado de más
                            </span>
                          ) : f.yaFacturada ? (
                            <span style={{fontSize:10,fontWeight:600,color:C.inkDim}}>Ya facturada</span>
                          ) : f.enviar ? (
                            <span style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:"rgba(254,188,46,0.13)",color:C.warning,whiteSpace:"nowrap"}}>
                              Enviar al paciente
                            </span>
                          ) : null}

                          {/* Los comprobantes que este paciente YA tiene de este
                              período. Así una fila que dice "ya facturada" no
                              obliga a ir al historial a buscar el PDF. */}
                          {Array.isArray(f.emitidas)&&f.emitidas.length>0&&(
                            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                              {f.emitidas.map(c=>(
                                <button key={c.id} onClick={()=>abrirPDFNominada(c.id)}
                                  title={`Ver el PDF · ${fmt(parseFloat(c.monto))} · ${c.fecha?fmtFechaCorta(c.fecha):""}`}
                                  style={{fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:20,background:"rgba(61,170,138,0.12)",color:C.green,border:`1px solid ${C.green}33`,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"monospace"}}>
                                  📄 {String(c.pv).padStart(4,"0")}-{String(c.nro).padStart(8,"0")}
                                </button>
                              ))}
                            </div>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Progreso y emisión */}
              {progresoNom&&(
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 15px",fontSize:12,color:C.inkMid}}>
                  {progresoNom.hechas} de {progresoNom.total} procesadas ·
                  <span style={{color:C.green,fontWeight:700}}> {progresoNom.ok} emitidas</span>
                  {progresoNom.error>0&&<span style={{color:C.danger,fontWeight:700}}> · {progresoNom.error} rechazadas</span>}
                </div>
              )}

              {/* Emitida la tanda, el envío por mail NO se hace acá: se hace
                  desde el Historial, que es donde viven todas las facturas y
                  sigue estando mañana. Acá sólo se avisa dónde está. */}
              {filasNom.some(f => f.estado==="aprobada" && f.idFactura)&&(
                <div style={{background:C.bgCard,border:`1px solid ${C.green}44`,borderRadius:8,padding:"13px 16px",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200,fontSize:11.5,color:C.inkMid,lineHeight:1.65}}>
                    Para mandarle las facturas por mail a <strong style={{color:C.ink}}>{cliente?.nombre||"el cliente"}</strong>,
                    andá al Historial y tildá las que quieras enviar.
                  </div>
                  <button onClick={()=>setTab("historial")}
                    style={{background:"transparent",border:`1px solid ${C.green}66`,color:C.green,borderRadius:8,padding:"9px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                    Ir al Historial →
                  </button>
                </div>
              )}

              <div style={{display:"flex",gap:8}}>
                <Btn onClick={()=>{setDatosNom(null);setFilasNom([]);setExcelNom(null);setProgresoNom(null);setModoNom(null);}}
                  variant="ghost">Empezar de nuevo</Btn>
                <button onClick={()=>setConfirmarNom(true)} disabled={emitiendoNom||selNom.size===0}
                  style={{background:selNom.size?C.green:"rgba(61,170,138,0.4)",border:"none",color:"#0A1219",borderRadius:8,padding:"10px 22px",fontSize:12.5,fontWeight:700,cursor:selNom.size?"pointer":"not-allowed",fontFamily:"'Inter',sans-serif"}}>
                  {emitiendoNom ? "Emitiendo..." : `Emitir ${selNom.size} factura${selNom.size===1?"":"s"} a ARCA`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {/* TAB MOVIMIENTOS — todo lo que se leyó de los resúmenes de este cliente,
          esté facturado o no. Es la base del motor financiero. */}
      {tab==="movimientos"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Resumen del período filtrado */}
          {movResumen&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
              {[
                ["Entró",       fmt(movResumen.entradas),  C.green],
                ["Salió",       fmt(movResumen.salidas),   C.danger],
                ["Facturado",   fmt(movResumen.facturado), C.ink],
                ["Sin facturar",fmt(movResumen.pendiente), C.warning],
              ].map(([k,v,col])=>(
                <div key={k} style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px"}}>
                  <div style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5}}>{k}</div>
                  <div style={{fontSize:17,fontWeight:800,color:col}}>{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
            {[["Desde",movDesde,setMovDesde],["Hasta",movHasta,setMovHasta]].map(([lab,val,set])=>(
              <div key={lab} style={{display:"flex",flexDirection:"column",gap:3}}>
                <label style={{fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{lab}</label>
                <input type="date" value={val} onChange={e=>set(e.target.value)}
                  style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 9px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
              </div>
            ))}
            {[
              ["Estado", movEstado, setMovEstado, [["todos","Todos"],["pendiente","Sin facturar"],["facturado","Facturado"]]],
              ["Fuente", movFuente, setMovFuente, [["todas","Todas"],["mercadopago","Mercado Pago"],["mercadopago_qr","MP Código QR"],["naranjax","NaranjaX"],["banco_entre_rios","Banco Entre Ríos"]]],
              ["Tipo",   movTipo,   setMovTipo,   [["todos","Todos"],["entrada","Entradas"],["salida","Salidas"],["revision","A revisar"]]],
            ].map(([lab,val,set,opts])=>(
              <div key={lab} style={{display:"flex",flexDirection:"column",gap:3}}>
                <label style={{fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{lab}</label>
                <select value={val} onChange={e=>set(e.target.value)}
                  style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 9px",fontSize:11.5,color:C.ink,fontFamily:"'Inter',sans-serif",cursor:"pointer",outline:"none"}}>
                  {opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
            <div style={{marginLeft:"auto",fontSize:11,color:C.inkDim}}>
              {movCargando ? "Cargando..." : `${movLista.length} movimiento${movLista.length===1?"":"s"}`}
            </div>
          </div>

          {/* Lista */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"6px 0",overflowX:"auto"}}>
            {movLista.length===0 ? (
              <div style={{padding:"36px 20px",textAlign:"center"}}>
                <div style={{fontSize:12.5,color:C.inkMid,marginBottom:4}}>
                  {movCargando ? "Cargando movimientos..." : "No hay movimientos en este período"}
                </div>
                {!movCargando&&(
                  <div style={{fontSize:11,color:C.inkDim}}>
                    Se guardan automáticamente cada vez que se procesa un resumen.
                  </div>
                )}
              </div>
            ) : (
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {["Fecha","Descripción","Fuente","Importe","Estado"].map((h,i)=>(
                      <th key={h} style={{textAlign:i>=3?"right":"left",padding:"10px 14px",fontSize:9.5,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movLista.map(m=>(
                    <tr key={m.id} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:"9px 14px",color:C.inkMid,whiteSpace:"nowrap"}}>{fmtF(m.fecha)}</td>
                      <td style={{padding:"9px 14px",color:C.ink}}>
                        {m.descripcion||m.concepto||"—"}
                        {m.medio&&<span style={{color:C.inkDim,fontSize:10.5}}> · {m.medio}</span>}
                      </td>
                      <td style={{padding:"9px 14px",color:C.inkDim,fontSize:11}}>{m.fuente}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:Number(m.valor)>=0?C.green:C.danger,whiteSpace:"nowrap"}}>
                        {fmt(Math.abs(Number(m.valor)))}
                      </td>
                      <td style={{padding:"9px 14px",textAlign:"right"}}>
                        <span style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,whiteSpace:"nowrap",
                          background: m.estado==="facturado"?"rgba(61,170,138,0.15)":"rgba(254,188,46,0.13)",
                          color:      m.estado==="facturado"?C.green:C.warning}}>
                          {m.estado==="facturado" ? (m.nro_comprobante?`Factura ${m.nro_comprobante}`:"Facturado") : "Sin facturar"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {/* TAB MEDIOS DE COBRO — qué lee el sistema hoy y cómo pedir uno nuevo */}
      {tab==="lectores"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4}}>Medios de cobro que leemos</div>
          <div style={{fontSize:11,color:C.inkDim,marginBottom:16,lineHeight:1.6}}>
            Estos son los resúmenes en PDF que VenciApp reconoce y procesa automáticamente.
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10,marginBottom:24}}>
            {lectores.map(l=>(
              <div key={l.id} style={{background:"rgba(61,170,138,0.06)",border:`1px solid rgba(61,170,138,0.25)`,borderRadius:8,padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
                  <span style={{color:C.green,fontSize:12}}>✓</span>
                  <span style={{fontSize:12.5,fontWeight:700,color:C.ink}}>{l.nombre}</span>
                </div>
                <div style={{fontSize:10.5,color:C.inkDim}}>{l.detalle}</div>
              </div>
            ))}
          </div>

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:18}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4}}>¿Cobrás por otro medio?</div>
            <div style={{fontSize:11,color:C.inkDim,marginBottom:14,lineHeight:1.6}}>
              Subí un resumen en PDF y lo revisamos. Te avisamos si podemos sumarlo.
            </div>

            {pedidoEnviado ? (
              <div style={{background:"rgba(61,170,138,0.08)",border:`1px solid rgba(61,170,138,0.3)`,borderRadius:8,padding:"14px 16px"}}>
                <div style={{fontSize:12.5,fontWeight:700,color:C.green,marginBottom:3}}>✓ Recibimos tu pedido</div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:10}}>Lo vamos a revisar y te avisamos si podemos sumarlo.</div>
                <button onClick={()=>setPedidoEnviado(false)}
                  style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 12px",fontSize:11,color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Pedir otro medio
                </button>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:520}}>
                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Medio de cobro *</label>
                  <input value={medioPedido} onChange={e=>setMedioPedido(e.target.value)}
                    placeholder="Personal Pay, Ualá, Banco Nación..."
                    style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 11px",fontSize:12,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.green}
                    onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>

                <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9,padding:"16px",border:`2px dashed ${archivoPedido?C.green:C.border}`,borderRadius:8,cursor:"pointer",background:archivoPedido?"rgba(61,170,138,0.05)":"transparent"}}>
                  <input type="file" accept=".pdf" style={{display:"none"}}
                    onChange={e=>{ if(e.target.files[0]) setArchivoPedido(e.target.files[0]); }}/>
                  <span style={{fontSize:18}}>{archivoPedido?"✅":"📎"}</span>
                  <span style={{fontSize:12,color:archivoPedido?C.green:C.inkMid,fontWeight:archivoPedido?600:400}}>
                    {archivoPedido ? archivoPedido.name : "Adjuntar el resumen en PDF"}
                  </span>
                </label>

                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Comentario (opcional)</label>
                  <textarea value={comentarioPedido} onChange={e=>setComentarioPedido(e.target.value)}
                    rows={2} placeholder="Algo que nos sirva saber sobre este resumen"
                    style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 11px",fontSize:12,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none",resize:"vertical"}}
                    onFocus={e=>e.target.style.borderColor=C.green}
                    onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>

                <div style={{fontSize:10,color:C.inkDim,lineHeight:1.6}}>
                  El resumen se envía únicamente al equipo de VenciApp para configurar la lectura. No se guarda en el sistema.
                </div>

                <button onClick={enviarPedidoLector} disabled={enviandoPedido||!medioPedido.trim()}
                  style={{alignSelf:"flex-start",background:medioPedido.trim()?C.green:"rgba(61,170,138,0.4)",border:"none",color:"#fff",borderRadius:8,padding:"10px 20px",fontSize:12.5,fontWeight:700,cursor:medioPedido.trim()?"pointer":"not-allowed",fontFamily:"'Inter',sans-serif"}}>
                  {enviandoPedido ? "Enviando..." : "Enviar pedido"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* TAB FACTURACIÓN MP */}
      {tab==="facturacion"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px",position:"relative"}}>


          {/* Selector manual de fuente — aparece solo si la detección
              automática no reconoció el formato del PDF */}
          {/* Aviso de verificación de CUIT */}
          {verificacionCuit&&verificacionCuit.coincide===true&&(
            <div style={{background:C.verdeDim,border:`1px solid ${C.green}`,borderRadius:8,padding:"12px 14px",marginBottom:12,fontSize:12,color:C.green,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:16}}>✅</span>
              <span>PDF de <strong>{verificacionCuit.nombrePDF||"este titular"}</strong> — CUIT {verificacionCuit.cuitPDF} coincide con el cliente seleccionado.</span>
            </div>
          )}
          {verificacionCuit&&verificacionCuit.coincide===false&&(
            <div style={{background:C.dangerBg,border:`2px solid ${C.danger}`,borderRadius:8,padding:"14px 16px",marginBottom:12}}>
              <div style={{fontWeight:800,marginBottom:6,fontSize:14,color:C.danger}}>⚠️ ATENCIÓN — Este PDF no es de este cliente</div>
              <div style={{fontSize:12,color:C.danger,marginBottom:4}}>El PDF pertenece al CUIT <strong>{verificacionCuit.cuitPDF}</strong>{verificacionCuit.nombrePDF?` (${verificacionCuit.nombrePDF})`:""}</div>
              <div style={{fontSize:11,color:C.inkMid}}>El cliente seleccionado tiene un CUIT diferente. Verificá antes de emitir.</div>
            </div>
          )}
          {verificacionCuit&&verificacionCuit.coincide===null&&verificacionCuit.cuitPDF&&(
            <div style={{background:C.warningBg,border:`1px solid ${C.warning}`,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:11,color:C.warning}}>
              <span style={{fontSize:14}}>⚠</span> CUIT detectado en el PDF: <strong>{verificacionCuit.cuitPDF}</strong>. Verificá que corresponda al cliente seleccionado.
            </div>
          )}

          {pidiendoFuente&&(
            <div style={{background:C.warningBg,border:`1px solid ${C.warning}44`,borderRadius:10,padding:"16px 18px",marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:C.ink,marginBottom:4}}>No reconocimos el formato de este PDF</div>
              <div style={{fontSize:11,color:C.inkDim,marginBottom:12,lineHeight:1.6}}>Elegí manualmente de qué billetera o banco es, así lo podemos procesar igual.</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Btn onClick={()=>{setPidiendoFuente(false);procesarPDFReal(archivoPendiente,"mercadopago");}} variant="ghost">Mercado Pago</Btn>
                <Btn onClick={()=>{setPidiendoFuente(false);procesarPDFReal(archivoPendiente,"mercadopago_qr");}} variant="ghost">MP Código QR</Btn>
                <Btn onClick={()=>{setPidiendoFuente(false);procesarPDFReal(archivoPendiente,"naranjax");}} variant="ghost">NaranjaX</Btn>
                <Btn onClick={()=>{setPidiendoFuente(false);procesarPDFReal(archivoPendiente,"banco_entre_rios");}} variant="ghost">Banco Entre Ríos</Btn>
                <Btn onClick={()=>setPidiendoFuente(false)} variant="ghost">Cancelar</Btn>
              </div>
            </div>
          )}

          {paso==="inicio"&&(
            <div>
              <div style={{fontSize:12,color:C.inkDim,marginBottom:16,lineHeight:1.7,paddingRight:110}}>
                Subí el PDF de <strong style={{color:C.ink}}>{cliente.nombre.split(" ")[0]}</strong>. La IA clasifica los cobros automáticamente y los prepara para emitir a ARCA como Factura {tipoFact} · Consumidor Final.
              </div>
              <label style={{display:"block",border:`2px dashed ${C.green+"44"}`,borderRadius:8,padding:"32px",textAlign:"center",cursor:"pointer",background:C.bg,transition:"all 0.2s",marginBottom:14}}
                onMouseEnter={e=>e.currentTarget.style.background=C.greenDim}
                onMouseLeave={e=>e.currentTarget.style.background=C.bg}>
                <input type="file" accept=".pdf" onChange={e=>{const f=e.target.files?.[0];if(f)procesarPDFReal(f);}} style={{display:"none"}}/>
                <div style={{fontSize:32,marginBottom:10}}>📄</div>
                <div style={{fontSize:14,fontWeight:600,color:C.ink,marginBottom:4}}>Subir PDF</div>
                <div style={{fontSize:11,color:C.inkDim,marginBottom:14}}>Resumen de cuenta en pesos</div>
                <span style={{background:C.green,color:"#0A1219",padding:"7px 18px",borderRadius:6,fontSize:12,fontWeight:700}}>📤 Seleccionar PDF</span>

              </label>

              {/* Qué resúmenes leemos y cómo pedir uno nuevo — fuera del label
                  para que el botón no dispare el selector de archivos */}
              {lectores.length>0&&(
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"16px",marginBottom:14,textAlign:"center"}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:11}}>
                    Resúmenes que leemos
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:15,justifyContent:"center"}}>
                    {lectores.map(l=>(
                      <span key={l.id} style={{display:"inline-flex",alignItems:"center",gap:5,background:"rgba(61,170,138,0.12)",border:`1px solid rgba(61,170,138,0.4)`,borderRadius:20,padding:"5px 13px",fontSize:11.5,fontWeight:600,color:C.green}}>
                        <span style={{fontSize:10}}>✓</span>{l.nombre}
                      </span>
                    ))}
                  </div>
                  <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:C.ink,marginBottom:3}}>¿Cobrás por otro medio?</div>
                    <div style={{fontSize:11,color:C.inkDim,marginBottom:12}}>Subí un resumen y lo revisamos. Te avisamos si podemos sumarlo.</div>
                    <button onClick={()=>setTab("lectores")}
                      style={{background:C.green,border:"none",color:"#0A1219",borderRadius:7,padding:"9px 20px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Pedir un medio nuevo
                    </button>
                  </div>
                </div>
              )}


              <div style={{textAlign:"center",display:"flex",gap:8,justifyContent:"center"}}>
                <button onClick={procesarDemo} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 14px",fontSize:11,color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Usar datos de demo
                </button>
                <button onClick={()=>setPaso("manual")} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 14px",fontSize:11,color:C.green,cursor:"pointer",fontFamily:"'Inter',sans-serif",fontWeight:600}}>
                  ✍️ Facturar manualmente
                </button>
              </div>
            </div>
          )}

          {paso==="manual"&&(
            <FacturadorManual
              cliente={cliente}
              tipoFact={tipoFact}
              onVolver={()=>setPaso("inicio")}
              onEmitida={(factura)=>{
                setFacturas(prev=>[factura, ...prev]);
                setPaso("inicio");
                showToast(`Factura emitida — CAE ${factura.cae}`);
              }}
              showToast={showToast}
            />
          )}

          {paso==="procesando"&&(
            <div style={{textAlign:"center",padding:"40px 0"}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
                <div style={{width:44,height:44,border:`2px solid ${C.border}`,borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              </div>
              <div style={{fontSize:15,fontWeight:600,color:C.ink,marginBottom:6}}>Procesando PDF con IA</div>
              <div style={{fontSize:11,color:C.green,marginBottom:16,minHeight:16}}>{pasoTxt}</div>
              <div style={{background:C.bg,borderRadius:3,height:3,maxWidth:360,margin:"0 auto 6px",overflow:"hidden"}}>
                <div style={{height:"100%",background:C.green,borderRadius:3,width:`${progreso}%`,transition:"width 0.35s ease"}}/>
              </div>
              <div style={{fontSize:10,color:C.inkDim}}>{progreso}%</div>
            </div>
          )}

          {paso==="revision"&&(
            <>
              {/* Resumen */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:2,display:"flex",alignItems:"center",gap:8}}>
                    PDF procesado · {entradasCnt} cobros encontrados
                    {fuenteDetectada&&(
                      <span style={{fontSize:9,fontWeight:700,color:C.green,background:C.greenDim,padding:"2px 8px",borderRadius:20,textTransform:"uppercase",letterSpacing:"0.04em"}}>
                        {fuenteDetectada==="naranjax"?"NaranjaX":fuenteDetectada==="mercadopago_qr"?"MP Código QR":fuenteDetectada==="banco_entre_rios"?"Banco Entre Ríos":"Mercado Pago"}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:C.inkDim}}>Seleccioná cuáles facturar y enviá a ARCA</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {[["entradas",`Cobros (${entradasCnt})`,C.success],["salidas",`Salidas (${salidasCnt})`,C.danger],["todos","Todos",C.inkMid]].map(([k,l,c])=>(
                    <button key={k} onClick={()=>setFiltro(k)} style={{padding:"4px 10px",borderRadius:4,border:`1px solid ${filtro===k?c:C.border}`,background:filtro===k?`${c}18`:"transparent",color:filtro===k?c:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif",fontWeight:filtro===k?700:400}}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Filtro por rango de fechas */}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                <span style={{fontSize:10,color:C.inkDim,fontWeight:600}}>Filtrar por fecha:</span>
                <input type="date" value={fechaDesde} onChange={e=>setFechaDesde(e.target.value)}
                  style={{padding:"5px 8px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,fontSize:11,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                <span style={{fontSize:10,color:C.inkDim}}>hasta</span>
                <input type="date" value={fechaHasta} onChange={e=>setFechaHasta(e.target.value)}
                  style={{padding:"5px 8px",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:5,fontSize:11,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                {(fechaDesde||fechaHasta)&&(
                  <button onClick={()=>{setFechaDesde("");setFechaHasta("");}} style={{padding:"5px 9px",borderRadius:5,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>✕ Limpiar</button>
                )}
              </div>

              {/* Tabla movimientos */}
              <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:14}}>
                <div style={{display:"flex",gap:4,padding:"6px 8px",background:C.bg,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap",alignItems:"center"}}>
                  <button onClick={()=>setSel(new Set(movimientos.filter(m=>m.tipo==="entrada"&&(!fechaDesde||m.fecha>=fechaDesde)&&(!fechaHasta||m.fecha<=fechaHasta)).map(m=>m.id)))} style={{padding:"3px 8px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.green,fontFamily:"'Inter',sans-serif"}}>☑ Todos los cobros</button>
                  <button onClick={()=>setSel(new Set())} style={{padding:"3px 8px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>☐ Ninguno</button>
                  <div style={{width:1,height:14,background:C.border,margin:"0 4px"}}/>
                  {/* Asignación de categoría POR TANDAS: se elige una categoría y se
                      aplica sólo a los movimientos tildados en ese momento. Después
                      el selector se vacía, así se puede tildar otro grupo y darle una
                      categoría distinta — o la misma, que antes no se podía porque el
                      select conservaba el valor y no volvía a disparar el cambio. */}
                  <select
                    value={categoriaGlobal}
                    onChange={e=>{
                      const cat = e.target.value;
                      setCategoriaGlobal(cat);
                      // Se aplica en el acto a los tildados, como fue siempre.
                      // El botón de al lado sirve para volver a aplicar la MISMA
                      // categoría a otro grupo (ahí el select no cambia de valor
                      // y por eso no dispara este onChange).
                      if (cat && sel.size) {
                        const nuevas = {...categoriaPorMov};
                        movimientos.filter(m=>sel.has(m.id)).forEach(m=>{ nuevas[m.id] = cat; });
                        setCategoriaPorMov(nuevas);
                      }
                    }}
                    style={{padding:"3px 6px",borderRadius:3,border:`1px solid ${categoriaGlobal?C.green:C.border}`,background:categoriaGlobal?C.greenDim:"transparent",fontSize:10,cursor:"pointer",color:categoriaGlobal?C.green:C.inkMid,fontFamily:"'Inter',sans-serif",maxWidth:200}}>
                    <option value="">Elegí una categoría...</option>
                    {CATEGORIAS_PRODUCTO.map(cat=>(
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    disabled={!categoriaGlobal||sel.size===0}
                    onClick={()=>{
                      const nuevas={...categoriaPorMov};
                      movimientos.filter(m=>sel.has(m.id)).forEach(m=>{ nuevas[m.id]=categoriaGlobal; });
                      setCategoriaPorMov(nuevas);
                      // No se vacía el select ni se destilda nada: el usuario
                      // sigue viendo qué categoría está usando y qué tiene
                      // seleccionado, listo para emitir.
                    }}
                    style={{padding:"3px 8px",borderRadius:3,
                      border:`1px solid ${(categoriaGlobal&&sel.size)?C.green:C.border}`,
                      background:(categoriaGlobal&&sel.size)?C.greenDim:"transparent",
                      fontSize:10,cursor:(categoriaGlobal&&sel.size)?"pointer":"not-allowed",
                      color:(categoriaGlobal&&sel.size)?C.green:C.inkDim,fontFamily:"'Inter',sans-serif"}}>
                    Volver a aplicar a los {sel.size} tildados
                  </button>
                  {Object.keys(categoriaPorMov).length>0&&(
                    <>
                      <span style={{fontSize:10,color:C.inkDim}}>
                        {Object.keys(categoriaPorMov).length} con categoría
                      </span>
                      <button onClick={()=>{setCategoriaGlobal("");setCategoriaPorMov({});}}
                        style={{padding:"3px 6px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",fontSize:10,cursor:"pointer",color:C.danger,fontFamily:"'Inter',sans-serif"}}>
                        ✕ Borrar categorías
                      </button>
                    </>
                  )}
                </div>
                <div style={{maxHeight:280,overflowY:"auto"}}>
                  {movimientos.filter(m=>{
                    if(filtro==="entradas"&&m.tipo!=="entrada") return false;
                    if(filtro==="salidas"&&m.tipo!=="salida") return false;
                    if(fechaDesde&&m.fecha<fechaDesde) return false;
                    if(fechaHasta&&m.fecha>fechaHasta) return false;
                    return true;
                  }).map((m,i)=>{
                    const yaFacturado=idsYaFacturados.has(m.id);
                    const isSel=sel.has(m.id);
                    const isSelectable=m.tipo==="entrada" && !yaFacturado;
                    const categoriaElegida=categoriaPorMov[m.id];
                    const desplegadoAbierto=movAbierto===m.id;
                    return (
                      <div key={m.id} style={{borderBottom:`1px solid ${C.border}`,background:yaFacturado?C.bg:isSel?C.greenDim:i%2===0?C.bgCard:C.bg,opacity:yaFacturado?0.6:1}}>
                        <div onClick={()=>isSelectable&&setSel(prev=>{const n=new Set(prev);isSel?n.delete(m.id):n.add(m.id);return n;})}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",fontSize:12,cursor:isSelectable?"pointer":"default",transition:"background 0.1s"}}>
                          <div style={{width:16,height:16,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {yaFacturado&&<span style={{fontSize:13,color:C.green}}>✓</span>}
                            {!yaFacturado&&isSelectable&&<div style={{width:15,height:15,borderRadius:3,border:`2px solid ${isSel?C.green:C.borderMid}`,background:isSel?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>{isSel&&<span style={{color:"#0A1219",fontSize:10}}>✓</span>}</div>}
                            {!yaFacturado&&!isSelectable&&<span style={{fontSize:10,color:C.danger}}>↓</span>}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.ink}}>{m.descripcion}</div>
                            <div style={{fontSize:9,color:C.inkDim,marginTop:1,display:"flex",gap:6,alignItems:"center"}}>
                              <span>{fmtF(m.fecha)}</span>
                              {categoriaElegida&&<span style={{color:C.green,fontWeight:600}}>· {categoriaElegida}</span>}
                              {yaFacturado&&<span style={{color:C.green,fontWeight:700}}>· Ya facturado</span>}
                            </div>
                          </div>
                          {isSelectable&&(
                            <button onClick={(e)=>{e.stopPropagation();setMovAbierto(desplegadoAbierto?null:m.id);}}
                              title="¿Qué se vendió?"
                              style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 7px",fontSize:10,cursor:"pointer",color:categoriaElegida?C.green:C.inkDim,flexShrink:0}}>
                              {desplegadoAbierto?"▲":"▼"}
                            </button>
                          )}
                          <span style={{fontWeight:700,flexShrink:0,color:m.valor>=0?C.success:C.danger}}>{m.valor>=0?"+":""}{fmt(m.valor)}</span>
                        </div>
                        {desplegadoAbierto&&(
                          <div onClick={e=>e.stopPropagation()} style={{padding:"10px 14px 12px 40px",background:C.bg,borderTop:`1px solid ${C.border}`}}>
                            <div style={{fontSize:10,color:C.inkDim,marginBottom:6}}>¿Qué se vendió? (opcional)</div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                              {CATEGORIAS_PRODUCTO.map(cat=>(
                                <button key={cat} onClick={()=>{setCategoriaPorMov(prev=>({...prev,[m.id]:cat}));setMovAbierto(null);}}
                                  style={{padding:"4px 9px",borderRadius:4,border:`1px solid ${categoriaElegida===cat?C.green:C.border}`,background:categoriaElegida===cat?C.greenDim:"transparent",color:categoriaElegida===cat?C.green:C.inkMid,fontSize:10,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                                  {cat}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Barra acción */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:C.bg,borderRadius:8,border:`1px solid ${selCnt>0?C.green+"44":C.border}`}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{selCnt>0?`${selCnt} cobro${selCnt>1?"s":""} seleccionado${selCnt>1?"s":""}`:""}</div>
                  {selCnt>0&&<div style={{fontSize:11,color:C.inkMid}}>Total: <strong style={{color:C.green}}>{fmt(totalSel)}</strong> · Factura {tipoFact} · Consumidor Final</div>}
                  {selSinCategoria>0&&(
                    <div style={{fontSize:10,color:C.warning,marginTop:3}}>
                      ⚠ {selSinCategoria} sin categoría — {selSinCategoria===1?"saldrá":"saldrán"} como "Venta de mercadería"
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <Btn onClick={reiniciarFact} variant="ghost" small>← Volver</Btn>
                  <Btn onClick={()=>selCnt>0&&setModalConf(true)} disabled={selCnt===0} variant="green">
                    ⚡ Emitir {selCnt||""} factura{selCnt!==1?"s":""} a ARCA
                  </Btn>
                </div>
              </div>
            </>
          )}

          {paso==="emitidas"&&(
            <div>
              <div style={{textAlign:"center",padding:"24px 0 18px"}}>
                <div style={{fontSize:40,marginBottom:10}}>✅</div>
                <div style={{fontSize:17,fontWeight:700,color:C.success,marginBottom:4}}>{facturasOk.length} facturas emitidas</div>
                <div style={{fontSize:11,color:C.inkDim}}>{fmt(facturasOk.reduce((s,f)=>s+f.monto,0))} facturado para {cliente.nombre.split(" ")[0]}</div>
              </div>
              <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:16,maxHeight:200,overflowY:"auto"}}>
                {facturasOk.map((f,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"9px 14px",borderBottom:`1px solid ${C.border}`,fontSize:11,background:i%2===0?C.bgCard:C.bg}}>
                    <div>
                      <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:280,color:C.ink}}>{f.descripcion}</div>
                      <div style={{fontSize:9,fontFamily:"monospace",color:C.success,marginTop:1}}>CAE {f.cae?.slice(0,12)}...</div>
                    </div>
                    <span style={{fontWeight:700,color:C.success,flexShrink:0}}>{fmt(f.monto)}</span>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn onClick={reiniciarFact} variant="ghost" style={{flex:1,justifyContent:"center"}}>+ Nuevo PDF</Btn>
                <Btn onClick={()=>setTab("historial")} variant="success" style={{flex:1,justifyContent:"center"}}>Ver historial →</Btn>
              </div>
            </div>
          )}

          {modalConf&&(
            <ModalEnvioMasivo
              movimientos={movsParaFact}
              cliente={cliente}
              onClose={()=>setModalConf(false)}
              onConfirmar={onEmitir}
            />
          )}
        </div>
      )}

      {/* TAB HISTORIAL */}
      {tab==="historial"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 18px",background:C.bg,borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:"0.07em",textTransform:"uppercase"}}>Facturas emitidas</span>
            <span style={{fontSize:11,color:C.inkDim}}>{facturas.length} total</span>
          </div>

          {/* ── ENVIAR POR MAIL ──────────────────────────────────────────────
              Sólo las nominadas se pueden mandar: son las que salieron a
              nombre de una persona. Las de los resúmenes van a Consumidor
              Final y no tienen a quién mandarse. */}
          {(()=>{
            const mandables = facturas.filter(f => f.doc_nro && f.resultado!=="rechazada");
            if (!mandables.length) return null;
            const sinEnviar = mandables.filter(f => !f.email_enviado_en);
            const sinMail   = !fichaActual.email;
            const tildadas  = mandables.filter(f => selMail.has(f.id));
            return (
              <div style={{padding:"11px 18px",background:tildadas.length?"rgba(61,170,138,0.07)":C.bg,borderBottom:`1px solid ${C.border}`,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:190,fontSize:11,color:sinMail?C.warning:C.inkMid,lineHeight:1.6}}>
                  {sinMail
                    ? <>Para enviarlas por mail, cargale el email a <strong style={{color:C.ink}}>{cliente?.nombre}</strong> arriba, en Datos del cliente.</>
                    : tildadas.length
                      ? <><strong style={{color:C.ink}}>{tildadas.length} tildada{tildadas.length===1?"":"s"}</strong> · van en un solo mail a {fichaActual.email}</>
                      : <>{mandables.length} factura{mandables.length===1?"":"s"} a nombre de una persona
                          {sinEnviar.length>0 && <> · <strong style={{color:C.ink}}>{sinEnviar.length} sin enviar</strong></>}</>}
                </div>
                <div style={{display:"flex",gap:7,flexShrink:0,flexWrap:"wrap"}}>
                  {!sinMail&&sinEnviar.length>0&&!tildadas.length&&(
                    <button onClick={()=>setSelMail(new Set(sinEnviar.map(f=>f.id)))}
                      style={{background:"transparent",border:`1px solid ${C.border}`,color:C.inkMid,borderRadius:7,padding:"8px 13px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Tildar las {sinEnviar.length} sin enviar
                    </button>
                  )}
                  {tildadas.length>0&&(
                    <button onClick={()=>setSelMail(new Set())}
                      style={{background:"transparent",border:`1px solid ${C.border}`,color:C.inkMid,borderRadius:7,padding:"8px 13px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Destildar
                    </button>
                  )}
                  <button onClick={()=>enviarFacturasPorMailNom(tildadas.map(f=>f.id), "")}
                    disabled={enviandoMailNom||sinMail||!tildadas.length}
                    title={sinMail ? "El cliente no tiene email cargado"
                         : !tildadas.length ? "Tildá las facturas que querés enviar"
                         : `Enviar ${tildadas.length} a ${fichaActual.email}`}
                    style={{background:(tildadas.length&&!sinMail)?C.green:"rgba(58,168,130,0.25)",border:"none",color:"#0A1219",borderRadius:7,padding:"8px 15px",fontSize:11.5,fontWeight:700,cursor:(enviandoMailNom||sinMail||!tildadas.length)?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
                    {enviandoMailNom ? "Enviando..." : `✉️ Enviar${tildadas.length?` ${tildadas.length}`:""} por mail`}
                  </button>
                </div>
              </div>
            );
          })()}
          {facturas.length===0?(
            <div style={{padding:"32px",textAlign:"center",color:C.inkDim,fontSize:12}}>
              No hay facturas emitidas todavía.<br/>
              <button onClick={()=>setTab("facturacion")} style={{marginTop:12,background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 14px",fontSize:11,color:C.green,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Subir primer PDF →
              </button>
            </div>
          ):(
            facturas.map((f,i)=>{
              const rechazada = f.resultado === "rechazada";
              return (
              <div key={f.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,background:rechazada?C.dangerBg:selMail.has(f.id)?"rgba(58,168,130,0.07)":i%2===0?C.bgCard:C.bg}}>
                {/* Tilde sólo en las nominadas: son las únicas que se mandan.
                    Las demás llevan un hueco del mismo ancho para que las
                    filas queden alineadas entre sí. Si no hay ninguna
                    nominada, no se reserva nada y el historial se ve igual
                    que antes. */}
                {facturas.some(x => x.doc_nro && x.resultado!=="rechazada")&&(
                  f.doc_nro&&!rechazada ? (
                    <input type="checkbox" checked={selMail.has(f.id)}
                      disabled={!fichaActual.email}
                      title={fichaActual.email ? "Tildar para enviar por mail" : "El cliente no tiene email cargado"}
                      onChange={()=>setSelMail(p=>{const s=new Set(p); s.has(f.id)?s.delete(f.id):s.add(f.id); return s;})}
                      style={{width:14,height:14,accentColor:C.green,cursor:fichaActual.email?"pointer":"not-allowed",marginRight:12,flexShrink:0}}/>
                  ) : (
                    <span style={{width:14,marginRight:12,flexShrink:0}}/>
                  )
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:500,color:rechazada?C.danger:C.ink,marginBottom:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {rechazada
                      ? <><span>⚠ Rechazada</span><span style={{fontSize:9,color:C.danger,fontWeight:400}}>— ARCA no la aprobó</span></>
                      : `Fct ${f.tipo_comprobante===11?"C":"B"} N°${f.nro_comprobante}`
                    }
                    {/* Las nominadas salieron a nombre de una persona, no a
                        Consumidor Final: conviene verlo sin abrir el PDF. */}
                    {f.doc_nro&&(
                      <span title={`DNI ${f.doc_nro}`}
                        style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:20,background:"rgba(61,170,138,0.13)",color:C.green,whiteSpace:"nowrap",maxWidth:170,overflow:"hidden",textOverflow:"ellipsis"}}>
                        A nombre de {f.nombre_receptor || `DNI ${f.doc_nro}`}
                      </span>
                    )}
                    {/* Si ya se le mandó al cliente, se ve acá y no se manda
                        dos veces sin querer. */}
                    {f.email_enviado_en&&(
                      <span title={`Enviada a ${f.email_enviado_a||"el cliente"} el ${fmtF(f.email_enviado_en)}`}
                        style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:20,background:"rgba(42,100,150,0.18)",color:C.inkMid,whiteSpace:"nowrap"}}>
                        ✉️ Enviada {fmtF(f.email_enviado_en)}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:10,color:C.inkDim,maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.descripcion||"—"}</div>
                  {!rechazada && <div style={{fontSize:9,fontFamily:"monospace",color:C.success,marginTop:1}}>CAE {f.cae}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontWeight:700,color:rechazada?C.danger:C.success,fontSize:13}}>{fmt(parseFloat(f.monto))}</div>
                    <div style={{fontSize:9,color:C.inkDim,marginTop:2}}>{fmtF(f.fecha_emision)}</div>
                  </div>
                  {rechazada ? (
                    <button onClick={async()=>{
                      try {
                        // Consultar cuál es la próxima fecha disponible para
                        // este cliente — día siguiente a la última factura
                        // aprobada, para no pisar fechas ya usadas ni saltear
                        // días innecesariamente.
                        const respFecha = await authFetch(`${API_URL}/clientes/${cliente.id}/proxima-fecha-emision`);
                        const dataFecha = await respFecha.json();
                        const fechaReemision = dataFecha.ok ? dataFecha.data.proximaFecha : new Date().toISOString().split("T")[0];

                        const resp = await authFetch(`${API_URL}/arca/factura`,{
                          method:"POST",
                          headers:{"Content-Type":"application/json"},
                          body:JSON.stringify({
                            tipoComprobante: f.tipo_comprobante || 11,
                            concepto: f.concepto || 2,
                            monto: parseFloat(f.monto),
                            fechaMovimiento: fechaReemision,
                            clienteId: cliente.id,
                            descripcion: f.descripcion || "",
                            idMovimientoOrigen: f.id_movimiento_origen,
                          })
                        });
                        const data = await resp.json();
                        if (data.ok) {
                          showToast(`✅ Re-emitida con fecha ${fmtF(fechaReemision)}`);
                          authFetch(`${API_URL}/clientes/${cliente.id}/facturas`)
                            .then(r=>r.json()).then(d=>{ if(d.ok) setFacturas(d.data); });
                        } else {
                          showToast("❌ " + (data.error || "No se pudo re-emitir"));
                        }
                      } catch(e) {
                        showToast("❌ Error de conexión");
                      }
                    }}
                      title="Re-emitir con fecha de hoy"
                      style={{background:C.dangerBg,border:`1px solid ${C.danger}44`,borderRadius:6,padding:"6px 10px",fontSize:11,cursor:"pointer",color:C.danger,flexShrink:0,fontWeight:600}}>
                      🔄 Re-emitir
                    </button>
                  ) : (
                    <button onClick={async()=>{
                      // Una factura nominada (tiene DNI del receptor) se arma con
                      // el comprobante oficial de tres copias; las de los
                      // resúmenes PDF siguen saliendo por donde salieron siempre.
                      if (f.doc_nro) { abrirPDFNominada(f.id); return; }
                      const token=localStorage.getItem("venci_token");
                      const r=await fetch(`${API_URL}/facturas/${f.id}/pdf`,{headers:{Authorization:`Bearer ${token}`}});
                      const blob=await r.blob();
                      const url=URL.createObjectURL(blob);
                      window.open(url,"_blank");
                    }} title={f.doc_nro?"Ver comprobante (3 copias)":"Descargar PDF"}
                      style={{background:C.greenDim,border:`1px solid ${C.green}44`,borderRadius:6,padding:"6px 10px",fontSize:12,cursor:"pointer",color:C.green,flexShrink:0}}>
                      📥
                    </button>
                  )}
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {/* TAB CONFIGURACIÓN ARCA — onboarding de certificado por cliente */}
      {tab==="arca"&&(
        <ConfiguracionARCA
          cliente={cliente}
          certEstado={certEstado}
          cargandoCert={cargandoCert}
          onActualizar={cargarEstadoCert}
          showToast={showToast}
        />
      )}

      {/* TAB CÓMO EMITIR — guía paso a paso, ahora integrada como tab en vez de modal flotante */}
      {tab==="exportar"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"28px 24px"}}>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:6}}>📥 Exportar facturas</div>
            <div style={{fontSize:13,color:C.inkDim,lineHeight:1.6}}>Elegí el rango de fechas y descargá el listado de facturas emitidas en ese período.</div>
          </div>

          {/* Selector de fechas */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkDim,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>Desde</label>
              <input type="date" id="exp-desde"
                defaultValue={new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]}
                style={{width:"100%",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",fontSize:13,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}
              />
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkDim,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>Hasta</label>
              <input type="date" id="exp-hasta"
                defaultValue={new Date().toISOString().split("T")[0]}
                style={{width:"100%",background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",fontSize:13,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}
              />
            </div>
          </div>

          {/* Botón de descarga */}
          <button onClick={async ()=>{
            const desde = document.getElementById("exp-desde").value;
            const hasta = document.getElementById("exp-hasta").value;
            if (!desde || !hasta) { showToast("Elegí las fechas primero"); return; }

            try {
              const token = localStorage.getItem("venci_token");
              const resp = await fetch(`${API_URL}/clientes/${cliente.id}/exportar-facturas?desde=${desde}&hasta=${hasta}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (!resp.ok) { showToast("Error al generar el reporte"); return; }
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `facturas_${cliente.nombre.replace(/ /g,"_")}_${desde}_${hasta}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              showToast("✅ Descarga iniciada");
            } catch(e) {
              showToast("Error al descargar");
            }
          }}
            style={{display:"flex",alignItems:"center",gap:8,background:C.green,color:"#fff",border:"none",borderRadius:8,padding:"12px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            📥 Descargar CSV
          </button>

          <div style={{marginTop:12,fontSize:11,color:C.inkDim}}>
            El archivo CSV se puede abrir directamente en Excel o Google Sheets. Incluye fecha, concepto, monto y CAE de cada factura.
          </div>
        </div>
      )}

      {tab==="ayuda"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          {/* Header con la diagonal de marca, igual al estilo del Login */}
          <div style={{background:"#0F2A4D",position:"relative",padding:"28px 28px 24px",overflow:"hidden"}}>
            <svg style={{position:"absolute",top:0,right:-2,height:"100%",width:60}} viewBox="0 0 60 200" preserveAspectRatio="none">
              <polygon points="26,0 60,0 60,200 0,200" fill="#3DAA8A"/>
              <polygon points="13,0 40,0 40,200 0,200" fill="#0F2A4D"/>
            </svg>
            <div style={{position:"relative",zIndex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:"#3DAA8A",letterSpacing:"0.1em",marginBottom:6}}>GUÍA PASO A PASO</div>
              <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Poppins',sans-serif"}}>Cómo emitir correctamente</div>
            </div>
          </div>

          <div style={{padding:"24px 28px"}}>
            {PASOS_GUIA.map((p,i)=>(
              <div key={i} style={{display:"flex",gap:16,paddingBottom:i<PASOS_GUIA.length-1?20:0,marginBottom:i<PASOS_GUIA.length-1?20:0,borderBottom:i<PASOS_GUIA.length-1?`1px solid ${C.border}`:"none"}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:C.greenDim,border:`1.5px solid ${C.green}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.green,flexShrink:0}}>
                  {p.n}
                </div>
                <div>
                  <div style={{fontSize:13.5,fontWeight:700,color:C.ink,marginBottom:4}}>{p.titulo}</div>
                  <div style={{fontSize:12.5,color:C.inkMid,lineHeight:1.7}}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

// ─── PANEL DE SUPER ADMIN — solo visible para el dueño de Venci ──────────────
const PanelAdmin = ({ onVolver }) => {
  const [contadores, setContadores] = useState([]);
  const [cargando, setCargando]     = useState(true);
  const [expandido, setExpandido]   = useState(null); // id del contador con drill-down abierto
  const [clientesPorContador, setClientesPorContador] = useState({}); // {contadorId: [clientes]}
  const [cargandoDetalle, setCargandoDetalle] = useState(null);

  useEffect(()=>{
    authFetch(`${API_URL}/admin/contadores`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setContadores(d.data); setCargando(false); })
      .catch(()=>setCargando(false));
  },[]);

  const [extendiendo, setExtendiendo] = useState(null); // id del contador que se está extendiendo

  // Le da N días más de acceso sin tocar la fecha de registro: se le pone
  // plan pago con vencimiento. Si ya tenía acceso vigente, se le suma.
  const extenderAcceso = async (id, nombre, dias=14) => {
    if (!window.confirm(`¿Darle ${dias} días más de acceso a ${nombre}?`)) return;
    setExtendiendo(id);
    try {
      const resp = await authFetch(`${API_URL}/admin/contadores/${id}/extender-acceso`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ dias })
      });
      const d = await resp.json();
      if (!d.ok) { alert("No se pudo extender: " + (d.error||"")); setExtendiendo(null); return; }
      setContadores(prev=>prev.map(x=>x.id===id
        ? {...x, plan:d.data.plan, plan_activo_hasta:d.data.plan_activo_hasta}
        : x));
      const hasta = new Date(d.data.plan_activo_hasta).toLocaleDateString("es-AR");
      alert(`Listo. ${nombre} tiene acceso hasta el ${hasta}.`);
    } catch(e) { alert("Error: " + e.message); }
    setExtendiendo(null);
  };

  const cambiarEstado = async (id, activoActual) => {
    await authFetch(`${API_URL}/admin/contadores/${id}/estado`, {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ activo: !activoActual })
    });
    setContadores(prev=>prev.map(c=>c.id===id?{...c,activo:!activoActual}:c));
  };

  // Drill-down — al tocar un contador, traemos el detalle de facturación
  // por cada uno de SUS clientes (útil para calcular % de facturación,
  // ver quién factura más, etc.)
  const toggleExpandido = async (contadorId) => {
    if (expandido === contadorId) {
      setExpandido(null);
      return;
    }
    setExpandido(contadorId);
    if (!clientesPorContador[contadorId]) {
      setCargandoDetalle(contadorId);
      const resp = await authFetch(`${API_URL}/admin/contadores/${contadorId}/clientes`);
      const data = await resp.json();
      if (data.ok) {
        setClientesPorContador(prev => ({ ...prev, [contadorId]: data.data }));
      }
      setCargandoDetalle(null);
    }
  };

  const totalFacturado = contadores.reduce((s,c)=>s+parseFloat(c.monto_total_facturado||0),0);
  const totalClientes  = contadores.reduce((s,c)=>s+parseInt(c.cantidad_clientes||0),0);
  const totalFacturas  = contadores.reduce((s,c)=>s+parseInt(c.cantidad_facturas||0),0);

  // Ciclos de facturación del mes actual
  const [ciclos, setCiclos] = useState([]);
  const [cargandoCiclos, setCargandoCiclos] = useState(true);
  const [pestanaAdmin, setPestanaAdmin] = useState("contadores"); // contadores | cobros | lectores
  const [solLectores, setSolLectores]   = useState([]); // medios de cobro pedidos por los clientes

  useEffect(()=>{
    authFetch(`${API_URL}/admin/solicitudes-lector`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setSolLectores(d.data); })
      .catch(()=>{});
  },[]);

  useEffect(()=>{
    authFetch(`${API_URL}/admin/ciclos`)
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setCiclos(d.data); setCargandoCiclos(false); })
      .catch(()=>setCargandoCiclos(false));
  },[]);

  const mesActual = new Date().toLocaleString('es-AR',{month:'long',year:'numeric'});
  const totalACobrarMes = ciclos.reduce((s,c)=>s+parseFloat(c.monto_a_cobrar||0),0);
  const totalCobradoMes = ciclos.filter(c=>c.estado==='pagado').reduce((s,c)=>s+parseFloat(c.monto_a_cobrar||0),0);
  const totalPendiente  = ciclos.filter(c=>c.estado==='pendiente').reduce((s,c)=>s+parseFloat(c.monto_a_cobrar||0),0);

  const marcarPagado = async (contadorId, periodo) => {
    await authFetch(`${API_URL}/admin/ciclos/pagar`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ contadorId, periodo })
    });
    setCiclos(prev=>prev.map(c=>c.contador_id===contadorId?{...c,estado:'pagado'}:c));
  };

  const activarPlan = async (contadorId, plan) => {
    await authFetch(`${API_URL}/admin/activar-plan`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ contadorId, plan, meses: 1 })
    });
    console.log(`✅ Plan ${plan} activado`);
    // Recargar contadores
    authFetch(`${API_URL}/admin/contadores/todos`)
      .then(r=>r.json()).then(d=>{ if(d.ok) setContadores(d.data); });
  };

  return (
    <div style={{animation:"fadeUp 0.3s ease"}}>
      <button onClick={onVolver}
        style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",color:C.inkMid,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:24,padding:"4px 0"}}
        onMouseEnter={e=>e.currentTarget.style.color=C.ink}
        onMouseLeave={e=>e.currentTarget.style.color=C.inkMid}>
        ← Volver a mi panel
      </button>

      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Super Admin</div>
        <h1 style={{fontSize:22,fontWeight:700,color:C.ink,marginBottom:4,letterSpacing:"-0.02em"}}>Panel de Control VenciApp</h1>
      </div>

      {/* Tabs admin */}
      <div style={{display:"flex",gap:4,marginBottom:20,background:C.bg,padding:4,borderRadius:8,border:`1px solid ${C.border}`}}>
        {[["contadores","👥 Contadores"],["cobros","💰 Cobros del mes"],["lectores","🏦 Lectores pedidos"]].map(([k,l])=>(
          <button key={k} onClick={()=>setPestanaAdmin(k)}
            style={{flex:1,padding:"8px 16px",borderRadius:6,border:"none",background:pestanaAdmin===k?C.bgCard:"transparent",color:pestanaAdmin===k?C.green:C.inkMid,fontSize:12,fontWeight:pestanaAdmin===k?700:500,cursor:"pointer",fontFamily:"'Inter',sans-serif",boxShadow:pestanaAdmin===k?`0 1px 4px rgba(0,0,0,0.1)`:"none"}}>
            {l}
          </button>
        ))}
      </div>

      {/* Stats globales — siempre visibles */}
      <div className="venci-stats" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
        {[
          {label:"Cuentas registradas", value:contadores.length},
          {label:"Clientes totales",    value:totalClientes},
          {label:"Facturas emitidas",   value:totalFacturas},
          {label:"Facturado total",     value:fmt(totalFacturado)},
        ].map((s,i)=>(
          <div key={i} style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px"}}>
            <div style={{fontSize:10,color:C.inkDim,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:20,fontWeight:700,color:C.green}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── PESTAÑA COBROS DEL MES ── */}
      {/* Medios de cobro que piden los clientes — ordenados por cuántos lo pidieron,
          para saber cuál lector conviene construir primero */}
      {pestanaAdmin==="lectores"&&(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4}}>Medios de cobro solicitados</div>
          <div style={{fontSize:11,color:C.inkDim,marginBottom:16}}>
            El resumen de cada pedido llega por mail. Acá se ve cuáles se repiten.
          </div>
          {solLectores.length===0 ? (
            <div style={{fontSize:12,color:C.inkDim,padding:"20px 0",textAlign:"center"}}>Todavía nadie pidió un medio nuevo.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {["Medio","Pedidos","Cuentas","Quiénes","Último"].map(h=>(
                    <th key={h} style={{textAlign:h==="Medio"||h==="Quiénes"?"left":"center",padding:"8px 6px",fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {solLectores.map((s,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"9px 6px",fontWeight:700,color:C.ink}}>{s.medio}</td>
                    <td style={{padding:"9px 6px",textAlign:"center",fontWeight:700,color:C.green}}>{s.pedidos}</td>
                    <td style={{padding:"9px 6px",textAlign:"center",color:C.inkMid}}>{s.contadores}</td>
                    <td style={{padding:"9px 6px",color:C.inkMid,fontSize:11}}>{(s.quienes||[]).filter(Boolean).join(", ")}</td>
                    <td style={{padding:"9px 6px",textAlign:"center",color:C.inkDim,fontSize:11}}>
                      {s.ultimo ? new Date(s.ultimo).toLocaleDateString("es-AR") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {pestanaAdmin==="cobros"&&(
        <div>
          {/* Stats del mes */}
          <div className="venci-admin-stats" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px"}}>
              <div style={{fontSize:10,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>A cobrar {mesActual}</div>
              <div style={{fontSize:20,fontWeight:700,color:C.green}}>{fmt(totalACobrarMes)}</div>
            </div>
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px"}}>
              <div style={{fontSize:10,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Ya cobrado</div>
              <div style={{fontSize:20,fontWeight:700,color:C.success}}>{fmt(totalCobradoMes)}</div>
            </div>
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px"}}>
              <div style={{fontSize:10,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Pendiente de cobro</div>
              <div style={{fontSize:20,fontWeight:700,color:C.warning}}>{fmt(totalPendiente)}</div>
            </div>
          </div>

          {/* Tabla de cobros */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 120px",padding:"10px 16px",background:C.bg,borderBottom:`1px solid ${C.border}`,fontSize:10,fontWeight:700,color:C.inkDim,letterSpacing:"0.05em",textTransform:"uppercase"}}>
              <span>Contador</span>
              <span>Facturado</span>
              <span>A cobrar (0.50%)</span>
              <span>Estado</span>
              <span>Acción</span>
            </div>
            {cargandoCiclos?(
              <div style={{padding:"32px",textAlign:"center",color:C.inkDim}}>Cargando...</div>
            ):ciclos.length===0?(
              <div style={{padding:"32px",textAlign:"center",color:C.inkDim}}>No hay actividad este mes todavía.</div>
            ):(
              ciclos.map((c,i)=>(
                <div key={c.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 120px",padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:12,background:i%2===0?C.bgCard:C.bg,alignItems:"center"}}>
                  <div>
                    <div style={{fontWeight:600,color:C.ink}}>{c.nombre}</div>
                    <div style={{fontSize:10,color:C.inkDim,marginTop:2}}>{c.email}</div>
                  </div>
                  <div style={{fontWeight:600,color:C.ink}}>{fmt(parseFloat(c.monto_facturado))}</div>
                  <div style={{fontWeight:700,color:C.green}}>{fmt(parseFloat(c.monto_a_cobrar))}</div>
                  <div>
                    {c.estado==="pagado"?(
                      <span style={{background:C.greenDim,color:C.green,padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700}}>✓ Pagado</span>
                    ):c.estado==="prueba_gratuita"?(
                      <span style={{background:"rgba(254,188,46,0.1)",color:C.warning,padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700}}>⏱ Prueba</span>
                    ):(
                      <span style={{background:"rgba(224,82,78,0.1)",color:C.danger,padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700}}>⚠ Pendiente</span>
                    )}
                  </div>
                  <div>
                    {c.estado!=="pagado"&&(
                      <button onClick={()=>marcarPagado(c.contador_id, c.periodo)}
                        style={{background:C.green,color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        ✓ Marcar pagado
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {cargando?(
        <div style={{textAlign:"center",padding:"60px",color:C.inkDim}}>Cargando...</div>
      ):(
        <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 100px",padding:"10px 16px",background:C.bg,borderBottom:`1px solid ${C.border}`,fontSize:10,fontWeight:700,color:C.inkDim,letterSpacing:"0.05em",textTransform:"uppercase"}}>
            <span>Nombre / Email</span>
            <span>Tipo</span>
            <span>Clientes</span>
            <span>Facturas</span>
            <span>Facturado</span>
            <span>Estado</span>
          </div>
          {contadores.length===0?(
            <div style={{padding:32,textAlign:"center",color:C.inkDim,fontSize:12}}>No hay cuentas registradas todavía.</div>
          ):(
            contadores.map((c,i)=>{
              const abierto = expandido === c.id;
              const clientesDeEste = clientesPorContador[c.id] || [];
              return (
              <div key={c.id}>
                <div onClick={()=>toggleExpandido(c.id)}
                  style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 100px",padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:12,background:abierto?C.greenDim:i%2===0?C.bgCard:C.bg,alignItems:"center",cursor:"pointer",transition:"background 0.15s"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:10,color:C.inkDim,transition:"transform 0.15s",transform:abierto?"rotate(90deg)":"rotate(0deg)",display:"inline-block"}}>▶</span>
                    <div>
                      <div style={{fontWeight:600,color:C.ink}}>{c.nombre}</div>
                      <div style={{fontSize:10,color:C.inkDim,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                        {c.email} · {fmtF(c.creado_en)}
                        {/* Celular con el que se registró. Si se puede armar el
                            link, va como botón de WhatsApp; si no, igual se
                            muestra el número para copiarlo. */}
                        {c.telefono ? (()=>{
                          const wa = linkWhatsApp(c.telefono);
                          return wa ? (
                            <a href={wa} target="_blank" rel="noopener noreferrer"
                              onClick={e=>e.stopPropagation()}
                              title={`Escribirle por WhatsApp al ${c.telefono}`}
                              style={{background:"rgba(58,168,130,0.15)",color:C.green,padding:"1px 7px",borderRadius:4,fontSize:9,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>
                              💬 {c.telefono}
                            </a>
                          ) : (
                            <span title="No se pudo armar el link — copiá el número"
                              style={{background:C.bg,color:C.inkMid,padding:"1px 7px",borderRadius:4,fontSize:9,fontWeight:600,whiteSpace:"nowrap"}}>
                              📞 {c.telefono}
                            </span>
                          );
                        })() : (
                          <span title="No dejó celular al registrarse"
                            style={{color:C.inkDim,fontSize:9,opacity:0.7}}>sin celular</span>
                        )}
                        {(c.plan==="prueba"||c.plan==="trial"||!c.plan)&&(()=>{
                          const dias = Math.floor((new Date()-new Date(c.fecha_registro||c.creado_en))/(1000*60*60*24));
                          const restantes = 14 - dias;
                          return restantes > 0
                            ? <span style={{background:"rgba(254,188,46,0.15)",color:C.warning,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>⏱ {restantes}d de prueba</span>
                            : <span style={{background:C.dangerBg,color:C.danger,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>⚠ Prueba vencida</span>
                        })()}
                      </div>
                    </div>
                  </div>
                  <span style={{color:C.inkMid,fontSize:11}}>{c.tipo_cuenta==="comercio"?"Comercio":"Estudio"}</span>
                  <span style={{color:C.ink,fontWeight:600}}>{c.cantidad_clientes}</span>
                  <span style={{color:C.ink,fontWeight:600}}>{c.cantidad_facturas}</span>
                  <span style={{color:C.success,fontWeight:600}}>{fmt(parseFloat(c.monto_total_facturado||0))}</span>
                  <button onClick={(e)=>{e.stopPropagation();cambiarEstado(c.id,c.activo);}}
                    style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${c.activo?C.success+"44":C.danger+"44"}`,background:c.activo?C.successBg:C.dangerBg,color:c.activo?C.success:C.danger,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    {c.activo?"Activo":"Inactivo"}
                  </button>
                  {/* Selector de plan — para activar manualmente hasta que esté MP */}
                  <select onClick={e=>e.stopPropagation()} onChange={e=>activarPlan(c.id, e.target.value)}
                    defaultValue={c.plan||"trial"}
                    style={{padding:"4px 8px",borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.ink,fontSize:10,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    <option value="trial">Trial</option>
                    <option value="basico">Básico</option>
                    <option value="profesional">Profesional</option>
                    <option value="corporativo">Corporativo</option>
                    <option value="suspendido">Suspendido</option>
                  </select>
                  {/* Extender acceso — para dar más días de prueba sin entrar a la base */}
                  <button onClick={(e)=>{e.stopPropagation();extenderAcceso(c.id, c.nombre, 14);}}
                    disabled={extendiendo===c.id}
                    title="Darle 14 días más de acceso"
                    style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:"transparent",color:C.inkMid,fontSize:10,fontWeight:600,cursor:extendiendo===c.id?"wait":"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}
                    onMouseEnter={e=>{e.currentTarget.style.color=C.green;e.currentTarget.style.borderColor=C.green;}}
                    onMouseLeave={e=>{e.currentTarget.style.color=C.inkMid;e.currentTarget.style.borderColor=C.border;}}>
                    {extendiendo===c.id?"...":"+14 días"}
                  </button>
                  {c.plan_activo_hasta&&(
                    <span style={{fontSize:9,color:C.inkDim,whiteSpace:"nowrap"}}>
                      hasta {new Date(c.plan_activo_hasta).toLocaleDateString("es-AR",{day:"2-digit",month:"2-digit"})}
                    </span>
                  )}
                </div>

                {/* Drill-down — detalle de facturación por cada cliente de este contador */}
                {abierto&&(
                  <div style={{background:C.bg,padding:"8px 16px 14px 44px",borderBottom:`1px solid ${C.border}`}}>
                    {cargandoDetalle===c.id?(
                      <div style={{padding:"12px",color:C.inkDim,fontSize:11}}>Cargando clientes...</div>
                    ):clientesDeEste.length===0?(
                      <div style={{padding:"12px",color:C.inkDim,fontSize:11}}>Este contador todavía no tiene clientes cargados.</div>
                    ):(
                      <div style={{border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden",marginTop:8}}>
                        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1.2fr",padding:"6px 12px",background:C.bgCard,fontSize:9,fontWeight:700,color:C.inkDim,letterSpacing:"0.05em",textTransform:"uppercase"}}>
                          <span>Cliente</span>
                          <span>Facturas</span>
                          <span>Facturado</span>
                          <span>% del total</span>
                          <span>Le queda</span>
                        </div>
                        {clientesDeEste.map((cl,j)=>{
                          const montoCliente = parseFloat(cl.monto_total_facturado||0);
                          const pctDelTotal = parseFloat(c.monto_total_facturado||0) > 0
                            ? ((montoCliente / parseFloat(c.monto_total_facturado)) * 100).toFixed(1)
                            : "0.0";
                          return (
                            <div key={cl.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1.2fr",padding:"8px 12px",borderTop:`1px solid ${C.border}`,fontSize:11,background:j%2===0?C.bgCard:C.bg,alignItems:"center"}}>
                              <div>
                                <div style={{color:C.ink,fontWeight:500}}>{cl.nombre}</div>
                                <div style={{fontSize:9,color:C.inkDim}}>{cl.tipo} · {cl.estado_certificado==="listo"?"✓ Listo":"Sin configurar"}</div>
                              </div>
                              <span style={{color:C.ink}}>{cl.cantidad_facturas}</span>
                              <span style={{color:C.success,fontWeight:600}}>{fmt(montoCliente)}</span>
                              <span style={{color:C.inkMid}}>{pctDelTotal}%</span>
                              {/* Cuánto le queda a ESTE cliente. El contador ya no
                                  vence; el que vence es cada cliente por separado. */}
                              <span>{(()=>{
                                const s = cl.suscripcion;
                                if (!s) return <span style={{color:C.inkDim,fontSize:9}}>—</span>;
                                const chip = (txto,col,bg)=>(
                                  <span title={s.venceEl?`Vence el ${fmtF(s.venceEl)}`:""}
                                    style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:4,background:bg,color:col,whiteSpace:"nowrap"}}>
                                    {txto}
                                  </span>
                                );
                                if (s.estado==="exento")  return chip("Exento", C.inkMid, "rgba(42,100,150,0.18)");
                                if (!s.permitido)         return chip("⛔ Vencido", C.danger, C.dangerBg);
                                if (s.diasGraciaRestantes!=null)
                                  return chip(`⚠ Gracia · ${s.diasGraciaRestantes}d`, C.warning, "rgba(254,188,46,0.15)");
                                if (s.diasRestantes==null) return chip("Sin fecha", C.inkDim, C.bg);
                                const prueba = s.estado==="trial";
                                const col = s.diasRestantes<=3 ? C.danger
                                          : s.diasRestantes<=7 ? C.warning : C.success;
                                const bg  = s.diasRestantes<=3 ? C.dangerBg
                                          : s.diasRestantes<=7 ? "rgba(254,188,46,0.15)" : C.successBg;
                                return chip(`${prueba?"Prueba · ":""}${s.diasRestantes}d`, col, bg);
                              })()}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ─── MÓDULO DE RECIBOS DE SUELDO ─────────────────────────────────────────────
const SeccionRecibos = ({ showToast, onVolver }) => {
  const [cargando, setCargando]     = useState(false);
  const [archivo, setArchivo]       = useState(null);
  const [excelLeido, setExcelLeido] = useState(false);

  // Todo lo que devuelve el lector de la planilla
  const [datos, setDatos]   = useState(null);
  const [token, setToken]   = useState("");

  // Lo único que hay que completar a mano (no está en la planilla)
  const [razonSocial, setRazonSocial]     = useState("");
  const [domEmpleador, setDomEmpleador]   = useState("");
  const [actividad, setActividad]         = useState("");
  const [nombreEmp, setNombreEmp]         = useState("");
  const [dniEmp, setDniEmp]               = useState("");
  const [domEmpleado, setDomEmpleado]     = useState("");
  const [fechaIngreso, setFechaIngreso]   = useState("");
  const [lugarPago, setLugarPago]         = useState("");
  const [modalidad, setModalidad]         = useState("");
  const [pctSindical, setPctSindical]     = useState("3");   // % a cargo del empleador

  // Renombre de conceptos (por si el texto de la planilla quedó de otro mes)
  const [renombres, setRenombres] = useState({});

  const [verPreview, setVerPreview] = useState(false);
  const AZUL_HEX = "#0F2A4D";
  const ROJO_HEX = "#E0524E";

  const money = (n) =>
    "$" + parseFloat(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const leerExcel = async () => {
    if (!archivo) { showToast("⚠ Seleccioná el Excel primero"); return; }
    setCargando(true);
    try {
      const formData = new FormData();
      formData.append("excel", archivo);
      const resp = await authFetch(`${API_URL}/recibos/leer-excel`, {
        method: "POST",
        body: formData,
      });
      const d = await resp.json();
      if (!d.ok) {
        showToast("❌ " + (d.error || "Error leyendo el Excel"));
        setCargando(false);
        return;
      }
      setDatos(d.data);
      setToken(d.data.token);
      setExcelLeido(true);

      // La planilla ya trae la fecha de ingreso cuando hubo alta en el mes
      if (d.data.encabezado?.fecha_ingreso) setFechaIngreso(d.data.encabezado.fecha_ingreso);

      if (!d.data.validaciones?.ok) {
        showToast("⚠ La liquidación no cierra — revisá los errores");
      } else {
        showToast("✅ Planilla leída correctamente");
      }
    } catch(e) { showToast("❌ Error: " + e.message); }
    setCargando(false);
  };

  const generar = async () => {
    if (!excelLeido) { showToast("⚠ Primero subí y leé el Excel"); return; }
    if (!datos?.validaciones?.ok) { showToast("⚠ La liquidación no cierra, no se puede emitir"); return; }
    if (!nombreEmp || !razonSocial) { showToast("⚠ Completá la razón social y el nombre del empleado"); return; }
    setCargando(true);
    try {
      const resp = await authFetch(`${API_URL}/recibos/generar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          renombres,
          pctSindicalEmpleador: (parseFloat(pctSindical) || 0) / 100,
          empleado: {
            razon_social_empleador: razonSocial,
            domicilio_empleador:    domEmpleador,
            actividad_empleador:    actividad,
            nombre_empleado:        nombreEmp,
            dni_empleado:           dniEmp,
            domicilio_empleado:     domEmpleado,
            fecha_ingreso:          fechaIngreso,
            lugar_de_pago:          lugarPago,
            modalidad:              modalidad,
          },
        })
      });
      if (!resp.ok) {
        let msg = "Error generando el recibo";
        try { const j = await resp.json(); msg = j.error || msg; if (j.detalle) console.error(j.detalle); } catch(e2) {}
        showToast("❌ " + msg);
        setCargando(false);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recibo_${nombreEmp.replace(/ /g,"_")}_${(datos.encabezado?.periodo_amm)||""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("✅ Recibo descargado");
    } catch(e) { showToast("❌ Error: " + e.message); }
    setCargando(false);
  };

  const inp = (label, val, set, placeholder="", type="text") => (
    <div style={{display:"flex",flexDirection:"column",gap:3}}>
      <label style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</label>
      <input type={type} value={val} onChange={e=>set(e.target.value)} placeholder={placeholder}
        style={{background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 10px",fontSize:12,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}
        onFocus={e=>e.target.style.borderColor=C.green}
        onBlur={e=>e.target.style.borderColor=C.border}
      />
    </div>
  );

  // Campo de solo lectura, para lo que ya trae la planilla
  const leido = (label, val) => (
    <div style={{display:"flex",flexDirection:"column",gap:3}}>
      <label style={{fontSize:10,fontWeight:600,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</label>
      <div style={{background:"rgba(61,170,138,0.06)",border:`1px solid rgba(61,170,138,0.25)`,borderRadius:7,padding:"8px 10px",fontSize:12,color:C.ink,display:"flex",alignItems:"center",gap:6}}>
        <span style={{color:C.green,fontSize:10}}>✓</span>{val || "—"}
      </div>
    </div>
  );

  const enc  = datos?.encabezado  || {};
  const tot  = datos?.totales     || {};
  const val  = datos?.validaciones || {};

  return (
    <div style={{animation:"fadeUp 0.3s ease"}}>
      <button onClick={onVolver}
        style={{background:"none",border:"none",cursor:"pointer",color:C.inkMid,fontSize:13,fontWeight:500,marginBottom:20,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}
        onMouseEnter={e=>e.currentTarget.style.color=C.ink}
        onMouseLeave={e=>e.currentTarget.style.color=C.inkMid}>
        ← Volver
      </button>

      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Módulo</div>
        <h1 style={{fontSize:22,fontWeight:700,color:C.ink,marginBottom:4,letterSpacing:"-0.02em"}}>📋 Recibos de Sueldo</h1>
        <p style={{fontSize:13,color:C.inkDim,lineHeight:1.6}}>Subí el Excel de liquidaciones — Venci toma los importes ya liquidados, sin recalcular nada. Solo completás los datos que la planilla no tiene.</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>

        {/* COL 1 — Subir Excel */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>
              1. Subir Excel de liquidaciones
            </div>
            <label style={{
              display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
              gap:10,padding:"28px 20px",
              border:`2px dashed ${excelLeido?C.green:archivo?C.green:C.border}`,
              borderRadius:10,cursor:"pointer",
              background:excelLeido?"rgba(61,170,138,0.08)":archivo?"rgba(61,170,138,0.04)":"transparent",
              transition:"all 0.2s"
            }}>
              <input type="file" accept=".xlsx,.xls" style={{display:"none"}}
                onChange={e=>{ if(e.target.files[0]){ setArchivo(e.target.files[0]); setExcelLeido(false); setDatos(null); setToken(""); setRenombres({}); }}}
              />
              <div style={{fontSize:32}}>{excelLeido ? "✅" : "📂"}</div>
              <div style={{fontSize:13,fontWeight:600,color:excelLeido?C.green:archivo?C.green:C.inkMid,textAlign:"center"}}>
                {excelLeido ? `✅ ${archivo.name}` : archivo ? archivo.name : "Tocá para subir el Excel"}
              </div>
              <div style={{fontSize:11,color:C.inkDim}}>Formato: .xlsx o .xls</div>
            </label>

            {archivo && !excelLeido && (
              <button onClick={leerExcel} disabled={cargando}
                style={{width:"100%",marginTop:12,background:C.green,color:"#fff",border:"none",borderRadius:8,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {cargando ? "⏳ Leyendo..." : "📖 Leer Excel"}
              </button>
            )}

            {excelLeido && datos && (
              <div style={{marginTop:12,background:"rgba(61,170,138,0.08)",border:`1px solid rgba(61,170,138,0.2)`,borderRadius:8,padding:"12px"}}>
                <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:8}}>Datos leídos de la planilla:</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {[
                    ["Período", enc.periodo_texto],
                    ["Conceptos", `${datos.haberes?.length||0} haberes, ${datos.descuentos?.length||0} descuentos`],
                    ["Total haberes", money(tot.bruto)],
                    ["Total descuentos", money(tot.descuentos)],
                    ["Sueldo neto", money(tot.neto_a_cobrar)],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                      <span style={{color:C.inkDim}}>{k}</span>
                      <span style={{fontWeight:600,color:C.ink}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Errores de validación — bloquean la emisión */}
          {excelLeido && val.errores?.length > 0 && (
            <div style={{background:"rgba(224,82,78,0.08)",border:`2px solid ${ROJO_HEX}`,borderRadius:10,padding:"16px"}}>
              <div style={{fontSize:12,fontWeight:800,color:ROJO_HEX,marginBottom:8}}>⚠️ La liquidación no cierra</div>
              {val.errores.map((e,i)=>(
                <div key={i} style={{fontSize:11,color:ROJO_HEX,marginBottom:6,lineHeight:1.5}}>• {e}</div>
              ))}
              <div style={{fontSize:10,color:C.inkDim,marginTop:6}}>Corregí la planilla y volvé a subirla. No se puede emitir el recibo así.</div>
            </div>
          )}

          {/* Avisos — no bloquean */}
          {excelLeido && val.avisos?.length > 0 && (
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Para revisar</div>
              {val.avisos.map((a,i)=>(
                <div key={i} style={{fontSize:11,color:C.inkMid,marginBottom:6,lineHeight:1.5,display:"flex",gap:6}}>
                  <span style={{color:"#D4A017"}}>•</span>{a}
                </div>
              ))}
            </div>
          )}

          {/* Celdas de la planilla con el importe pegado a mano */}
          {excelLeido && datos?.formulas_sospechosas?.length > 0 && (
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Celdas sin fórmula en la planilla</div>
              {datos.formulas_sospechosas.map((s,i)=>(
                <div key={i} style={{fontSize:11,color:C.inkMid,marginBottom:4}}>
                  <span style={{fontWeight:700,color:C.ink}}>{s.celda}</span> — {s.concepto}: {money(s.valor)}
                </div>
              ))}
              <div style={{fontSize:10,color:C.inkDim,marginTop:6,lineHeight:1.5}}>
                Tienen el importe escrito a mano en lugar de la fórmula: no se recalculan si cambian las horas del mes. El recibo igual sale bien porque Venci los recalcula desde la base.
              </div>
            </div>
          )}
        </div>

        {/* COL 2 — Datos manuales + generar */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Lo que ya trae la planilla */}
          {excelLeido && (
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>
                Ya viene de la planilla
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {leido("CUIT empleador", enc.cuit_empleador)}
                {leido("CUIL empleado", enc.cuil_empleado)}
                {leido("Legajo", enc.legajo)}
                {leido("Período", enc.periodo_texto)}
                {leido("Categoría", enc.categoria)}
                {leido("Antigüedad", enc.anios_antiguedad ? `${enc.anios_antiguedad} años` : "—")}
                {leido("Horas trabajadas", enc.horas_trabajadas ? `${enc.horas_trabajadas} hs` : "—")}
                {leido("Obra social", datos?.ddjj?.obra_social || datos?.ddjj?.cod_obra_social)}
              </div>
            </div>
          )}

          {/* Empleador */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>
              2. Datos del empleador
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {inp("Razón social *", razonSocial, setRazonSocial, "Clínica San Martín")}
              {inp("Domicilio", domEmpleador, setDomEmpleador, "San Martín 1234, Paraná")}
              {inp("Actividad", actividad, setActividad, "Servicios de salud")}
              {inp("% Contribución sindical del empleador", pctSindical, setPctSindical, "3")}
              <div style={{fontSize:10,color:C.inkDim,lineHeight:1.5,marginTop:-2}}>
                Se calcula sobre remunerativo + no remunerativo. La planilla no lo trae porque no es un descuento del trabajador. Dejalo en 0 si el CCT no lo prevé.
                {excelLeido && tot.bruto ? (
                  <span style={{color:C.green,fontWeight:600}}>
                    {" "}= {money(tot.bruto * (parseFloat(pctSindical)||0) / 100)}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Empleado */}
          <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>
              3. Datos del empleado
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {inp("Apellido y nombre *", nombreEmp, setNombreEmp, "García, María L.")}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {inp("DNI", dniEmp, setDniEmp, "32.580.108")}
                {inp("Fecha ingreso", fechaIngreso, setFechaIngreso, "01/04/2015")}
              </div>
              {inp("Domicilio", domEmpleado, setDomEmpleado, "Belgrano 567, Paraná")}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {inp("Lugar de pago", lugarPago, setLugarPago, "Santa Elena")}
                {inp("Modalidad", modalidad, setModalidad, "Tiempo parcial")}
              </div>
            </div>
          </div>

          {/* Renombrar conceptos */}
          {excelLeido && datos?.conceptos?.length > 0 && (
            <div style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>
                4. Textos de los conceptos
              </div>
              <div style={{fontSize:11,color:C.inkDim,marginBottom:12,lineHeight:1.5}}>
                Editá el texto que sale impreso en el recibo. No cambia ningún importe ni toca la planilla.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {datos.conceptos.map((cpt)=>(
                  <div key={cpt.codigo} style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:10,color:C.inkDim,minWidth:56,fontFamily:"monospace"}}>{cpt.codigo}</span>
                    <input
                      value={renombres[cpt.codigo] ?? cpt.descripcion}
                      onChange={e=>setRenombres(r=>({...r, [cpt.codigo]: e.target.value}))}
                      style={{flex:1,background:C.bgInput,border:`1px solid ${C.border}`,borderRadius:7,padding:"7px 9px",fontSize:11,color:C.ink,fontFamily:"'Inter',sans-serif",outline:"none"}}
                      onFocus={e=>e.target.style.borderColor=C.green}
                      onBlur={e=>e.target.style.borderColor=C.border}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botón preview */}
          <button onClick={()=>{ if(!nombreEmp||!razonSocial){showToast("⚠ Completá la razón social y el nombre del empleado");return;} setVerPreview(true); }}
            disabled={!excelLeido || !val.ok}
            style={{
              background: (!excelLeido || !val.ok) ? "rgba(61,170,138,0.4)" : `linear-gradient(135deg, #2E8B6F, ${C.green})`,
              color:"#fff",border:"none",borderRadius:10,padding:"16px",
              fontSize:15,fontWeight:700,
              cursor:(!excelLeido || !val.ok)?"not-allowed":"pointer",
              fontFamily:"'Inter',sans-serif",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
              boxShadow: (excelLeido && val.ok) ? "0 4px 16px rgba(61,170,138,0.3)" : "none",
              transition:"all 0.2s"
            }}>
            {!excelLeido ? "📋 Primero leé el Excel"
              : !val.ok ? "⚠️ La liquidación no cierra"
              : "👁 Ver preview del recibo"}
          </button>

          {/* MODAL DE PREVIEW */}
          {verPreview && datos && (
            <div style={{
              position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
              zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",
              padding:"20px",backdropFilter:"blur(4px)"
            }}>
              <div style={{
                background:C.bgCard,borderRadius:16,width:"100%",maxWidth:700,
                maxHeight:"90vh",overflowY:"auto",border:`1px solid ${C.border}`,
                boxShadow:"0 32px 80px rgba(0,0,0,0.5)"
              }}>
                <div style={{background:AZUL_HEX,padding:"16px 20px",borderRadius:"16px 16px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{color:"white",fontWeight:700,fontSize:13}}>{razonSocial}</div>
                    <div style={{color:"rgba(255,255,255,0.6)",fontSize:11}}>CCT Sanidad · Período: {enc.periodo_texto}</div>
                  </div>
                  <button onClick={()=>setVerPreview(false)}
                    style={{background:"rgba(255,255,255,0.1)",border:"none",color:"white",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
                    ✕ Cerrar
                  </button>
                </div>

                <div style={{padding:"20px"}}>
                  <div style={{background:C.bg,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Empleado</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                      {[
                        ["Nombre", nombreEmp],
                        ["CUIL", enc.cuil_empleado||"—"],
                        ["Legajo", enc.legajo],
                        ["Categoría", enc.categoria],
                        ["Ingreso", fechaIngreso||"—"],
                        ["Horas", enc.horas_trabajadas ? `${enc.horas_trabajadas} hs` : "—"],
                      ].map(([k,v])=>(
                        <div key={k}>
                          <div style={{fontSize:9,color:C.inkDim}}>{k}</div>
                          <div style={{fontSize:11,fontWeight:600,color:C.ink}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Haberes */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Haberes</div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:AZUL_HEX}}>
                          <th style={{color:"white",padding:"6px 8px",textAlign:"left",fontSize:10,fontWeight:600}}>Concepto</th>
                          <th style={{color:"white",padding:"6px 8px",textAlign:"right",fontSize:10,fontWeight:600}}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {datos.haberes?.map((h,i)=>(
                          <tr key={i} style={{background:i%2===0?C.bg:"white"}}>
                            <td style={{padding:"5px 8px",color:C.ink}}>{renombres[h.codigo] ?? h.descripcion}</td>
                            <td style={{padding:"5px 8px",textAlign:"right",color:C.green,fontWeight:600}}>
                              {money(h.remunerativo || h.no_remunerativo)}
                            </td>
                          </tr>
                        ))}
                        <tr style={{background:"rgba(61,170,138,0.1)"}}>
                          <td style={{padding:"6px 8px",fontWeight:700,color:C.ink}}>Total haberes</td>
                          <td style={{padding:"6px 8px",textAlign:"right",fontWeight:700,color:C.green}}>{money(tot.bruto)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Descuentos */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Descuentos</div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <tbody>
                        {datos.descuentos?.map((d,i)=>(
                          <tr key={i} style={{background:i%2===0?C.bg:"white"}}>
                            <td style={{padding:"5px 8px",color:C.ink}}>{renombres[d.codigo] ?? d.descripcion}</td>
                            <td style={{padding:"5px 8px",textAlign:"right",color:ROJO_HEX,fontWeight:600}}>{money(d.descuento)}</td>
                          </tr>
                        ))}
                        <tr style={{background:"rgba(224,82,78,0.08)"}}>
                          <td style={{padding:"6px 8px",fontWeight:700,color:C.ink}}>Total descuentos</td>
                          <td style={{padding:"6px 8px",textAlign:"right",fontWeight:700,color:ROJO_HEX}}>{money(tot.descuentos)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Neto */}
                  <div style={{background:AZUL_HEX,borderRadius:8,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <span style={{color:"white",fontWeight:700,fontSize:14}}>SUELDO NETO</span>
                    <span style={{color:C.green,fontWeight:800,fontSize:18}}>{money(tot.neto_a_cobrar)}</span>
                  </div>

                  {/* Costo empleador */}
                  <div style={{background:C.bg,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
                      <span style={{color:C.inkDim}}>Costo total del empleador</span>
                      <span style={{fontWeight:700,color:C.ink}}>
                        {money(datos.costo_empleador + tot.bruto * (parseFloat(pctSindical)||0) / 100)}
                      </span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
                      <span style={{color:C.inkDim}}>Costo total (neto + cargas del empleador)</span>
                      <span style={{fontWeight:700,color:C.green}}>
                        {money(tot.neto_a_cobrar + datos.costo_empleador + tot.bruto * (parseFloat(pctSindical)||0) / 100)}
                      </span>
                    </div>
                    {enc.jornada_parcial && (
                      <div style={{fontSize:10,color:C.inkDim,lineHeight:1.5,marginTop:6}}>
                        Jornada parcial: obra social calculada sobre el mínimo de jornada completa ({money(datos.base_obra_social)}).
                      </div>
                    )}
                  </div>

                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setVerPreview(false)}
                      style={{flex:1,background:"transparent",border:`1px solid ${C.border}`,color:C.inkMid,borderRadius:8,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      ← Volver y editar
                    </button>
                    <button onClick={()=>{ setVerPreview(false); generar(); }}
                      disabled={cargando}
                      style={{flex:2,background:`linear-gradient(135deg, #2E8B6F, ${C.green})`,color:"#fff",border:"none",borderRadius:8,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",boxShadow:"0 4px 16px rgba(61,170,138,0.3)"}}>
                      {cargando ? "⏳ Generando..." : "📥 Confirmar y generar PDF"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const PantallaPlanes = ({ contador, onLogout }) => {
  const [planes, setPlanes] = useState({});
  const [cargando, setCargando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/planes`)
      .then(r => r.json())
      .then(d => { if (d.ok) setPlanes(d.data.planes || {}); });
  }, []);

  const suscribirse = async (planId) => {
    setCargando(true);
    setSeleccionado(planId);
    try {
      const token = localStorage.getItem('venci_token');
      const resp = await fetch(`${API_URL}/planes/suscribir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ planId })
      });
      const data = await resp.json();
      if (data.ok && data.data.init_point) {
        window.location.href = data.data.init_point;
      } else {
        alert(data.error || "Error al procesar el pago");
        setCargando(false);
      }
    } catch(e) {
      alert("Error de conexión");
      setCargando(false);
    }
  };

  const diasPrueba = contador?.fecha_registro 
    ? Math.floor((new Date() - new Date(contador.fecha_registro)) / (1000*60*60*24))
    : 0;
  const restantes = Math.max(0, 14 - diasPrueba);
  const vencida = diasPrueba > 14;

  return (
    <div style={{
      minHeight:"100vh",
      background:`linear-gradient(135deg, ${"#0F1923"} 0%, #0A1E36 60%, #071524 100%)`,
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:"24px 16px",
      position:"relative",overflow:"hidden"
    }}>
      {/* Partículas de fondo decorativas */}
      <div style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"hidden"}}>
        {[...Array(6)].map((_,i)=>(
          <div key={i} style={{
            position:"absolute",
            width: [200,150,100,250,180,120][i],
            height: [200,150,100,250,180,120][i],
            borderRadius:"50%",
            background:`radial-gradient(circle, rgba(61,170,138,0.06) 0%, transparent 70%)`,
            top:["-5%","30%","60%","10%","70%","40%"][i],
            left:["10%","70%","20%","80%","50%","90%"][i],
          }}/>
        ))}
      </div>

      <div style={{maxWidth:960,width:"100%",position:"relative",zIndex:1}}>
        {/* Header */}
        <div style={{textAlign:"center",marginBottom:48}}>
          <Logo size={192}/>
          <h1 style={{fontFamily:"'Rubik',sans-serif",fontWeight:800,fontSize:34,color:"#FFFFFF",marginTop:20,marginBottom:10,lineHeight:1.2}}>
            {vencida ? "Tu período de prueba venció" : `Te quedan ${restantes} días de prueba`}
          </h1>
          <p style={{fontSize:15,color:"rgba(255,255,255,0.5)",lineHeight:1.7,maxWidth:480,margin:"0 auto"}}>
            {vencida 
              ? "Elegí un plan para seguir facturando. Sin interrupciones en tus datos."
              : "Elegí tu plan ahora o seguí usando VenciApp gratis hasta que venza tu prueba."
            }
          </p>
          {/* Línea decorativa */}
          <div style={{width:60,height:3,background:`linear-gradient(90deg, transparent, ${"#3DAA8A"}, transparent)`,margin:"20px auto 0",borderRadius:2}}/>
        </div>

        {/* Planes */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20,marginBottom:28}}>
          {[
            {id:"basico", nombre:"Básico", precio:"$15.000", limite:"Hasta $5.000.000/mes",
              features:["PDF de Mercado Pago y NaranjaX","Emisión directa a ARCA","Historial completo con CAE","Re-emisión de rechazadas","Soporte por email y WhatsApp"]},
            {id:"profesional", nombre:"Profesional", precio:"$25.000", limite:"Hasta $15.000.000/mes", popular:true,
              features:["PDF de Mercado Pago y NaranjaX","Emisión directa a ARCA","Historial completo con CAE","Re-emisión de rechazadas","Soporte por email y WhatsApp"]},
            {id:"corporativo", nombre:"Corporativo", precio:"$40.000", limite:"Facturación ilimitada",
              features:["PDF de Mercado Pago y NaranjaX","Emisión directa a ARCA","Historial completo con CAE","Re-emisión de rechazadas","Soporte por email y WhatsApp"]},
          ].map(p => (
            <div key={p.id} style={{
              background: p.popular
                ? "linear-gradient(160deg, rgba(61,170,138,0.15) 0%, rgba(61,170,138,0.05) 100%)"
                : "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              border: p.popular
                ? `2px solid ${"#3DAA8A"}`
                : "1.5px solid rgba(255,255,255,0.1)",
              borderRadius:20,
              padding:"32px 26px",
              position:"relative",
              transition:"transform 0.25s, box-shadow 0.25s",
              boxShadow: p.popular
                ? `0 8px 40px rgba(61,170,138,0.2), 0 0 0 1px rgba(61,170,138,0.1), inset 0 1px 0 rgba(255,255,255,0.08)`
                : `0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)`,
            }}
              onMouseEnter={e=>{
                e.currentTarget.style.transform="translateY(-8px) scale(1.01)";
                e.currentTarget.style.boxShadow=p.popular
                  ? `0 20px 60px rgba(61,170,138,0.35), 0 0 0 1px rgba(61,170,138,0.2), inset 0 1px 0 rgba(255,255,255,0.1)`
                  : `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px rgba(61,170,138,0.2), inset 0 1px 0 rgba(255,255,255,0.08)`;
              }}
              onMouseLeave={e=>{
                e.currentTarget.style.transform="translateY(0) scale(1)";
                e.currentTarget.style.boxShadow=p.popular
                  ? `0 8px 40px rgba(61,170,138,0.2), 0 0 0 1px rgba(61,170,138,0.1), inset 0 1px 0 rgba(255,255,255,0.08)`
                  : `0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)`;
              }}>
              {p.popular&&(
                <div style={{
                  position:"absolute",top:-14,left:"50%",transform:"translateX(-50%)",
                  background:`linear-gradient(90deg, #2E8B6F, ${"#3DAA8A"})`,
                  color:"#fff",fontSize:10,fontWeight:800,padding:"5px 18px",
                  borderRadius:20,letterSpacing:"0.06em",textTransform:"uppercase",
                  boxShadow:"0 4px 12px rgba(61,170,138,0.4)"
                }}>⭐ Más popular</div>
              )}
              
              {/* Nombre del plan */}
              <div style={{fontSize:10,fontWeight:800,color:p.popular?"#3DAA8A":"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:16}}>{p.nombre}</div>
              
              {/* Precio */}
              <div style={{display:"flex",alignItems:"baseline",gap:4,marginBottom:8}}>
                <div style={{
                  fontFamily:"'Rubik',sans-serif",fontWeight:800,fontSize:42,
                  color:p.popular?"#3DAA8A":"#FFFFFF",
                  textShadow:p.popular?`0 0 30px rgba(61,170,138,0.4)`:"none"
                }}>{p.precio}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.35)",fontWeight:400}}>/mes</div>
              </div>
              
              {/* Límite */}
              <div style={{
                fontSize:12,fontWeight:700,color:"#3DAA8A",
                background:"rgba(61,170,138,0.1)",
                border:"1px solid rgba(61,170,138,0.2)",
                padding:"5px 12px",borderRadius:6,
                display:"inline-block",marginBottom:24
              }}>{p.limite}</div>
              
              {/* Separador */}
              <div style={{height:1,background:"rgba(255,255,255,0.06)",marginBottom:20}}/>
              
              {/* Features */}
              <ul style={{listStyle:"none",display:"flex",flexDirection:"column",gap:10,marginBottom:28}}>
                {p.features.map(f=>(
                  <li key={f} style={{fontSize:13,color:"rgba(255,255,255,0.6)",display:"flex",gap:8,alignItems:"flex-start"}}>
                    <span style={{
                      color:"#fff",background:"#3DAA8A",
                      width:16,height:16,borderRadius:"50%",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:9,fontWeight:900,flexShrink:0,marginTop:1
                    }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              
              {/* Botón */}
              <button onClick={()=>suscribirse(p.id)} disabled={cargando&&seleccionado===p.id}
                style={{
                  width:"100%",
                  background:p.popular
                    ? `linear-gradient(135deg, #2E8B6F, ${"#3DAA8A"})`
                    : "transparent",
                  color:p.popular?"#fff":"#3DAA8A",
                  border:`2px solid ${"#3DAA8A"}`,
                  borderRadius:12,padding:"13px",fontSize:14,fontWeight:700,
                  cursor:"pointer",fontFamily:"'Inter',sans-serif",
                  boxShadow:p.popular?"0 4px 20px rgba(61,170,138,0.3)":"none",
                  transition:"all 0.2s"
                }}
                onMouseEnter={e=>{if(!p.popular){e.currentTarget.style.background="#3DAA8A";e.currentTarget.style.color="#fff";}}}
                onMouseLeave={e=>{if(!p.popular){e.currentTarget.style.background="transparent";e.currentTarget.style.color="#3DAA8A";}}}>
                {cargando&&seleccionado===p.id ? "⏳ Procesando..." : `Elegir ${p.nombre} →`}
              </button>
            </div>
          ))}
        </div>

        {/* Links */}
        <div style={{display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap"}}>
          {!vencida&&(
            <button onClick={()=>window.location.reload()}
              style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
              Seguir con la prueba gratuita
            </button>
          )}
          <button onClick={onLogout}
            style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default function VenciAdmin({ contador, onLogout }) {
  const isMobile = useMobile();
  const [clientes, setClientes]         = useState([]);
  const [estadoPlan, setEstadoPlan]     = useState(null);

  // Cargar estado del plan al iniciar
  useEffect(() => {
    authFetch(`${API_URL}/mi-plan`)
      .then(r => r.json())
      .then(d => { if (d.ok) setEstadoPlan(d.data); })
      .catch(() => {});
  }, []);
  const [cargando, setCargando]         = useState(true);
  const [clienteActual, setClienteActual] = useState(null);
  const [verAdmin, setVerAdmin] = useState(false);
  const [verRecibos, setVerRecibos] = useState(false);
  const [obligaciones, setObligaciones] = useState([]);
  const [toast, setToast] = useState(null);
  const [vis, setVis]     = useState(false);

  useEffect(()=>{ setTimeout(()=>setVis(true),80); },[]);

  const esComercio = contador?.tipo_cuenta === "comercio";

  // Cargar clientes desde la DB
  useEffect(()=>{
    authFetch(`${API_URL}/clientes`)
      .then(r=>r.json())
      .then(data=>{
        if(data.ok){
          setClientes(data.data);
          // Cuenta tipo "comercio" — entra directo a su propio perfil,
          // sin pasar por la grilla de clientes (que no necesita, porque
          // solo tiene el suyo propio, creado automáticamente al registrarse).
          if(esComercio && data.data.length>0){
            setClienteActual(data.data[0]);
          }
        }
        setCargando(false);
      })
      .catch(()=>setCargando(false));
  },[]);

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(null),3200); };
  const urgentesCount = clientes.filter(c=>c.arca_estado==="Suspendido").length;
  const pendientesCnt = obligaciones.filter(o=>o.estado==="pendiente").length;

  const NAV = [
    {key:"clientes", icon:"◈", label:"Clientes", badge:urgentesCount||null},
    {key:"recibos",  icon:"📋", label:"Recibos de Sueldo", badge:null},
  ];

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"'Inter','Helvetica Neue',sans-serif",color:C.ink}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
        @keyframes toastIn{from{transform:translateY(20px);opacity:0;}to{transform:translateY(0);opacity:1;}}
        input:focus,select:focus{outline:none;}
        a{text-decoration:none;}
      `}</style>

      {/* SIDEBAR — PC: fijo a la izquierda | Mobile: barra superior */}
      {(() => {
        const isMob = isMobile;
        return isMob ? (
          // MOBILE: barra superior horizontal
          <div style={{position:"fixed",top:0,left:0,right:0,zIndex:20,background:C.bgSide,borderBottom:`1px solid ${C.border}`,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",opacity:vis?1:0,transition:"opacity 0.4s"}}>
            <Logo size={36}/>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {!esComercio&&(
                <button onClick={()=>setClienteActual(null)}
                  style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"6px 12px",color:C.green,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  ◈ Clientes {urgentesCount>0&&<span style={{background:C.danger,color:"#fff",fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:8,marginLeft:4}}>{urgentesCount}</span>}
                </button>
              )}
              {contador?.es_admin&&(
                <button onClick={()=>{setVerAdmin(true);setClienteActual(null);}}
                  style={{background:C.greenDim,border:`1px solid ${C.green}44`,borderRadius:6,padding:"6px 10px",color:C.green,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  👑
                </button>
              )}
              <button onClick={onLogout}
                style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",color:C.inkMid,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                ⏻
              </button>
            </div>
          </div>
        ) : (
          // PC: sidebar fijo original
          <aside style={{position:"fixed",left:0,top:0,width:220,height:"100vh",background:C.bgSide,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",zIndex:20,padding:"20px 12px",opacity:vis?1:0,transition:"opacity 0.4s"}}>
            <div style={{marginBottom:28,textAlign:"center"}}>
              <Logo size={80}/>
            </div>
            <div style={{background:C.greenDim,border:`1px solid ${C.green}33`,borderRadius:6,padding:"8px 10px",marginBottom:12,display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:C.green,flexShrink:0,boxShadow:`0 0 6px ${C.green}`}}/>
              <div style={{fontSize:10,color:C.green,lineHeight:1.5}}>
                <strong>Actualizado 08:00</strong><br/>
                <span style={{color:C.inkDim}}>Datos frescos de ARCA</span>
              </div>
            </div>

            {/* El cartel del estado del plan DEL CONTADOR se quitó: el contador
                no vence nunca. Cada cliente tiene su propio ciclo y su propio
                aviso de vencimiento, dentro de la ficha de ese cliente. */}
            <nav style={{flex:1,display:"flex",flexDirection:"column",gap:2}}>
              {!esComercio&&NAV.map(item=>(
                <button key={item.key} onClick={()=>{setClienteActual(null);setVerAdmin(false);if(item.key==="recibos"){setVerRecibos(true);}else{setVerRecibos(false);}}}
                  style={{display:"flex",alignItems:"center",gap:9,padding:"9px 10px",background:C.greenDim,border:`1px solid ${C.green+"33"}`,borderRadius:7,color:C.green,fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif",transition:"all 0.15s",position:"relative"}}
                  onMouseEnter={e=>{}} onMouseLeave={e=>{}}>
                  <span style={{fontSize:14,opacity:0.8}}>{item.icon}</span>
                  <span style={{flex:1}}>{item.label}</span>
                  {item.badge>0&&<span style={{background:C.danger,color:"white",fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:10}}>{item.badge}</span>}
                </button>
              ))}
            </nav>
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:8}}>
              {[
                {l:"Clientes",           v:clientes.length,   c:C.inkMid},
                {l:"Requieren atención", v:urgentesCount,     c:urgentesCount>0?C.danger:C.success},
              ].map((s,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 4px",fontSize:11}}>
                  <span style={{color:C.inkDim}}>{s.l}</span>
                  <span style={{fontWeight:600,color:s.c}}>{s.v}</span>
                </div>
              ))}
            </div>
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:8,display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div style={{width:32,height:32,borderRadius:8,background:`linear-gradient(135deg,${C.navy}66,${C.green}66)`,border:`1px solid ${C.borderMid}`,display:"flex",alignItems:"center",justifyContent:"center",color:C.green,fontSize:11,fontWeight:700,flexShrink:0}}>{initiales(contador?.nombre||"??")}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{contador?.nombre||"Contador"}</div>
                <div style={{fontSize:9,color:C.inkDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{contador?.colegio||contador?.email||""}</div>
              </div>
            </div>
            {contador?.es_admin&&(
              <button onClick={()=>{setVerAdmin(true);setClienteActual(null);}}
                style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,width:"100%",background:C.greenDim,border:`1px solid ${C.green}44`,borderRadius:7,padding:"8px 10px",color:C.green,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:8,transition:"all 0.15s"}}>
                👑 Panel de Admin
              </button>
            )}
            <button onClick={onLogout}
              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,width:"100%",background:"transparent",border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 10px",color:C.inkMid,fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.danger+"66";e.currentTarget.style.color=C.danger;e.currentTarget.style.background=C.dangerBg;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.inkMid;e.currentTarget.style.background="transparent";}}>
              ⏻ Cerrar sesión
            </button>
          </aside>
        );
      })()}

      {/* CONTENIDO */}
      <main style={{
        marginLeft: isMobile ? 0 : 220,
        marginTop: isMobile ? 60 : 0,
        padding: isMobile ? "16px 14px 80px" : "28px 32px 60px",
        minHeight:"100vh",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        opacity:vis?1:0,
        transition:"opacity 0.4s ease 0.1s"
      }}>

        {verAdmin ? (
          /* PANEL DE SUPER ADMIN */
          <PanelAdmin onVolver={()=>setVerAdmin(false)} />
        ) : verRecibos ? (
          /* MÓDULO DE RECIBOS DE SUELDO */
          <SeccionRecibos showToast={showToast} onVolver={()=>setVerRecibos(false)} />
        ) : clienteActual ? (
          /* PÁGINA DEL CLIENTE */
          <PaginaCliente
            cliente={clienteActual}
            obligaciones={obligaciones}
            setObligaciones={setObligaciones}
            onVolver={esComercio?null:()=>setClienteActual(null)}
            showToast={showToast}
          />
        ) : (
          /* GRILLA DE CLIENTES */
          <>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,color:C.inkDim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>
                {cargando?"Cargando...":"Clientes del estudio"}
              </div>
              <h1 style={{fontSize:22,fontWeight:700,color:C.ink,marginBottom:4,letterSpacing:"-0.02em"}}>
                {cargando?"":`Hola, ${contador?.nombre?.split(" ")[0]||""}`}
              </h1>
              <p style={{fontSize:12,color:C.inkDim,lineHeight:1.6}}>
                Consulta automática · Actualizado a las 08:00 · Junio 2026
              </p>
            </div>
            {cargando?(
              <div style={{textAlign:"center",padding:"60px",color:C.inkDim}}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
                  <div style={{width:32,height:32,border:`2px solid ${C.border}`,borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
                </div>
                <div style={{fontSize:12}}>Cargando clientes...</div>
              </div>
            ):(
              <SeccionClientes
                clientes={clientes}
                setClientes={setClientes}
                obligaciones={obligaciones}
                setObligaciones={setObligaciones}
                showToast={showToast}
                onVerCliente={setClienteActual}
              />
            )}
          </>
        )}
      </main>

      {/* Toast */}
      {toast&&(
        <div style={{position:"fixed",bottom:24,right:24,background:C.bgCard,color:C.ink,padding:"11px 18px",borderRadius:8,fontSize:12,fontWeight:600,zIndex:300,animation:"toastIn 0.3s ease",boxShadow:"0 8px 32px rgba(0,0,0,0.5)",border:`1px solid ${C.green}44`,display:"flex",alignItems:"center",gap:8}}>
          <span style={{color:C.green,fontSize:14}}>✓</span>{toast}
        </div>
      )}
    </div>
  );
}

export { PantallaPlanes };
